// @ts-nocheck
import { useState, useRef, useEffect, useMemo } from "react";
import TermineScreenFull, { DEFAULT_DAY_CONFIG } from "./TermineScreen";
import { ThemeCtx, useTheme, THEMES, Z, ini, Sp, Av, Divider, SLabel, SH, DRow, Btn, Card, CardHead, Met, MiniStat, ProgRow, Modal, Input, Select, Textarea, SearchInput, Toggle, SegControl, Portal, DidaWordmark, DidaLogo, Picto, Insight, toast, ToastHost } from "./ui";
import TrackingScreen, { computeScore, ScoreBadge } from "./TrackingScreen";
import ImportScreen from "./ImportScreen";
import { supabase, isSupabaseConfigured } from "./lib/supabase";
import { listPatients, addPatient as storeAddPatient, updatePatient as storeUpdatePatient, loadPatientsCache } from "./lib/patientStore";
import { computeMetrics, loadApts, loadReviews } from "./lib/metrics";
import { listStats } from "./lib/statsStore";
import { listHkps } from "./lib/hkpStore";
import { listRecalls } from "./lib/recallStore";
import { listApts as listAptsStore } from "./lib/aptStore";
import { reqStore, reviewStore, invoiceStore, teamStore, chatStore } from "./lib/docStore";
import { listFeeds } from "./lib/icsFeeds";
import { INITIAL_REQS } from "./data/requests";
import { TEAM, CHAT_INIT } from "./data/team";
import { loadDemoData } from './data/demoData';
import { migrateLegacyStorage } from './lib/legacyMigration';
import { syncAllFeeds } from './lib/icsFeeds';
import { getPraxis, praxisFooter, getBehandler, getSetting, loadSettings, saveSetting, makeShort, BEHANDLER_PALETTE, DEFAULT_PRAXIS } from './lib/settingsStore';
import { checkSchema } from './lib/healthCheck';
import { demoFallbacks, serverActive } from './lib/mode';
import { submitPulse, loadPulse, eNPS, hasVoted, pulsePeriode, MIN_VOTES } from './lib/teamPulse';
import { sendMails, findPatientEmail } from './lib/mailer';
import { loadMailStats, quote as mailQuote } from './lib/mailStats';
import PatientPicker from './PatientPicker';
import LandingPage from './LandingPage';

// ─── DATA ─────────────────────────────────────────────────────────────────────
const DAYS = ["Mo","Di","Mi","Do","Fr","Sa","So"];
// Praxisname & Behandler kommen aus den Praxis-Einstellungen (settingsStore)
const eurFmt = (n) => "€ " + Math.round(n).toLocaleString("de-DE");
// Praxisname für Mails & Vorschauen — neutraler Fallback, solange die
// Praxis nicht eingerichtet ist (nie Demo-Namen in echten Mails)
const praxisName = () => getPraxis().name || "Unsere Praxis";
const praxisSig  = () => (getPraxis().name ? `${getPraxis().name} & Team` : "Ihr Praxisteam");

// ── KI-Texterstellung: Themen-Bibliothek ────────────────────────────────────
// Für bekannte Dentalthemen komponiert dida echte Inhalte; freie Themen
// bekommen einen sauber formulierten Rahmen statt "wir informieren Sie über: x".
const MAIL_TOPICS = [
  { keys:["parodont","zahnfleisch"], subject:"Gesundes Zahnfleisch — Parodontitis früh erkennen und stoppen",
    intro:"Zahnfleischbluten, empfindliche Zahnhälse oder anhaltender Mundgeruch sind Warnsignale, die Sie ernst nehmen sollten: Parodontitis ist die häufigste Ursache für Zahnverlust im Erwachsenenalter — und sie entwickelt sich oft jahrelang unbemerkt.",
    points:["Regelmäßiges Parodontitis-Screening (PSI) im Rahmen der Kontrolle","Systematische Behandlung — von der schonenden Reinigung der Zahnfleischtaschen bis zur langfristigen Nachsorge (UPT)","Persönliche Tipps für die tägliche Pflege der Zahnzwischenräume"],
    cta:"Je früher eine Parodontitis erkannt wird, desto einfacher lässt sie sich stoppen. Vereinbaren Sie am besten gleich Ihren nächsten Kontrolltermin." },
  { keys:["pzr","prophylaxe","zahnreinigung"], subject:"Professionelle Zahnreinigung — der beste Schutz für Ihre Zähne",
    intro:"Auch bei gründlicher Pflege zu Hause bleiben Beläge in Nischen zurück, die Karies und Zahnfleischentzündungen begünstigen. Die professionelle Zahnreinigung (PZR) entfernt sie gründlich und schonend.",
    points:["Entfernung von Zahnstein und Verfärbungen — auch an schwer erreichbaren Stellen","Politur und Fluoridierung für langanhaltenden Schutz","Individuelle Beratung zu Bürste, Zahnseide und Zwischenraumpflege"],
    cta:"Gönnen Sie Ihren Zähnen zwei PZR-Termine im Jahr — vereinbaren Sie Ihren nächsten Termin einfach telefonisch oder online." },
  { keys:["implantat"], subject:"Zahnimplantate — feste Zähne, neues Lebensgefühl",
    intro:"Eine Zahnlücke ist mehr als ein optisches Thema: Nachbarzähne kippen, der Kieferknochen bildet sich zurück. Implantate ersetzen verlorene Zähne dauerhaft und fühlen sich an wie eigene.",
    points:["Fester Halt beim Sprechen, Lachen und Essen — ohne herausnehmbaren Ersatz","Schutz des Kieferknochens vor Rückbildung","Langlebige Lösung bei guter Pflege — oft ein Leben lang"],
    cta:"Ob ein Implantat für Sie in Frage kommt, klären wir gern in einem persönlichen Beratungsgespräch — sprechen Sie uns an." },
  { keys:["bleaching","aufhellung","verfärb"], subject:"Strahlend weiße Zähne — professionelles Bleaching in Ihrer Praxis",
    intro:"Kaffee, Tee und Rotwein hinterlassen mit der Zeit Spuren. Professionelles Bleaching hellt Ihre Zähne schonend und kontrolliert auf — deutlich sicherer als frei verkäufliche Produkte.",
    points:["Zahnschonende Aufhellung unter zahnärztlicher Kontrolle","Sichtbares Ergebnis oft schon nach einer Sitzung","Vorherige Kontrolle und Reinigung für ein gleichmäßiges Resultat"],
    cta:"Lassen Sie sich unverbindlich beraten, welches Verfahren zu Ihnen passt — wir freuen uns auf Sie." },
  { keys:["angst"], subject:"Zahnarztangst? Wir nehmen uns Zeit für Sie",
    intro:"Herzklopfen vor dem Zahnarzttermin kennen viele — manche vermeiden den Besuch deshalb ganz. Damit sind Sie bei uns nicht allein, und dafür muss sich niemand schämen.",
    points:["Ausführliches Kennenlerngespräch — ohne Behandlung beim ersten Termin, wenn Sie möchten","Jeder Schritt wird vorher erklärt, Sie behalten jederzeit die Kontrolle","Schonende Behandlungsmethoden und auf Wunsch Behandlung in entspannter Atmosphäre"],
    cta:"Trauen Sie sich — der erste Schritt ist ein unverbindliches Gespräch. Wir nehmen uns die Zeit, die Sie brauchen." },
  { keys:["kinder","milchzähne","milchzahn"], subject:"Gesunde Kinderzähne von Anfang an",
    intro:"Milchzähne sind wichtiger, als viele denken: Sie halten den Platz für die bleibenden Zähne und prägen die Zahngesundheit fürs ganze Leben. Früh geübte Routine nimmt Kindern die Scheu vor dem Zahnarztbesuch.",
    points:["Spielerische Eingewöhnung — erste Besuche ganz ohne Behandlung","Individualprophylaxe für Kinder und Jugendliche (von den Kassen übernommen)","Fissurenversiegelung und Fluoridierung zum Schutz der neuen Zähne"],
    cta:"Vereinbaren Sie einen Termin für Ihr Kind — am besten schon ab dem ersten Milchzahn." },
  { keys:["zahnersatz","krone","brücke","prothese"], subject:"Moderner Zahnersatz — Lösungen, die zu Ihnen passen",
    intro:"Ob einzelne Krone, Brücke oder Prothese: Moderner Zahnersatz ist heute kaum noch von echten Zähnen zu unterscheiden — wichtig ist die Lösung, die zu Ihrer Situation und Ihren Wünschen passt.",
    points:["Vollkeramische Kronen und Brücken in natürlicher Zahnfarbe","Transparente Beratung zu Kosten, Kassenzuschuss und Alternativen","Heil- und Kostenplan vorab — Sie entscheiden in Ruhe"],
    cta:"Kommen Sie zur Beratung — wir zeigen Ihnen Ihre Möglichkeiten und erstellen Ihnen ein transparentes Angebot." },
  { keys:["knirsch","cmd","kiefergelenk","schiene"], subject:"Zähneknirschen & Kieferschmerzen — wir helfen",
    intro:"Verspannter Kiefer am Morgen, Kopfschmerzen oder abgeriebene Zähne? Nächtliches Knirschen und Pressen bleibt oft lange unbemerkt — kann aber Zähne und Kiefergelenk dauerhaft schädigen.",
    points:["Genaue Funktionsanalyse von Kiefer und Kaumuskulatur","Individuell angepasste Aufbissschiene schützt Ihre Zähne im Schlaf","Tipps zur Entspannung der Kaumuskulatur im Alltag"],
    cta:"Sprechen Sie uns auf Ihre Beschwerden an — oft bringt schon eine Schiene deutliche Entlastung." },
  { keys:["wurzel","endodont"], subject:"Wurzelbehandlung — Zähne erhalten statt ziehen",
    intro:"Ist der Zahnnerv entzündet, war früher oft das Ziehen des Zahnes die Folge. Die moderne Wurzelkanalbehandlung erhält Ihren eigenen Zahn — und ist dank heutiger Technik deutlich schonender als ihr Ruf.",
    points:["Präzise Aufbereitung der Wurzelkanäle mit moderner Technik","Behandlung unter effektiver lokaler Betäubung — in der Regel schmerzarm","Der eigene Zahn bleibt erhalten, Lücke und Zahnersatz werden vermieden"],
    cta:"Bei Zahnschmerzen zögern Sie nicht — je früher wir behandeln, desto besser stehen die Chancen für Ihren Zahn." },
];
function composeMailText(topic, anrede) {
  const t = String(topic).trim().replace(/[.!\s]+$/,"");
  const hit = MAIL_TOPICS.find(x => x.keys.some(k => t.toLowerCase().includes(k)));
  if (hit) return { subject: hit.subject,
    body: `${anrede},\n\n${hit.intro}\n\nDas bieten wir Ihnen:\n${hit.points.map(pt=>`– ${pt}`).join("\n")}\n\n${hit.cta}\n\nMit freundlichen Grüßen\n${praxisSig()}` };
  const T = t.charAt(0).toUpperCase() + t.slice(1);
  return { subject: `${T} — Wissenswertes aus Ihrer Zahnarztpraxis`,
    body: `${anrede},\n\nheute möchten wir uns mit einem Thema an Sie wenden, das vielen unserer Patientinnen und Patienten am Herzen liegt: ${T}.\n\nUnser Team berät Sie dazu gern persönlich und beantwortet Ihre Fragen — sprechen Sie uns bei Ihrem nächsten Besuch einfach an oder vereinbaren Sie einen kurzen Beratungstermin. Gemeinsam finden wir die Lösung, die zu Ihnen passt.\n\nTermine erhalten Sie telefonisch oder bequem online.\n\nMit freundlichen Grüßen\n${praxisSig()}` };
}



// Behandler-Kürzel → Anzeigename (muss zu TermineScreen BEHANDLER passen)
const BEHANDLER_NAME = {dh:"Dr. Hoffmann",ds:"Dr. Schulz",lbe:"Dr. Bergmann",tf:"Dr. Fischer",mr:"Maja Roth"};

const TYPE_META = {
  Medikament:  {icon:"RX", bg:"#E1F5EE", col:"#085041"},
  Überweisung: {icon:"UE", bg:"#FAEEDA", col:"#633806"},
};

// ─── SHARED MODALS ────────────────────────────────────────────────────────────

function PatAkteModal({patient, onClose}) {
  if(!patient) return null;
  return <>
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay2}}/>
    <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:620,height:"85vh",maxWidth:"96vw",background:"var(--mf-bg)",borderRadius:14,zIndex:Z.modal2,display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 64px rgba(0,0,0,.3)"}}>
      <div style={{padding:"14px 18px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
        <Av text={ini(`${patient.vorname} ${patient.nachname}`)} bg="#E1F5EE" col="#085041" size={40}/>
        <div style={{flex:1}}>
          <div style={{fontSize:14,fontWeight:600}}>{patient.nachname}, {patient.vorname}</div>
          <div style={{fontSize:11,color:"var(--mf-muted)"}}>{patient.dob} · {patient.kv}</div>
        </div>
        <div onClick={onClose} style={{cursor:"pointer",fontSize:20,color:"var(--mf-muted)",width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",background:"var(--mf-bg-alt)"}}>×</div>
      </div>
      <div style={{padding:"14px 18px",flex:1,overflowY:"auto"}}>
        <SLabel>Stammdaten</SLabel>
        <DRow label="Behandler">{patient.treat}</DRow>
        <DRow label="Patient seit">seit {patient.since}</DRow>
        <DRow label="Status"><Sp color={{Aktiv:"g",Erinnern:"a",Inaktiv:"r"}[patient.status]||"b"}>{patient.status}</Sp></DRow>
        <Divider/>
        <SLabel>Medizinisches</SLabel>
        <DRow label="Allergien"><span style={{color:"#A32D2D",fontWeight:500}}>{patient.allergy}</span></DRow>
        <DRow label="Dauermedikation">{patient.meds}</DRow>
        <Divider/>
        <SLabel>Prophylaxe</SLabel>
        <DRow label="Teilnahme"><Sp color={patient.prop?"g":"r"}>{patient.prop?"Aktiv":"Nicht teilnehmend"}</Sp></DRow>
        <DRow label="PZR-Häufigkeit">{patient.pzr}</DRow>
        <DRow label="Nächste PZR">{patient.next}</DRow>
        <Divider/>
        <SLabel>Verlauf &amp; Notizen</SLabel>
        {(patient.history||[]).map((h,i)=>(
          <div key={`${h.date}-${h.title}-${h.note}`} style={{display:"flex",gap:10,paddingBottom:10}}>
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",flexShrink:0}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:"#0F6E56",marginTop:3}}/>
              {i<(patient.history||[]).length-1&&<div style={{flex:1,width:1,background:"var(--mf-border)",marginTop:3}}/>}
            </div>
            <div>
              <div style={{fontSize:10,color:"var(--mf-muted)"}}>{h.date}</div>
              <div style={{fontSize:11,fontWeight:500}}>{h.title}</div>
              <div style={{fontSize:10,color:"var(--mf-muted)",lineHeight:1.5,marginTop:2}}>{h.note}</div>
            </div>
          </div>
        ))}
        <div style={{marginTop:8}}><Btn onClick={onClose}>Schließen</Btn></div>
      </div>
    </div>
  </>;
}

// ─── HELPERS (data) ───────────────────────────────────────────────────────────
const getAllPatientNames = () => loadPatientsCache().map(p=>`${p.nachname}, ${p.vorname}`);

// ─── SCREENS ──────────────────────────────────────────────────────────────────

const DASH_TILE_DEFS = [
  {id:"metrics",  label:"Kennzahlen-Kacheln"},
  {id:"heute",    label:"Heute (Terminliste)"},
  {id:"umsatz",   label:"Umsatz-Chart"},
  {id:"aufgaben", label:"Offene Aufgaben / Befunde"},
  {id:"bewertungen", label:"Neue Bewertungen"},
];

// ── Intro: "dida" in Schreibschrift — Übergang nach erfolgreichem Login ─────
function IntroSplash({ onDone }) {
  const [fading, setFading] = useState(false);
  useEffect(() => {
    const t1 = setTimeout(() => setFading(true), 2100);
    const t2 = setTimeout(() => { if (onDone) onDone(); }, 2750);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onDone]);
  return (
    <div style={{position:"fixed",inset:0,zIndex:99999,background:"var(--mf-bg)",display:"flex",alignItems:"center",justifyContent:"center",opacity:fading?0:1,transition:"opacity .65s ease"}}>
      <svg width="300" height="140" viewBox="0 0 300 140" aria-label="dida">
        <text x="150" y="98" textAnchor="middle"
          fontFamily='"Snell Roundhand","Savoye LET","Segoe Script","Brush Script MT",cursive'
          fontSize="96" fontStyle="italic" fill="none" stroke="var(--mf-primary)" strokeWidth="2.2"
          strokeLinecap="round" strokeLinejoin="round" className="dida-schreib">dida</text>
      </svg>
    </div>
  );
}

// ── Team-Puls: anonyme eNPS-Abstimmung (alle Rollen, Dashboard) ─────────────
function TeamPulseCard() {
  const [score,setScore]=useState(null);
  const [comment,setComment]=useState("");
  const [done,setDone]=useState(hasVoted);
  const [busy,setBusy]=useState(false);
  const send=async()=>{
    if(score==null)return;
    setBusy(true);
    try{ await submitPulse(score, comment.trim()); setDone(true); }
    catch{ toast("Stimme konnte nicht gespeichert werden — Verbindung prüfen.","r"); }
    setBusy(false);
  };
  return (
    <Card>
      <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:6}}>
        <i className="ti ti-heart-handshake" style={{fontSize:15,color:"var(--mf-primary)"}}/>
        <span style={{fontSize:12,fontWeight:500}}>Team-Puls</span>
        <span style={{fontSize:9.5,color:"var(--mf-muted)"}}>anonym · 1× pro Monat</span>
      </div>
      {done ? (
        <div style={{fontSize:11,color:"var(--mf-primary)"}}>✓ Danke — deine Stimme für diesen Monat ist gezählt.</div>
      ) : (<>
        <div style={{fontSize:11,color:"var(--mf-muted)",marginBottom:8}}>Wie wahrscheinlich würdest du unsere Praxis als Arbeitgeber weiterempfehlen? (0 = gar nicht, 10 = sehr wahrscheinlich)</div>
        <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:8}}>
          {Array.from({length:11},(_,i)=>(
            <button key={i} onClick={()=>setScore(i)} style={{width:30,height:30,borderRadius:8,border:`1.5px solid ${score===i?"var(--mf-primary)":"var(--mf-border2)"}`,background:score===i?"var(--mf-primary)":"var(--mf-bg-alt)",color:score===i?"#fff":"var(--mf-text)",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>{i}</button>
          ))}
        </div>
        {score!=null&&<>
          <Input label="Kommentar (optional, anonym)" value={comment} onChange={setComment} style={{marginBottom:8}}/>
          <Btn color="g" onClick={send}>{busy?"Sende…":"Abschicken"}</Btn>
        </>}
      </>)}
    </Card>
  );
}

// ── Team-Puls-Auswertung (Mitarbeiter-Reiter, Inhaber) ──────────────────────
function TeamPulseResults() {
  const [rows,setRows]=useState(null);
  useEffect(()=>{ loadPulse().then(setRows).catch(()=>setRows([])); },[]);
  if(rows===null) return null;
  const monate=[...rows].sort((a,b)=>String(b.periode).localeCompare(String(a.periode))).slice(0,6);
  const cur=monate.find(r=>r.periode===pulsePeriode());
  const curScores=cur?.payload?.scores||[];
  return (
    <Card>
      <CardHead title="Team-Puls (eNPS)" icon="ti-heart-handshake"/>
      {monate.length===0 ? (
        <div style={{fontSize:11,color:"var(--mf-muted)"}}>Noch keine Stimmen — das Team stimmt anonym über die Team-Puls-Karte im Dashboard ab (1× pro Monat).</div>
      ) : (<>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:7,marginBottom:10}}>
          <Met v={curScores.length>=MIN_VOTES?String(eNPS(curScores)):"—"} l="eNPS aktueller Monat" d={curScores.length?`${curScores.length} Stimmen${curScores.length<MIN_VOTES?` — ab ${MIN_VOTES} auswertbar`:""}`:"noch keine Stimmen"} dc={curScores.length>=MIN_VOTES?(eNPS(curScores)>=0?"up":"dn"):undefined}/>
          <Met v={String(monate.reduce((s,r)=>s+(r.payload?.scores||[]).length,0))} l="Stimmen gesamt" d={`${monate.length} Monat${monate.length===1?"":"e"}`}/>
        </div>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr>{["Monat","Stimmen","eNPS"].map(h=><th key={h} style={{textAlign:"left",color:"var(--mf-muted)",fontWeight:400,fontSize:10,padding:"3px 5px",borderBottom:"0.5px solid var(--mf-border)"}}>{h}</th>)}</tr></thead>
          <tbody>
            {monate.map(r=>{
              const sc=r.payload?.scores||[];
              return (<tr key={r.periode}>
                <td style={{padding:"4px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>{r.periode}</td>
                <td style={{padding:"4px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>{sc.length}</td>
                <td style={{padding:"4px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,fontWeight:600,color:sc.length>=MIN_VOTES?(eNPS(sc)>=0?"var(--mf-primary)":"var(--mf-neg)"):"var(--mf-muted)"}}>{sc.length>=MIN_VOTES?eNPS(sc):`ab ${MIN_VOTES} Stimmen`}</td>
              </tr>);
            })}
          </tbody>
        </table>
        {cur&&(cur.payload?.comments||[]).length>0&&curScores.length>=MIN_VOTES&&<>
          <SH t="Kommentare (aktueller Monat, anonym)"/>
          {cur.payload.comments.slice(-5).map((cm,i)=>(
            <div key={i} style={{fontSize:11,color:"var(--mf-text)",padding:"4px 0",borderBottom:"0.5px solid var(--mf-border)"}}>„{cm}"</div>
          ))}
        </>}
      </>)}
      <div style={{fontSize:10,color:"var(--mf-muted)",marginTop:6}}>eNPS = %Promotoren (9–10) − %Detraktoren (0–6) · Auswertung erst ab {MIN_VOTES} Stimmen pro Monat (Anonymität).</div>
    </Card>
  );
}

function DashboardScreen({ role, setScreen, profileName, onOpenPraxis }) {
  const isInhaber = role === "inhaber";
  const isArzt    = ["arzt","arzt2","arzt3"].includes(role);
  const isDH      = role === "dh";
  const isMfa     = role === "mfa";
  const isOwn     = isArzt || isDH || isInhaber;
  const [showAllApts, setShowAllApts] = useState(false);

  const [dashConfig, setDashConfig] = useState(()=>{
    try{const s=localStorage.getItem("dida_dashboard_config");if(s)return JSON.parse(s);}catch{}
    return DASH_TILE_DEFS.map(t=>({...t,enabled:true}));
  });
  useEffect(()=>{try{localStorage.setItem("dida_dashboard_config",JSON.stringify(dashConfig));}catch{}},[dashConfig]);

  const [cfgOpen, setCfgOpen] = useState(false);
  const [noShowOpen, setNoShowOpen] = useState(false);
  const [selAppt, setSelAppt] = useState(null);

  const isTileOn = id => dashConfig.find(t=>t.id===id)?.enabled !== false;

  const moveUp = idx => {if(idx===0)return;const a=[...dashConfig];[a[idx-1],a[idx]]=[a[idx],a[idx-1]];setDashConfig(a);};
  const moveDown = idx => {if(idx===dashConfig.length-1)return;const a=[...dashConfig];[a[idx],a[idx+1]]=[a[idx+1],a[idx]];setDashConfig(a);};
  const toggleTile = id => setDashConfig(prev=>prev.map(t=>t.id===id?{...t,enabled:!t.enabled}:t));

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 11 ? "Guten Morgen" : hour < 18 ? "Guten Tag" : "Guten Abend";
  const GreetPicto = hour < 11 ? Picto.Sunrise : hour < 18 ? Picto.Sun : Picto.Moon;
  const roleName = profileName || ROLES_CFG[role]?.fn || "Kolleg:in";
  const todayStr = now.toLocaleDateString("de-DE",{weekday:"long",day:"numeric",month:"long",year:"numeric"});

  const todayISO = now.toISOString().slice(0,10);
  const TODAY_APTS_ALL = useMemo(() => {
    try {
      const s = localStorage.getItem("dida_apts");
      if (s) {
        const all = JSON.parse(s);
        const mapped = all.filter(a => a.date === todayISO).map(a => ({
          time: a.time, patient: a.patient,
          behandler: BEHANDLER_NAME[a.behandler] || a.behandler,
          status: a.status==="bestätigt"?"g":a.status==="wartend"?"a":"b",
          st: a.status==="bestätigt"?"Check-in":a.status==="wartend"?"Wartend":"Gebucht",
          dob: a.dob||"", kv: a.kv||"", typ: a.anliegen||a.type||"",
        }));
        if (mapped.length > 0) return mapped;
      }
    } catch {}
    if (isSupabaseConfigured) return []; // LIVE: Clean Slate statt Demo-Termine
    return [
      {time:"08:00",patient:"Klaus Weber",  behandler:"Dr. Hoffmann",status:"g",st:"Check-in",dob:"05.11.1954",kv:"TK",    typ:"Kontrolle"},
      {time:"08:15",patient:"Hans Schmidt", behandler:"Dr. Schulz",  status:"g",st:"Check-in",dob:"08.04.1956",kv:"BKK",   typ:"Füllung 37"},
      {time:"09:00",patient:"Maria Maier",  behandler:"Dr. Hoffmann",status:"a",st:"Wartend",  dob:"12.03.1967",kv:"AOK",   typ:"Beratung"},
      {time:"09:15",patient:"Lena Bauer",   behandler:"Maja Roth",   status:"b",st:"Gebucht",  dob:"14.05.1995",kv:"TK",    typ:"PZR"},
      {time:"10:00",patient:"Eva Richter",  behandler:"Dr. Schulz",  status:"b",st:"Gebucht",  dob:"30.09.1965",kv:"AOK",   typ:"Kontrolle"},
      {time:"10:30",patient:"Inge Wolf",    behandler:"Maja Roth",   status:"b",st:"Gebucht",  dob:"30.01.1952",kv:"AOK",   typ:"PZR+Paro"},
      {time:"11:00",patient:"Tim Schreier", behandler:"Dr. Bergmann",status:"b",st:"Gebucht",  dob:"03.07.2012",kv:"TK",    typ:"KFO-Kontrolle"},
      {time:"11:15",patient:"Felix Graf",   behandler:"Dr. Fischer", status:"b",st:"Gebucht",  dob:"22.11.1998",kv:"AOK",   typ:"Füllung"},
    ];
  },[todayISO]);
  const ROLE_BEH = {inhaber:"Dr. Hoffmann", arzt:"Dr. Schulz", arzt2:"Dr. Bergmann", arzt3:"Dr. Fischer", dh:"Maja Roth"};
  const myBeh = ROLE_BEH[role] || null;
  const TODAY_APTS = myBeh && !showAllApts
    ? TODAY_APTS_ALL.filter(a=>a.behandler===myBeh)
    : TODAY_APTS_ALL;
  // Rechenschicht: gleiche Quellen wie das Tracking
  const [statsRows, setStatsRows] = useState([]);
  const [warm, setWarm] = useState(0);
  const [onboardingDone, setOnboardingDone] = useState(()=>{ try{return localStorage.getItem("dida_onboarding_done")==="1";}catch{return false;} });
  useEffect(() => {
    let on = true;
    Promise.allSettled([
      listPatients(), listHkps(), listRecalls(), listAptsStore(), reqStore.list(), reviewStore.list(),
      listStats("umsatz").then(r => { if (on) setStatsRows(r); }),
    ]).then(() => { if (on) setWarm(w => w + 1); });
    return () => { on = false; };
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const m = useMemo(() => computeMetrics(statsRows), [statsRows, warm]);

  const reqsOffen = useMemo(() => {
    try { const s = localStorage.getItem("dida_reqs"); if (s) return JSON.parse(s).filter(r => r.status === "offen").length; } catch {}
    return demoFallbacks() ? 5 : 0;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warm]);
  const wartende = TODAY_APTS_ALL.filter(a => a.st === "Wartend").length;

  // Absagen der letzten 30 Tage (echte Termine statt hardcodierter Liste)
  const NO_SHOWS = useMemo(() => {
    const cutoff = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
    return loadApts()
      .filter(a => a.status === "abgesagt" && a.date >= cutoff)
      .sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time))
      .slice(0, 8)
      .map(a => ({ date: a.date, time: a.time, patient: a.patient, grund: a.cancelReason || "" }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warm]);

  const tileOrder = dashConfig.map(t=>t.id);

  const renderTile = id => {
    if(!isTileOn(id)) return null;
    if(id==="metrics") return (
      <div key="metrics" style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:7}}>
        {isInhaber&&<>
          <Met v={m.umsatz.ok?m.umsatz.fmt:"—"} l={m.umsatz.ok?`Umsatz ${m.umsatz.latest.periode}`:"Umsatz (kein Import)"} d={m.umsatz.ok&&m.umsatz.deltaPct!=null?`${m.umsatz.deltaPct>=0?"↑":"↓"} ${Math.abs(m.umsatz.deltaPct)}% vs. Vorperiode`:m.umsatz.ok?undefined:"PVS-Import öffnen"} dc={m.umsatz.ok&&(m.umsatz.deltaPct==null||m.umsatz.deltaPct>=0)?"up":m.umsatz.ok?"dn":undefined} clickable onClick={()=>setScreen(m.umsatz.ok?"tracking":"import")}/>
          <Met v={m.auslastung.ok?`${m.auslastung.pct}%`:"—"} l="Auslastung diese Woche" d={m.auslastung.ok?`${m.auslastung.termine} Termine`:"Terminplan füllen"} dc={m.auslastung.ok?"up":undefined} clickable onClick={()=>setScreen("termine")}/>
          <Met v={m.ausfall.ok?m.ausfall.fmt:"—"} l="Ausfallquote (30 T.)" d={m.ausfall.ok?`${m.ausfall.abgesagt} Absagen`:"keine Termindaten"} dc={m.ausfall.ok?"dn":undefined} clickable onClick={()=>setNoShowOpen(true)}/>
          <Met v={m.recall.ok?String(m.recall.ueberfaellig):"—"} l="Überfällige Recalls" d={m.recall.ok?`${m.recall.offen} offen gesamt`:"Recall-Liste leer"} dc={m.recall.ok&&m.recall.ueberfaellig>0?"dn":"up"} clickable onClick={()=>setScreen("tracking")}/>
        </>}
        {isArzt&&<>
          <Met v={String(TODAY_APTS_ALL.filter(a=>a.behandler===roleName).length)} l="Meine Termine heute" clickable onClick={()=>setScreen("termine")}/>
          <Met v={m.hkp.ok?String(m.hkp.imUmlauf):"—"} l="HKPs im Umlauf" d={m.hkp.ok?`${eurFmt(m.hkp.offenVol)} offen`:"HKP-Manager"} clickable onClick={()=>setScreen("tracking")}/>
          <Met v={m.ausfall.ok?m.ausfall.fmt:"—"} l="Ausfallquote (30 T.)" dc={m.ausfall.ok?"dn":undefined} clickable onClick={()=>setNoShowOpen(true)}/>
          <Met v={String(reqsOffen)} l="Offene Rezeptanfragen" d={reqsOffen>0?"prüfen":undefined} dc={reqsOffen>0?"dn":undefined} clickable onClick={()=>setScreen("rezepte")}/>
        </>}
        {isDH&&<>
          <Met v={String(TODAY_APTS_ALL.filter(a=>a.behandler==="Maja Roth").length)} l="Meine Termine heute" clickable onClick={()=>setScreen("termine")}/>
          <Met v={m.recall.ok?String(m.recall.offen):"—"} l="Offene Recalls" d={m.recall.ok?`${m.recall.ueberfaellig} überfällig`:undefined} dc={m.recall.ok&&m.recall.ueberfaellig>0?"dn":undefined} clickable onClick={()=>setScreen("tracking")}/>
          <Met v={String(TODAY_APTS_ALL.filter(a=>(a.typ||"").toLowerCase().includes("pzr")||(a.typ||"").toLowerCase().includes("prophylaxe")).length)} l="PZR heute"/>
          <Met v={m.feedback.ok?`${String(m.feedback.avg).replace(".",",")} ★`:"—"} l="Ø Bewertung" clickable onClick={()=>setScreen("tracking")}/>
        </>}
        {isMfa&&<>
          <Met v={String(TODAY_APTS_ALL.length)} l="Termine heute gesamt" clickable onClick={()=>setScreen("termine")}/>
          <Met v={String(wartende)} l="Wartende Patienten"/>
          <Met v={String(reqsOffen)} l="Offene Rezepte" d={reqsOffen>0?"prüfen":undefined} dc={reqsOffen>0?"dn":undefined} clickable onClick={()=>setScreen("rezepte")}/>
          <Met v={m.recall.ok?String(m.recall.ueberfaellig):"—"} l="Überfällige Recalls" dc={m.recall.ok&&m.recall.ueberfaellig>0?"dn":"up"} clickable onClick={()=>setScreen("tracking")}/>
        </>}
      </div>
    );
    if(id==="heute") {
      const showBehCol = isMfa || (isOwn && showAllApts);
      const headCols = showBehCol ? ["Zeit","Patient","Behandler","Status"] : ["Zeit","Patient","Typ","Status"];
      return (
        <Card key="heute">
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
            <CardHead title={isOwn&&!showAllApts?"Mein Plan heute":"Heute"} icon="ti-calendar"/>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              {isOwn&&<Btn onClick={()=>setShowAllApts(p=>!p)} style={{fontSize:10,padding:"2px 7px"}}>{showAllApts?"Eigene":"Alle anzeigen"}</Btn>}
              <Btn onClick={()=>setScreen("termine")} style={{fontSize:10,padding:"2px 7px"}}>Terminplan →</Btn>
            </div>
          </div>
          <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed"}}>
            <thead><tr>{headCols.map(h=><th key={h} style={{textAlign:"left",color:"var(--mf-muted)",fontWeight:400,fontSize:10,padding:"3px 5px",borderBottom:"0.5px solid var(--mf-border)"}}>{h}</th>)}</tr></thead>
            <tbody>
              {TODAY_APTS.length===0&&<tr><td colSpan={4} style={{padding:"12px 5px",fontSize:11,color:"var(--mf-muted)",textAlign:"center"}}>Keine Termine heute</td></tr>}
              {TODAY_APTS.map((apt,i)=>(
                <tr key={apt.id||`${apt.time}-${apt.patient}`} onClick={()=>setSelAppt(apt)} style={{cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.background="var(--mf-bg-alt)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  {showBehCol
                    ? [apt.time,apt.patient,apt.behandler].map((v,j)=><td key={j} style={{padding:"5px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v}</td>)
                    : [apt.time,apt.patient,apt.typ].map((v,j)=><td key={j} style={{padding:"5px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v}</td>)
                  }
                  <td style={{padding:"5px 5px",borderBottom:"0.5px solid var(--mf-border)"}}><Sp color={apt.status}>{apt.st}</Sp></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      );
    }
    if(id==="umsatz" && (isInhaber||isArzt||isDH)) return (
      <div key="umsatz" onClick={()=>setScreen("umsatz")} style={{background:"var(--mf-bg)",border:"0.5px solid var(--mf-border)",borderRadius:10,padding:12,cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.boxShadow="0 2px 12px rgba(0,0,0,.07)"} onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
          <CardHead title={`Umsatz ${new Date().toLocaleString("de-DE",{month:"long"})}`} icon="ti-chart-pie"/>
          <span style={{fontSize:10,color:"var(--mf-primary)"}}>Details →</span>
        </div>
        {m.umsatz.ok ? (()=>{
          const privat = m.umsatz.latest.privatanteil;
          return <>
            <ProgRow label="Gesamt" val={100} valStr={m.umsatz.fmt}/>
            {privat!=null&&<>
              <ProgRow label="Kasse" val={100-privat} valStr={`${100-privat} %`}/>
              <ProgRow label="Privat" val={privat} valStr={`${privat} %`} color="#185FA5"/>
            </>}
            {privat==null&&<div style={{fontSize:10,color:"var(--mf-muted)"}}>Kassen-/Privat-Aufteilung: Privatanteil beim Umsatz-Import mitliefern.</div>}
          </>;
        })() : (
          <div style={{fontSize:11,color:"var(--mf-muted)",padding:"6px 0",lineHeight:1.5}}>
            Noch keine Umsatzstatistik importiert.<br/>
            <span style={{color:"var(--mf-primary)",fontWeight:500}}>PVS-Import → Umsatzstatistik</span>
          </div>
        )}
      </div>
    );
    if(id==="aufgaben") return (
      <Card key="aufgaben">
        <CardHead title="Offene Aufgaben" icon="ti-list-check"/>
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          {(() => {
            const rows = [];
            if (reqsOffen>0) rows.push([`${reqsOffen} Rezeptanfrage${reqsOffen===1?"":"n"} offen`,"a","Prüfen","rezepte"]);
            if (m.recall.ok&&m.recall.ueberfaellig>0) rows.push([`${m.recall.ueberfaellig} Recall${m.recall.ueberfaellig===1?"":"s"} überfällig`,"r","Dringend","tracking"]);
            if (m.recall.ok&&m.recall.offen>0) rows.push([`${m.recall.offen} Recalls offen`,"b","Offen","tracking"]);
            if (m.hkp.ok&&m.hkp.imUmlauf>0) rows.push([`${m.hkp.imUmlauf} HKP${m.hkp.imUmlauf===1?"":"s"} im Umlauf`,"b","Nachfassen","tracking"]);
            if (wartende>0) rows.push([`${wartende} Patient${wartende===1?"":"en"} im Wartezimmer`,"a","Jetzt","termine"]);
            if (!rows.length) return <div style={{fontSize:11,color:"var(--mf-muted)",padding:"6px 2px",display:"flex",alignItems:"center",gap:6}}><Picto.Spark size={15}/>Nichts offen — alles erledigt.</div>;
            return rows.slice(0,5).map(([t,s,st,nav])=>(
              <div key={t} onClick={()=>setScreen(nav)} style={{display:"flex",alignItems:"center",gap:7,padding:"5px 7px",background:"var(--mf-bg-alt)",borderRadius:5,fontSize:11,cursor:"pointer"}}>
                <span style={{flex:1}}>{t}</span><Sp color={s}>{st}</Sp>
              </div>
            ));
          })()}
        </div>
      </Card>
    );
    if(id==="bewertungen") return (
      <div key="bewertungen" onClick={()=>setScreen("tracking")} style={{background:"var(--mf-bg)",border:"0.5px solid var(--mf-border)",borderRadius:10,padding:12,cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.boxShadow="0 2px 12px rgba(0,0,0,.07)"} onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
          <CardHead title="Neue Bewertungen" icon="ti-star"/>
          <span style={{fontSize:10,color:"var(--mf-primary)"}}>Alle →</span>
        </div>
        {(() => {
          const rv = [...loadReviews()].sort((a,b)=>(b.id||0)-(a.id||0))[0]; // neueste zuerst
          if (!rv) return <div style={{fontSize:11,color:"var(--mf-muted)",padding:"6px 2px"}}>Noch keine Bewertungen erfasst.</div>;
          return (
            <div style={{background:"var(--mf-bg-alt)",borderRadius:6,padding:"7px 9px"}}>
              <div style={{color:"#BA7517",fontSize:11,marginBottom:2}}>{"★".repeat(rv.stars)}{"☆".repeat(5-rv.stars)}</div>
              <div style={{fontSize:11,fontWeight:500}}>{rv.name} · {rv.date}</div>
              <div style={{fontSize:10,color:"var(--mf-muted)",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{rv.text}</div>
            </div>
          );
        })()}
      </div>
    );
    return null;
  };

  // split tiles into left (heute) and right column (rest), preserving order
  const leftIds = ["heute"];
  const rightIds = ["umsatz","aufgaben","bewertungen"];
  const leftTiles = tileOrder.filter(id=>leftIds.includes(id)&&isTileOn(id));
  const rightTiles = tileOrder.filter(id=>rightIds.includes(id)&&isTileOn(id));

  return (
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {/* Greeting bar */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between"}}>
        <div>
          <div style={{fontSize:15,fontWeight:600,display:"flex",alignItems:"center",gap:7}}><GreetPicto size={19}/>{greeting}, {roleName}!</div>
          <div style={{fontSize:11,color:"var(--mf-muted)",marginTop:2}}>
            {todayStr} · {TODAY_APTS.length} Termine heute eingetragen
          </div>
        </div>
        <Btn onClick={()=>setCfgOpen(true)} title="Dashboard anpassen" style={{padding:"5px 8px",display:"flex",alignItems:"center",gap:5,fontSize:11}}>
          <i className="ti ti-settings" style={{fontSize:14}}/>
          Anpassen
        </Btn>
      </div>

      {/* Erste Schritte (Live-Modus, bis eingerichtet) */}
      {isSupabaseConfigured && !onboardingDone && (() => {
        const steps = [
          { ok: getBehandler().length>0, label: "Behandler anlegen", hint: "Praxisname & Team einrichten", action: onOpenPraxis },
          { ok: m.score.ok, label: "Patienten einspielen", hint: "CSV aus deinem PVS importieren", nav: "import" },
          { ok: m.umsatz.ok || m.recall.ok, label: "Statistiken & Recalls importieren", hint: "Umsatz- und Recall-Listen (CSV)", nav: "import" },
        ];
        const open = steps.filter(s=>!s.ok).length;
        if (open===0) { try{localStorage.setItem("dida_onboarding_done","1");}catch{} return null; }
        return (
          <Card style={{border:"1px solid var(--mf-primary)"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
              <span style={{fontSize:13,fontWeight:600,display:"flex",alignItems:"center",gap:6}}><i className="ti ti-rocket" style={{color:"var(--mf-primary)"}}/> Erste Schritte — {steps.length-open} von {steps.length} erledigt</span>
              <span onClick={()=>{try{localStorage.setItem("dida_onboarding_done","1");}catch{} setOnboardingDone(true);}} style={{fontSize:11,color:"var(--mf-muted)",cursor:"pointer"}}>Ausblenden ×</span>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:8}}>
              {steps.map((s,i)=>(
                <div key={s.label} onClick={()=>{ if(s.action) s.action(); else if(s.nav) setScreen(s.nav); }} style={{display:"flex",gap:8,alignItems:"flex-start",padding:"9px 11px",background:"var(--mf-bg-alt)",borderRadius:12,cursor:"pointer",opacity:s.ok?.6:1}}>
                  <i className={`ti ${s.ok?"ti-circle-check-filled":"ti-circle-number-"+(i+1)}`} style={{fontSize:16,color:s.ok?"var(--mf-primary)":"var(--mf-muted)",flexShrink:0}}/>
                  <div>
                    <div style={{fontSize:11,fontWeight:600,textDecoration:s.ok?"line-through":"none"}}>{s.label}</div>
                    <div style={{fontSize:10,color:"var(--mf-muted)",marginTop:2}}>{s.hint}</div>
                    {!s.ok&&<div style={{fontSize:10,color:"var(--mf-primary)",fontWeight:600,marginTop:3}}>Jetzt einrichten →</div>}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        );
      })()}

      {/* Metrics row */}
      {isTileOn("metrics") && renderTile("metrics")}

      {/* Two-column layout for remaining tiles in order */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9}}>
        <div style={{display:"flex",flexDirection:"column",gap:9}}>
          {leftTiles.map(id=>renderTile(id))}
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:9}}>
          {rightTiles.map(id=>renderTile(id))}
        </div>
      </div>

      {/* NO-SHOW MODAL */}
      {noShowOpen&&<>
        <div onClick={()=>setNoShowOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay}}/>
        <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:500,maxHeight:"75vh",maxWidth:"96vw",background:"var(--mf-bg)",borderRadius:14,zIndex:Z.modal,display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 64px rgba(0,0,0,.3)"}}>
          <div style={{padding:"14px 18px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
            <span style={{fontSize:14,fontWeight:600,flex:1}}>Terminausfälle</span>
            <div onClick={()=>setNoShowOpen(false)} style={{cursor:"pointer",fontSize:20,color:"var(--mf-muted)",width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",background:"var(--mf-bg-alt)"}}>×</div>
          </div>
          <div style={{padding:"14px 18px",overflowY:"auto"}}>
            <div style={{display:"flex",gap:10,marginBottom:14}}>
              <div style={{flex:1,background:"var(--mf-bg-alt)",borderRadius:8,padding:"10px 12px",textAlign:"center"}}><div style={{fontSize:18,fontWeight:600,color:"var(--mf-primary)"}}>{m.ausfall.ok?`${100-m.ausfall.quote}%`:"—"}</div><div style={{fontSize:10,color:"var(--mf-muted)"}}>Show-Rate (30 Tage)</div></div>
              <div style={{flex:1,background:"var(--mf-bg-alt)",borderRadius:8,padding:"10px 12px",textAlign:"center"}}><div style={{fontSize:18,fontWeight:600,color:"var(--mf-neg)"}}>{NO_SHOWS.length}</div><div style={{fontSize:10,color:"var(--mf-muted)"}}>Absagen (30 Tage)</div></div>
            </div>
            <SLabel>Abgesagte Termine</SLabel>
            {NO_SHOWS.length===0&&<div style={{fontSize:11,color:"var(--mf-muted)",padding:"8px 2px"}}>Keine Absagen in den letzten 30 Tagen.</div>}
            {NO_SHOWS.map(ns=>(
              <div key={`${ns.date}-${ns.time}-${ns.patient}`} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:"var(--mf-bg-alt)",borderRadius:6,marginBottom:5}}>
                <div style={{width:32,height:32,borderRadius:"50%",background:"rgba(255,110,100,0.16)",color:"var(--mf-neg)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:600,flexShrink:0}}>!</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,fontWeight:500}}>{ns.patient}</div>
                  <div style={{fontSize:10,color:"var(--mf-muted)"}}>{ns.date} · {ns.time} Uhr{ns.grund?` · ${ns.grund}`:""}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </>}

      {/* APPOINTMENT DETAIL MODAL */}
      {selAppt&&<>
        <div onClick={()=>setSelAppt(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay}}/>
        <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:400,maxHeight:"70vh",maxWidth:"96vw",background:"var(--mf-bg)",borderRadius:14,zIndex:Z.modal,display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 64px rgba(0,0,0,.3)"}}>
          <div style={{padding:"14px 18px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
            <i className="ti ti-calendar-event" style={{fontSize:20,color:"var(--mf-primary)"}}/>
            <div style={{flex:1}}><div style={{fontSize:14,fontWeight:600}}>{selAppt.time} Uhr</div><div style={{fontSize:11,color:"var(--mf-muted)"}}>{todayStr}</div></div>
            <div onClick={()=>setSelAppt(null)} style={{cursor:"pointer",fontSize:20,color:"var(--mf-muted)",width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",background:"var(--mf-bg-alt)"}}>×</div>
          </div>
          <div style={{padding:"16px 18px",overflowY:"auto"}}>
            <DRow label="Patient">{selAppt.patient}</DRow>
            <DRow label="Geburtsdatum">{selAppt.dob}</DRow>
            <DRow label="KV">{selAppt.kv}</DRow>
            <DRow label="Behandler">{selAppt.behandler}</DRow>
            <DRow label="Terminart">{selAppt.typ}</DRow>
            <DRow label="Status"><Sp color={selAppt.status}>{selAppt.st}</Sp></DRow>
            <div style={{display:"flex",gap:6,marginTop:12}}>
              <Btn color="g" onClick={()=>{setSelAppt(null);setScreen("termine");}}>Im Terminplan öffnen</Btn>
              <Btn onClick={()=>setSelAppt(null)}>Schließen</Btn>
            </div>
          </div>
        </div>
      </>}

      {/* DASHBOARD CONFIG MODAL */}
      <TeamPulseCard/>

      {cfgOpen&&<>
        <div onClick={()=>setCfgOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay}}/>
        <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:420,maxHeight:"75vh",maxWidth:"96vw",background:"var(--mf-bg)",borderRadius:14,zIndex:Z.modal,display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 64px rgba(0,0,0,.3)"}}>
          <div style={{padding:"14px 18px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",flexShrink:0}}>
            <span style={{fontSize:14,fontWeight:600,flex:1}}>Dashboard anpassen</span>
            <div onClick={()=>setCfgOpen(false)} style={{cursor:"pointer",fontSize:20,color:"var(--mf-muted)",width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",background:"var(--mf-bg-alt)"}}>×</div>
          </div>
          <div style={{padding:"14px 18px",overflowY:"auto"}}>
            <div style={{fontSize:10,color:"var(--mf-muted)",marginBottom:10}}>Kacheln ein-/ausblenden und Reihenfolge anpassen:</div>
            {dashConfig.map((tile,idx)=>(
              <div key={tile.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:6,background:"var(--mf-bg-alt)",marginBottom:5}}>
                <div style={{flex:1,fontSize:12,fontWeight:tile.enabled?500:400,color:tile.enabled?"var(--mf-text)":"var(--mf-muted)"}}>{tile.label}</div>
                <button onClick={()=>moveUp(idx)} disabled={idx===0} style={{background:"none",border:"none",cursor:idx===0?"default":"pointer",fontSize:14,color:idx===0?"var(--mf-border2)":"var(--mf-muted)",padding:"0 2px"}}>↑</button>
                <button onClick={()=>moveDown(idx)} disabled={idx===dashConfig.length-1} style={{background:"none",border:"none",cursor:idx===dashConfig.length-1?"default":"pointer",fontSize:14,color:idx===dashConfig.length-1?"var(--mf-border2)":"var(--mf-muted)",padding:"0 2px"}}>↓</button>
                <div onClick={()=>toggleTile(tile.id)} style={{width:36,height:20,borderRadius:10,background:tile.enabled?"#0F6E56":"var(--mf-border2)",cursor:"pointer",position:"relative",transition:"background .2s",flexShrink:0}}>
                  <div style={{position:"absolute",top:2,left:tile.enabled?18:2,width:16,height:16,borderRadius:"50%",background:"rgba(255,255,255,0.95)",transition:"left .2s"}}/>
                </div>
              </div>
            ))}
            <div style={{marginTop:8,display:"flex",gap:6}}>
              <Btn color="g" onClick={()=>setCfgOpen(false)}>Fertig</Btn>
              <Btn onClick={()=>{setDashConfig(DASH_TILE_DEFS.map(t=>({...t,enabled:true})));}}>Zurücksetzen</Btn>
            </div>
          </div>
        </div>
      </>}
    </div>
  );
}

function TermineScreen({ presetPatient, onPresetConsumed }) {
  return <TermineScreenFull presetPatient={presetPatient} onPresetConsumed={onPresetConsumed}/>;
}


function PatientsScreen({ setScreen, setTerminePreset, setRezeptePreset, setMailPreset }) {
  const [isMobileP, setIsMobileP] = useState(()=>typeof window!=="undefined"&&window.innerWidth<640);
  const [mobOpen, setMobOpen] = useState(null); // aufgeklappte Zeile (Patient-Id)
  useEffect(()=>{ const f=()=>setIsMobileP(window.innerWidth<640); window.addEventListener("resize",f); return ()=>window.removeEventListener("resize",f); },[]);
  // Cache sofort anzeigen, dann frisch laden (Supabase im LIVE-, localStorage im DEMO-Modus)
  const [patients, setPatients] = useState(loadPatientsCache);
  useEffect(()=>{
    let active = true;
    listPatients().then(p=>{ if(active) setPatients(p); }).catch(()=>{});
    return ()=>{ active = false; };
  },[]);
  const [search, setSearch] = useState("");
  const [statusF, setStatusF] = useState("");
  const [selPat, setSelPat] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [newPat, setNewPat] = useState({ vorname:"", nachname:"", dob:"", email:"", kv:"AOK", treat:getBehandler()[0]?.name||"", since:"2025", status:"Aktiv", allergy:"—", meds:"—", prop:false, pzr:"—", next:"—", src:"" });

  const isDateSearch = search.trim().length > 0 && /^[\d.]+$/.test(search.trim());
  const filtered = patients.filter(p => {
    const full = `${p.vorname} ${p.nachname} ${p.nachname} ${p.vorname}`.toLowerCase();
    const matchSearch = !search || (isDateSearch ? p.dob.includes(search.trim()) : full.includes(search.toLowerCase()));
    return matchSearch && (!statusF || p.status === statusF);
  });

  const addNote = () => {
    if(!noteText.trim()||!selPat) return;
    const entry = { date:new Date().toLocaleDateString("de-DE",{day:"2-digit",month:"2-digit",year:"2-digit"}), title:"Notiz", note:noteText };
    const newHistory = [entry,...(selPat.history||[])];
    setSelPat(prev=>({...prev,history:newHistory}));
    setNoteText("");
    storeUpdatePatient(selPat.id,{history:newHistory}).then(setPatients).catch(()=>toast("Notiz konnte nicht gespeichert werden — Verbindung prüfen.","r"));
  };
  const addPatient = () => {
    if(!newPat.vorname||!newPat.nachname) return;
    storeAddPatient({...newPat,history:[]}).then(setPatients).catch(()=>toast("Patient konnte nicht gespeichert werden — Verbindung prüfen.","r"));
    setShowNew(false);
    setNewPat({ vorname:"", nachname:"", dob:"", kv:"AOK", treat:getBehandler()[0]?.name||"", since:"2025", status:"Aktiv", allergy:"—", meds:"—", prop:false, pzr:"—", next:"—", src:"" });
  };


  // ── Mobil: reine Patienten-Schnellsuche (lesen, nicht bearbeiten) ──
  // Der Praxismoment: Patient ruft an, MFA hat nur das Handy — Name oder
  // Geburtsdatum tippen, Stammdaten & Allergien sehen. Pflege am Desktop.
  if (isMobileP) {
    const rows = [...filtered].sort((a,b)=>a.nachname.localeCompare(b.nachname,"de")).slice(0,30);
    return (
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        <SearchInput value={search} onChange={setSearch} placeholder="Name oder Geburtsdatum suchen…"/>
        {search.length===0&&<div style={{fontSize:11,color:"var(--mf-muted)",textAlign:"center",padding:"10px 0"}}>{patients.length} Patienten — tippe zum Suchen. Anlegen &amp; Bearbeiten am Desktop.</div>}
        {search.length>0&&rows.length===0&&<div style={{fontSize:11,color:"var(--mf-muted)",textAlign:"center",padding:"10px 0"}}>Kein Treffer.</div>}
        {rows.map(p=>{
          const open = mobOpen===p.id;
          const warn = p.allergy&&p.allergy!=="—";
          return (
            <div key={p.id} onClick={()=>setMobOpen(open?null:p.id)}
              style={{background:"var(--mf-bg)",border:`0.5px solid ${open?"var(--mf-primary)":"var(--mf-border)"}`,borderRadius:12,padding:"10px 12px",cursor:"pointer"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.nachname}, {p.vorname}{warn&&<span style={{color:"var(--mf-neg)",marginLeft:6,fontSize:11}}>⚠</span>}</div>
                  <div style={{fontSize:10.5,color:"var(--mf-muted)"}}>Geb. {p.dob||"—"} · {p.kv||"—"}</div>
                </div>
                <i className={`ti ti-chevron-${open?"up":"down"}`} style={{fontSize:14,color:"var(--mf-muted)",flexShrink:0}}/>
              </div>
              {open&&(
                <div style={{marginTop:8,paddingTop:8,borderTop:"0.5px solid var(--mf-border)",display:"flex",flexDirection:"column",gap:4,fontSize:11.5}}>
                  {warn&&<div style={{color:"var(--mf-neg)",fontWeight:600}}>⚠ Allergie: {p.allergy}</div>}
                  <div><span style={{color:"var(--mf-muted)"}}>Medikation: </span>{p.meds||"—"}</div>
                  <div><span style={{color:"var(--mf-muted)"}}>Letzter Termin: </span>{p.last||"—"}</div>
                  <div><span style={{color:"var(--mf-muted)"}}>Behandler: </span>{p.treat||"—"}</div>
                  <div><span style={{color:"var(--mf-muted)"}}>Status: </span>{p.status||"—"}{p.prop?" · Prophylaxe":""}</div>
                  {p.email&&<div><span style={{color:"var(--mf-muted)"}}>E-Mail: </span><a href={`mailto:${p.email}`} onClick={e=>e.stopPropagation()} style={{color:"var(--mf-primary)"}}>{p.email}</a></div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:10,flex:1,minHeight:0}}>
      <div style={{display:"flex",gap:7,alignItems:"center",flexShrink:0}}>
        <SearchInput value={search} onChange={setSearch} placeholder="Name oder Geburtsdatum suchen…" style={{flex:1}}/>
        <Select value={statusF} onChange={setStatusF} options={[{value:"",label:"Alle"},"Aktiv","Erinnern","Inaktiv"]} style={{marginBottom:0,width:120}}/>
        <span style={{fontSize:10,color:"var(--mf-muted)",whiteSpace:"nowrap"}}>{filtered.length} von {patients.length}</span>
        <Btn color="g" onClick={()=>setShowNew(true)}>+ Neu</Btn>
      </div>
      <div style={{flex:1,overflowY:"auto",minHeight:0}}>
        <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed"}}>
          <thead><tr>{["Name","Geburtsdatum","KV","Behandler","Letzter Termin","Pat. seit","Status","Score"].map(h=><th key={h} style={{textAlign:"left",color:"var(--mf-muted)",fontWeight:400,fontSize:10,padding:"3px 6px",borderBottom:"0.5px solid var(--mf-border)",position:"sticky",top:0,background:"var(--mf-bg)",zIndex:Z.sticky}}>{h}</th>)}</tr></thead>
          <tbody>
            {filtered.length===0&&(
              <tr><td colSpan={8} style={{padding:"32px 8px",textAlign:"center"}}>
                <i className="ti ti-users" style={{fontSize:26,color:"var(--mf-border2)"}}/>
                <div style={{fontSize:12,fontWeight:500,marginTop:6}}>{patients.length===0?"Noch keine Patienten":"Keine Treffer"}</div>
                <div style={{fontSize:11,color:"var(--mf-muted)",marginTop:3}}>{patients.length===0?"Per PVS-Import (CSV) einspielen oder oben rechts anlegen.":"Suche oder Filter anpassen."}</div>
                {patients.length===0&&setScreen&&<div style={{marginTop:10}}><Btn color="g" onClick={()=>setScreen("import")}>Zum PVS-Import</Btn></div>}
              </td></tr>
            )}
            {filtered.map(p=>(
              <tr key={p.id} onClick={()=>setSelPat(p)} style={{cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.background="var(--mf-bg-alt)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <td style={{padding:"5px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,fontWeight:500}}>{p.nachname}, {p.vorname}</td>
                {[p.dob,p.kv,p.treat,p.last,`seit ${p.since}`].map((v,i)=><td key={i} style={{padding:"5px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v}</td>)}
                <td style={{padding:"5px 6px",borderBottom:"0.5px solid var(--mf-border)"}}><Sp color={{Aktiv:"g",Erinnern:"a",Inaktiv:"r"}[p.status]||"b"}>{p.status}</Sp></td>
                <td style={{padding:"5px 6px",borderBottom:"0.5px solid var(--mf-border)"}}><ScoreBadge score={computeScore(p)}/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selPat&&<>
        <div onClick={()=>setSelPat(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay}}/>
        <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:620,height:"85vh",maxWidth:"96vw",background:"var(--mf-bg)",borderRadius:14,zIndex:Z.modal,display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 64px rgba(0,0,0,.3)"}}>
          <div style={{padding:"14px 18px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
            <Av text={ini(`${selPat.vorname} ${selPat.nachname}`)} bg="#E1F5EE" col="#085041" size={40}/>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:600}}>{selPat.nachname}, {selPat.vorname}</div>
              <div style={{fontSize:11,color:"var(--mf-muted)"}}>{selPat.dob} · {selPat.kv}</div>
            </div>
            <div onClick={()=>setSelPat(null)} style={{cursor:"pointer",fontSize:20,color:"var(--mf-muted)",width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",background:"var(--mf-bg-alt)"}}>×</div>
          </div>
          <div style={{padding:"14px 18px",flex:1,overflowY:"auto"}}>
            <SLabel>Stammdaten</SLabel>
            <DRow label="Behandler">{selPat.treat}</DRow>
            <DRow label="Patient seit">seit {selPat.since}</DRow>
            <DRow label="Status"><Sp color={{Aktiv:"g",Erinnern:"a",Inaktiv:"r"}[selPat.status]||"b"}>{selPat.status}</Sp></DRow>
            <DRow label="E-Mail"><input defaultValue={selPat.email||""} placeholder="— eintragen —" key={selPat.id}
              onBlur={e=>{ const v=e.target.value.trim(); if(v!==(selPat.email||"")){ storeUpdatePatient(selPat.id,{email:v}).then(setPatients).catch(()=>toast("E-Mail nicht gespeichert — Verbindung prüfen.","r")); setSelPat(s=>({...s,email:v})); } }}
              onKeyDown={e=>{ if(e.key==="Enter") e.target.blur(); }}
              style={{fontSize:11,fontWeight:500,textAlign:"right",border:"none",outline:"none",background:"transparent",color:"var(--mf-text)",fontFamily:"inherit",width:170,borderBottom:"1px dashed var(--mf-border2)"}}/></DRow>
            <DRow label="Patienten-Score"><ScoreBadge score={computeScore(selPat)} showLabel/></DRow>
            <Divider/>
            <SLabel>Medizinisches</SLabel>
            <DRow label="Allergien"><span style={{color:selPat.allergy&&selPat.allergy!=="—"?"#A32D2D":"var(--mf-text)",fontWeight:selPat.allergy&&selPat.allergy!=="—"?500:400}}>{selPat.allergy}</span></DRow>
            <DRow label="Dauermedikation">{selPat.meds}</DRow>
            <Divider/>
            <SLabel>Prophylaxe</SLabel>
            <DRow label="Teilnahme"><Sp color={selPat.prop?"g":"r"}>{selPat.prop?"Aktiv":"Nicht teilnehmend"}</Sp></DRow>
            <DRow label="PZR-Häufigkeit">{selPat.pzr}</DRow>
            <DRow label="Nächste PZR">{selPat.next}</DRow>
            <Divider/>
            <SLabel>Herkunft</SLabel>
            <DRow label="Wie gefunden">{selPat.src}</DRow>
            <Divider/>
            <SLabel>Verlauf &amp; Notizen</SLabel>
            <div style={{display:"flex",gap:8,marginBottom:8,alignItems:"center"}}>
              <Input value={noteText} onChange={setNoteText} placeholder="Neue Notiz hinzufügen…" style={{flex:1,marginBottom:0}}/>
              <Btn color="g" lg onClick={addNote}>+</Btn>
            </div>
            {(selPat.history||[]).map((h,i)=>(
              <div key={`${h.date}-${h.title}-${h.note}`} style={{display:"flex",gap:10,paddingBottom:10}}>
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",flexShrink:0}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:"#0F6E56",marginTop:3}}/>
                  {i<(selPat.history||[]).length-1&&<div style={{flex:1,width:1,background:"var(--mf-border)",marginTop:3}}/>}
                </div>
                <div><div style={{fontSize:10,color:"var(--mf-muted)"}}>{h.date}</div><div style={{fontSize:11,fontWeight:500}}>{h.title}</div><div style={{fontSize:10,color:"var(--mf-muted)",lineHeight:1.5,marginTop:2}}>{h.note}</div></div>
              </div>
            ))}
            <div style={{display:"flex",gap:5,flexWrap:"wrap",marginTop:8,paddingTop:8,borderTop:"0.5px solid var(--mf-border)"}}>
              <Btn color="g" onClick={()=>{if(setTerminePreset&&setScreen){setTerminePreset({patient:`${selPat.nachname}, ${selPat.vorname}`,dob:selPat.dob,kv:selPat.kv,allergy:selPat.allergy||""});setSelPat(null);setScreen("termine");}}}>Termin buchen</Btn>
              <Btn onClick={()=>{if(setRezeptePreset&&setScreen){setRezeptePreset({vorname:selPat.vorname,nachname:selPat.nachname,dob:selPat.dob,kv:selPat.kv});setSelPat(null);setScreen("rezepte");}}}>Rezept vorerfassen</Btn>
              <Btn onClick={()=>{if(setMailPreset&&setScreen){setMailPreset({patient:`${selPat.nachname}, ${selPat.vorname}`,email:`${selPat.vorname.toLowerCase()}.${selPat.nachname.toLowerCase()}@beispiel.de`});setSelPat(null);setScreen("newsletter");}}}>Erinnerung senden</Btn>
            </div>
          </div>
        </div>
      </>}

      <Modal open={showNew} onClose={()=>setShowNew(false)} title="Neuen Patienten anlegen">
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 10px"}}>
          <Input label="Vorname" value={newPat.vorname} onChange={v=>setNewPat(p=>({...p,vorname:v}))} placeholder="Vorname"/>
          <Input label="E-Mail (für Recall & Newsletter)" value={newPat.email||""} onChange={v=>setNewPat(p=>({...p,email:v}))} placeholder="name@mail.de"/>
          <Input label="Nachname" value={newPat.nachname} onChange={v=>setNewPat(p=>({...p,nachname:v}))} placeholder="Nachname"/>
        </div>
        <Input label="Geburtsdatum" value={newPat.dob} onChange={v=>setNewPat(p=>({...p,dob:v}))} placeholder="TT.MM.JJJJ"/>
        <Select label="Krankenkasse" value={newPat.kv} onChange={v=>setNewPat(p=>({...p,kv:v}))} options={["AOK","TK","BKK","Privat","Sonstiges"]}/>
        <Select label="Behandler" value={newPat.treat} onChange={v=>setNewPat(p=>({...p,treat:v}))} options={getBehandler().length?getBehandler().map(b=>b.name):["—"]}/>
        <Input label="Allergien" value={newPat.allergy} onChange={v=>setNewPat(p=>({...p,allergy:v}))} placeholder="z.B. Penicillin"/>
        <Input label="Dauermedikation" value={newPat.meds} onChange={v=>setNewPat(p=>({...p,meds:v}))} placeholder="z.B. Ramipril 5mg"/>
        <Select label="Wie gefunden?" value={newPat.src} onChange={v=>setNewPat(p=>({...p,src:v}))} options={["Freunde / Familie","Google","Instagram","Werbung","Sonstiges"]}/>
        <div style={{display:"flex",gap:10,marginTop:8}}>
          <Btn color="g" lg style={{flex:1}} onClick={addPatient}>Patient anlegen</Btn>
          <Btn lg onClick={()=>setShowNew(false)}>Abbrechen</Btn>
        </div>
      </Modal>
    </div>
  );
}

function RezepteScreen({ role="mfa", rezeptePreset, onPresetConsumed }) {
  const [reqs, setReqs] = useState(()=>{ const cached=reqStore.loadCache(); return cached!==null?cached:(demoFallbacks()?INITIAL_REQS:[]); });
  useEffect(()=>{ let on=true; reqStore.list().then(r=>{ if(on&&(r.length||!demoFallbacks())) setReqs(r); }).catch(()=>{}); return ()=>{ on=false; }; },[]);
  useEffect(()=>{ if(serverActive()) return; try{localStorage.setItem("dida_reqs",JSON.stringify(reqs));}catch{} },[reqs]); // LIVE spiegelt der Store
  const [tab, setTab] = useState("offen");
  const [sel, setSel] = useState(null);
  const [search, setSearch] = useState("");
  const [typeF, setTypeF] = useState("");
  const [aktePatient, setAktePatient] = useState(null);
  const [newReqOpen, setNewReqOpen] = useState(false);
  const [newReq, setNewReq] = useState({vorname:"",nachname:"",dob:"",kv:"AOK",typ:"Medikament",med:"",notiz:""});

  const counts = { offen:reqs.filter(r=>r.status==="offen").length, genehmigt:reqs.filter(r=>r.status==="genehmigt").length, abgelehnt:reqs.filter(r=>r.status==="abgelehnt").length };
  const filtered = reqs.filter(r=>r.status===tab&&(!search||(r.vorname+" "+r.nachname+" "+r.dob).toLowerCase().includes(search.toLowerCase()))&&(!typeF||r.typ===typeF));

  useEffect(()=>{
    if(!rezeptePreset) return;
    setNewReq(p=>({...p,vorname:rezeptePreset.vorname||"",nachname:rezeptePreset.nachname||"",dob:rezeptePreset.dob||"",kv:rezeptePreset.kv||"AOK"}));
    setNewReqOpen(true);
    if(onPresetConsumed) onPresetConsumed();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[rezeptePreset]);

  const canApprove = ["inhaber","arzt","arzt2","arzt3"].includes(role);
  const act = (id,status) => {
    setReqs(p=>p.map(r=>r.id===id?{...r,status}:r)); setSel(null); // optimistisch
    if(serverActive()){ const hit=reqs.find(r=>r.id===id); if(hit) reqStore.upsert({...hit,status}).catch(()=>toast("Statuswechsel nicht gespeichert — Verbindung prüfen.","r")); }
  };

  const openAkte = (r) => {
    const allPats = loadPatientsCache();
    const pat = allPats.find(p=>p.vorname===r.vorname&&p.nachname===r.nachname);
    setAktePatient(pat||{vorname:r.vorname,nachname:r.nachname,dob:r.dob,kv:r.kv,treat:"—",since:"—",status:"Aktiv",allergy:"—",meds:"—",prop:false,pzr:"—",next:"—",history:[]});
  };

  const saveNewReq = () => {
    if(!newReq.vorname||!newReq.nachname||!newReq.med) return;
    const r = {id:Date.now(),status:"offen",vorname:newReq.vorname,nachname:newReq.nachname,dob:newReq.dob,kv:newReq.kv,last:"—",typ:newReq.typ,med:newReq.med,src:"Patienten-Akte",time:"jetzt",ago:"gerade",ki:[{ok:true,txt:"Patient bekannt"}],notiz:newReq.notiz};
    setReqs(p=>[r,...p]); // optimistisch
    if(serverActive()) reqStore.upsert(r).catch(()=>toast("Anfrage nicht gespeichert — Verbindung prüfen.","r"));
    setNewReqOpen(false);
    setNewReq({vorname:"",nachname:"",dob:"",kv:"AOK",typ:"Medikament",med:"",notiz:""});
    setTab("offen");
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:10,flex:1,minHeight:0}}>
      <div style={{display:"flex",padding:0,borderBottom:"0.5px solid var(--mf-border)",alignItems:"center",gap:0,flexShrink:0}}>
        {[["offen","Offen / Zu prüfen","a"],["genehmigt","Genehmigt","g"],["abgelehnt","Abgelehnt","r"]].map(([k,l,c])=>(
          <div key={k} onClick={()=>setTab(k)} style={{fontSize:12,padding:"8px 14px",cursor:"pointer",color:tab===k?"#0F6E56":"var(--mf-muted)",fontWeight:tab===k?500:400,borderBottom:tab===k?"2px solid #0F6E56":"2px solid transparent",marginBottom:-0.5,display:"flex",alignItems:"center",gap:6}}>
            {l}<span style={{fontSize:9,padding:"1px 5px",borderRadius:10,fontWeight:500,background:{g:"#E1F5EE",a:"#FAEEDA",r:"#FCEBEB"}[c],color:{g:"#085041",a:"#633806",r:"#791F1F"}[c]}}>{counts[k]}</span>
          </div>
        ))}
        <div style={{marginLeft:"auto",padding:"8px 0"}}><Sp color="g">KI-Vorprüfung aktiv</Sp></div>
      </div>
      <div style={{display:"flex",gap:7,alignItems:"center",flexShrink:0}}>
        <SearchInput value={search} onChange={setSearch} placeholder="Name, Vorname, Geburtsdatum…" style={{flex:1,maxWidth:280}}/>
        <Select value={typeF} onChange={setTypeF} options={[{value:"",label:"Alle Typen"},...Object.keys(TYPE_META)]} style={{marginBottom:0,width:140}}/>
        <span style={{fontSize:10,color:"var(--mf-muted)",marginLeft:"auto"}}>{filtered.length} Anfrage{filtered.length!==1?"n":""}</span>
      </div>

      <div style={{flex:1,overflowY:"auto",minHeight:0,display:"flex",flexDirection:"column",gap:7}}>
        {filtered.length===0&&<div style={{display:"flex",flexDirection:"column",alignItems:"center",padding:40,gap:8,color:"var(--mf-muted)"}}><i className="ti ti-inbox" style={{fontSize:28,color:"var(--mf-border2)"}}/><span style={{fontSize:12}}>Keine Anfragen</span></div>}
        {filtered.map(r=>{
          const tm = TYPE_META[r.typ]||{icon:"XX",bg:"#E0DED8",col:"#666"};
          return (
            <div key={r.id} onClick={()=>setSel(r)} style={{background:"var(--mf-bg-alt)",border:`0.5px solid #e7e5e4`,borderLeft:`3px solid ${tab==="offen"?"#BA7517":tab==="genehmigt"?"#0F6E56":"#993C1D"}`,borderRadius:"8px",padding:"10px 12px",cursor:"pointer",flexShrink:0}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:32,height:32,borderRadius:"50%",background:tm.bg,color:tm.col,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:500,flexShrink:0}}>{ini(`${r.vorname} ${r.nachname}`)}</div>
                <div><div style={{fontSize:12,fontWeight:500}}>{r.nachname}, {r.vorname}</div><div style={{fontSize:10,color:"var(--mf-muted)"}}>Geb. {r.dob} · {r.kv}</div></div>
                <div style={{marginLeft:"auto",textAlign:"right"}}><Sp color={tab==="offen"?"a":tab==="genehmigt"?"g":"r"}>{tab==="offen"?"Zu prüfen":tab==="genehmigt"?"Genehmigt":"Abgelehnt"}</Sp><div style={{fontSize:10,color:"var(--mf-muted)",marginTop:3}}>{r.ago}</div></div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginTop:8,paddingTop:8,borderTop:"0.5px solid var(--mf-border)"}}>
                <div style={{width:22,height:22,borderRadius:5,background:tm.bg,color:tm.col,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12}}>{tm.icon}</div>
                <div><div style={{fontSize:11,fontWeight:500}}>{r.typ}</div><div style={{fontSize:10,color:"var(--mf-muted)"}}>{r.med}</div></div>
                <div style={{marginLeft:"auto"}}><Sp color={r.src==="KI-Telefon"?"pu":"b"}>{r.src}</Sp></div>
              </div>
            </div>
          );
        })}
      </div>

      {sel&&<>
        <div onClick={()=>setSel(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay}}/>
        <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:620,height:"85vh",maxWidth:"96vw",background:"var(--mf-bg)",borderRadius:14,zIndex:Z.modal,display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 64px rgba(0,0,0,.3)"}}>
          <div style={{padding:"14px 18px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
            <div style={{width:38,height:38,borderRadius:"50%",background:(TYPE_META[sel.typ]||{}).bg||"#eee",color:(TYPE_META[sel.typ]||{}).col||"#333",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:500}}>{ini(`${sel.vorname} ${sel.nachname}`)}</div>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:600}}>{sel.nachname}, {sel.vorname}</div>
              <div style={{fontSize:11,color:"var(--mf-muted)"}}>Geb. {sel.dob}</div>
            </div>
            <div onClick={()=>setSel(null)} style={{cursor:"pointer",fontSize:20,color:"var(--mf-muted)",width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",background:"var(--mf-bg-alt)"}}>×</div>
          </div>
          <div style={{padding:"14px 18px",flex:1,overflowY:"auto"}}>
            <SLabel>Anfrage</SLabel>
            <DRow label="Typ">{sel.typ}</DRow>
            <DRow label="Gewünschtes Mittel">{sel.med}</DRow>
            <DRow label="Eingegangen via"><Sp color={sel.src==="KI-Telefon"?"pu":"b"}>{sel.src}</Sp></DRow>
            <DRow label="Zeitpunkt">{sel.time}</DRow>
            <Divider/>
            <SLabel>Patient</SLabel>
            <DRow label="KV">{sel.kv}</DRow>
            <DRow label="Letzter Termin">{sel.last}</DRow>
            <Divider/>
            <SLabel>KI-Vorprüfung</SLabel>
            {sel.ki.map(k=>(
              <div key={k.txt} style={{display:"flex",alignItems:"flex-start",gap:7,padding:"6px 9px",borderRadius:6,marginBottom:4,background:k.ok?"#E1F5EE":"#FAEEDA",color:k.ok?"#085041":"#633806",fontSize:11}}>
                <i className={`ti ti-${k.ok?"circle-check":"alert-triangle"}`} style={{fontSize:14,flexShrink:0,marginTop:1}}/>
                <span>{k.txt}</span>
              </div>
            ))}
            {sel.reason&&<div style={{marginTop:8,padding:"8px 10px",background:"#FCEBEB",borderRadius:6,fontSize:11,color:"#791F1F"}}>{sel.reason}</div>}
            <div style={{display:"flex",gap:5,flexWrap:"wrap",marginTop:10,paddingTop:10,borderTop:"0.5px solid var(--mf-border)"}}>
              {sel.status==="offen"&&canApprove&&<><Btn color="g" onClick={()=>act(sel.id,"genehmigt")}>Genehmigen</Btn><Btn color="r" onClick={()=>act(sel.id,"abgelehnt")}>Ablehnen</Btn></>}
              {sel.status!=="offen"&&<Btn onClick={()=>act(sel.id,"offen")}>Zurück zu Offen</Btn>}
              <Btn onClick={()=>openAkte(sel)}>Akte öffnen</Btn>
            </div>
          </div>
        </div>
      </>}

      {/* NEUE ANFRAGE MODAL */}
      {newReqOpen&&<>
        <div onClick={()=>setNewReqOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay}}/>
        <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:480,maxHeight:"85vh",maxWidth:"96vw",background:"var(--mf-bg)",borderRadius:14,zIndex:Z.modal,display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 64px rgba(0,0,0,.3)"}}>
          <div style={{padding:"14px 18px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",flexShrink:0}}>
            <span style={{fontSize:14,fontWeight:600,flex:1}}>Neue Rezeptanfrage</span>
            <div onClick={()=>setNewReqOpen(false)} style={{cursor:"pointer",fontSize:20,color:"var(--mf-muted)",width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",background:"var(--mf-bg-alt)"}}>×</div>
          </div>
          <div style={{padding:"16px 18px",overflowY:"auto",display:"flex",flexDirection:"column",gap:10}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 10px"}}>
              <PatientPicker label="Patient suchen (füllt Felder aus)" value={newReq._suche||""}
                onChange={v=>setNewReq(p=>({...p,_suche:v}))}
                onPick={pt=>setNewReq(p=>({...p,_suche:`${pt.nachname}, ${pt.vorname}`,vorname:pt.vorname,nachname:pt.nachname,dob:pt.dob||"",kv:pt.kv||p.kv}))}/>
              <Input label="Vorname" value={newReq.vorname} onChange={v=>setNewReq(p=>({...p,vorname:v}))}/>
              <Input label="Nachname" value={newReq.nachname} onChange={v=>setNewReq(p=>({...p,nachname:v}))}/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 10px"}}>
              <Input label="Geburtsdatum" value={newReq.dob} onChange={v=>setNewReq(p=>({...p,dob:v}))} placeholder="TT.MM.JJJJ"/>
              <Select label="KV" value={newReq.kv} onChange={v=>setNewReq(p=>({...p,kv:v}))} options={["AOK","TK","BKK","Privat"]}/>
            </div>
            <Select label="Typ" value={newReq.typ} onChange={v=>setNewReq(p=>({...p,typ:v}))} options={["Medikament","AU","Physio","Überweisung"]}/>
            <Input label="Medikament / Leistung *" value={newReq.med} onChange={v=>setNewReq(p=>({...p,med:v}))} placeholder="z.B. Ibuprofen 400mg"/>
            <Input label="Notiz (optional)" value={newReq.notiz} onChange={v=>setNewReq(p=>({...p,notiz:v}))}/>
            <div style={{display:"flex",gap:10}}><Btn color="g" lg style={{flex:1}} onClick={saveNewReq}>Anfrage speichern</Btn><Btn lg onClick={()=>setNewReqOpen(false)}>Abbrechen</Btn></div>
          </div>
        </div>
      </>}

      {/* PATIENTEN AKTE MODAL */}
      <PatAkteModal patient={aktePatient} onClose={()=>setAktePatient(null)}/>
    </div>
  );
}

function MitarbeiterScreen({ role }) {
  const [isMobileM, setIsMobileM] = useState(()=>typeof window!=="undefined"&&window.innerWidth<640);
  useEffect(()=>{ const f=()=>setIsMobileM(window.innerWidth<640); window.addEventListener("resize",f); return ()=>window.removeEventListener("resize",f); },[]);
  const INIT_URLAUB = {1:[16,17,18,19,20],2:[9,10,11,12,13],3:[16,17,18,19,20,23,24,25,26,27],4:[],5:[23,24,25,26,27]};
  const [team, setTeam] = useState(()=>{
    const cached=teamStore.loadCache(); return cached!==null?cached:(demoFallbacks()?TEAM.map(m=>({...m,urlaubTageJuni:INIT_URLAUB[m.id]||[]})):[]);
  });
  const [messages, setMessages] = useState(()=>{
    const cached=chatStore.loadCache(); return cached!==null?cached:(demoFallbacks()?CHAT_INIT:{});
  });
  useEffect(()=>{ let on=true;
    teamStore.list().then(r=>{ if(on&&(r.length||!demoFallbacks())) setTeam(r); }).catch(()=>{});
    chatStore.load().then(obj=>{ if(on&&obj) setMessages(obj); }).catch(()=>{});
    return ()=>{ on=false; };
  },[]);
  // Persistenz: DEMO in localStorage, LIVE über die Stores (jede Änderung)
  useEffect(()=>{ if(!serverActive()){ try{localStorage.setItem("dida_team",JSON.stringify(team));}catch{} return; } if(team.length) teamStore.saveAll(team).catch(()=>{}); },[team]);
  useEffect(()=>{ if(!serverActive()){ try{localStorage.setItem("dida_chat",JSON.stringify(messages));}catch{} return; } if(Object.keys(messages||{}).length) chatStore.save(messages).catch(()=>{}); },[messages]);

  const [selMember, setSelMember] = useState(null);
  const [vacFrom, setVacFrom] = useState("");
  const [vacTo, setVacTo] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [chatTarget, setChatTarget] = useState("group");
  const [msgInput, setMsgInput] = useState("");
  const [wochenplanOpen, setWochenplanOpen] = useState(false);
  const [wpView, setWpView] = useState("woche");
  const [wpKW, setWpKW] = useState(23);
  const [wpMonth, setWpMonth] = useState({year:2026,month:5});
  const [editingCell, setEditingCell] = useState(null);
  const [auFiles, setAuFiles] = useState({});
  const [einladenOpen, setEinladenOpen] = useState(false);
  const [einladenForm, setEinladenForm] = useState({name:"",email:"",rolle:"mfa"});
  const [einladenDone, setEinladenDone] = useState(false);
  const chatEndRef = useRef(null);
  const isInhaber = role==="inhaber";

  useEffect(()=>{chatEndRef.current?.scrollIntoView({behavior:"smooth"})},[messages,chatTarget]);

  const sendMsg = () => {
    if(!msgInput.trim()) return;
    setMessages(prev=>({...prev,[chatTarget]:[...(prev[chatTarget]||[]),{from:ROLES_CFG[role]?.fav||"??",text:msgInput,time:new Date().toLocaleTimeString("de-DE",{hour:"2-digit",minute:"2-digit"})}]}));
    setMsgInput("");
  };

  const totalH = (m) => DAYS.reduce((s,d)=>s+(m.stunden[d]||0),0);

  // ISO 8601 KW helpers
  const kwStart = kw => {
    const year = wpMonth.year;
    const jan4 = new Date(year, 0, 4);
    const dow = (jan4.getDay() + 6) % 7; // 0=Mon
    const w1mon = new Date(year, 0, 4 - dow);
    w1mon.setDate(w1mon.getDate() + (kw - 1) * 7);
    return w1mon;
  };
  const kwDays = kw => {
    const monday = kwStart(kw);
    return Array.from({length:7}, (_, i) => {
      const d = new Date(monday);
      d.setDate(d.getDate() + i);
      return d.getMonth() === wpMonth.month ? d.getDate() : null;
    }).filter(Boolean);
  };
  const kwIsoDays = kw => {
    const monday = kwStart(kw);
    return Array.from({length:7}, (_, i) => {
      const d = new Date(monday); d.setDate(d.getDate()+i);
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    });
  };
  const memberVacInKW = (m, kw) => {
    const isos = kwIsoDays(kw);
    if ((m.urlaubDaten||[]).some(d=>isos.includes(d))) return true;
    // Legacy-Demo (Juni 2026)
    return wpMonth.year===2026&&wpMonth.month===5&&(m.urlaubTageJuni||[]).some(d=>kwDays(kw).includes(d));
  };
  const vacInKW = kw => team.filter(m=>memberVacInKW(m,kw));
  const hasOverlap = kw => vacInKW(kw).length>=2;

  const MONTH_NAMES = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
  const calCells = (year=2026,month=5) => {
    const daysInMonth = new Date(year,month+1,0).getDate();
    const firstDow = new Date(year,month,1).getDay();
    const empties = firstDow===0?6:firstDow-1;
    return [...Array(empties).fill(null),...Array.from({length:daysInMonth},(_,i)=>i+1)];
  };
  const prevMonth = () => setWpMonth(({year,month})=>month===0?{year:year-1,month:11}:{year,month:month-1});
  const nextMonth = () => setWpMonth(({year,month})=>month===11?{year:year+1,month:0}:{year,month:month+1});

  const escHtml = s => String(s).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  const printWochenplan = () => {
    const pw = window.open("","_blank");
    const rows = team.map(m=>`<tr><td style="padding:6px 10px;border:1px solid #ddd;font-weight:500">${escHtml(m.name)}</td>${DAYS.map(d=>{
      const h=m.stunden[d]||0;
      const isKrank=m.sick&&(d==="Do"||d==="Fr");
      const dayIdx=DAYS.indexOf(d);
      const iso=kwIsoDays(wpKW)[dayIdx];
      const isUrlaub=(m.urlaubDaten||[]).includes(iso)||((m.urlaubTageJuni||[]).some(day=>kwDays(wpKW).includes(day)&&dayIdx===kwDays(wpKW).indexOf(day))&&wpMonth.year===2026&&wpMonth.month===5);
      const bg=isKrank?"#FCEBEB":isUrlaub?"#E1F5EE":"#fff";
      const txt=isKrank?"Krank":isUrlaub?"Urlaub":h>0?escHtml(h+"h"):"—";
      return `<td style="padding:6px 10px;border:1px solid #ddd;text-align:center;background:${bg}">${txt}</td>`;
    }).join("")}<td style="padding:6px 10px;border:1px solid #ddd;text-align:center;font-weight:500">${escHtml(totalH(m)+"h")}</td></tr>`).join("");
    pw.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Wochenplan KW${wpKW}</title><style>body{font-family:system-ui,sans-serif;padding:20px}table{border-collapse:collapse;width:100%}th{padding:8px 10px;border:1px solid #ddd;background:#f5f5f4;text-align:center}@media print{button{display:none}}</style></head><body><h2 style="margin-bottom:16px">Wochenplan — KW ${wpKW} (${MONTH_NAMES[wpMonth.month]} ${wpMonth.year})</h2><table><thead><tr><th style="text-align:left">Mitarbeiter</th>${DAYS.map(d=>`<th>${d}</th>`).join("")}<th>∑</th></tr></thead><tbody>${rows}</tbody></table><p style="margin-top:16px;font-size:12px;color:#666">Erstellt: ${new Date().toLocaleDateString("de-DE")} | dida</p><script>setTimeout(()=>window.print(),300)</script></body></html>`);
    pw.document.close();
  };

  // ── Mobil: nur Wochenplan (KW-Tabelle) + Urlaubskalender ──
  if (isMobileM) {
    const isos = kwIsoDays(wpKW);
    const dayNames = ["Mo","Di","Mi","Do","Fr","Sa","So"];
    const todayIso = new Date().toISOString().slice(0,10);
    const deDat = iso => { const [y,mo,d]=iso.split("-"); return `${d}.${mo}.${y.slice(2)}`; };
    const thM = {fontSize:9,color:"var(--mf-muted)",fontWeight:500,padding:"3px 2px",textAlign:"center",borderBottom:"0.5px solid var(--mf-border)"};
    const tdM = {fontSize:10,padding:"5px 2px",textAlign:"center",borderBottom:"0.5px solid var(--mf-border)"};
    const navB = {background:"var(--mf-bg-alt)",border:"none",borderRadius:8,padding:"4px 10px",cursor:"pointer",fontSize:13,color:"var(--mf-text)"};
    return (
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        <Card>
          <div style={{display:"flex",alignItems:"center",marginBottom:8,gap:6}}>
            <span style={{fontSize:12,fontWeight:600,flex:1}}>Wochenplan · KW {wpKW}</span>
            <button onClick={()=>setWpKW(k=>Math.max(1,k-1))} style={navB}>←</button>
            <button onClick={()=>setWpKW(k=>Math.min(53,k+1))} style={navB}>→</button>
          </div>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr><th style={{...thM,textAlign:"left"}}>Team</th>{dayNames.map(d=><th key={d} style={thM}>{d}</th>)}</tr></thead>
            <tbody>
              {team.map(m=>(
                <tr key={m.id}>
                  <td style={{...tdM,textAlign:"left",fontWeight:600,maxWidth:76,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.name.replace("Dr. ","")}</td>
                  {dayNames.map((d,i)=>{
                    const urlaub=(m.urlaubDaten||[]).includes(isos[i]);
                    const h=(m.stunden||{})[d]||0;
                    return <td key={d} style={{...tdM,color:urlaub?"var(--mf-neg)":h?"var(--mf-text)":"var(--mf-border2)",fontWeight:urlaub?700:400,background:isos[i]===todayIso?"var(--mf-bg-alt)":"transparent"}}>{urlaub?"U":(h||"—")}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {team.length===0&&<div style={{fontSize:11,color:"var(--mf-muted)",padding:"8px 0"}}>Noch kein Team angelegt — am Desktop unter „Mitarbeiter“ pflegen.</div>}
          <div style={{fontSize:9,color:"var(--mf-muted)",marginTop:5}}>Zahlen = Soll-Stunden · U = Urlaub · heutiger Tag hinterlegt</div>
        </Card>
        <Card>
          <CardHead title="Urlaubskalender" icon="ti-plane-departure"/>
          {team.map(m=>{
            const kommend=(m.urlaubDaten||[]).filter(d=>d>=todayIso).sort();
            return (
              <div key={m.id} style={{display:"flex",justifyContent:"space-between",gap:8,padding:"6px 0",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>
                <span style={{fontWeight:500}}>{m.name}</span>
                <span style={{color:kommend.length?"var(--mf-text)":"var(--mf-muted)",textAlign:"right"}}>{kommend.length?`${kommend.length} Tage · ab ${deDat(kommend[0])}`:"nichts geplant"}</span>
              </div>
            );
          })}
        </Card>
      </div>
    );
  }
  return (
    <div style={{display:"flex",flexDirection:"column",gap:10,position:"relative"}}>

      {/* Team overview */}
      <Card>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:9}}>
          <span style={{fontSize:12,fontWeight:500}}>Team</span>
          <div style={{display:"flex",gap:6}}>
            <Btn onClick={()=>{setChatTarget("group");setChatOpen(true);}}>Gruppen-Chat</Btn>
            {isInhaber&&<Btn color="g" onClick={()=>{setEinladenForm({name:"",email:"",rolle:"mfa"});setEinladenDone(false);setEinladenOpen(true);}}>+ Einladen</Btn>}
          </div>
        </div>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr>{["Mitarbeiter","Rolle","Vertrag","Urlaub","Stunden/Wo","Überstd.","Minstd.","Status","AU"].map(h=><th key={h} style={{textAlign:"left",color:"var(--mf-muted)",fontWeight:400,fontSize:10,padding:"3px 6px",borderBottom:"0.5px solid var(--mf-border)"}}>{h}</th>)}</tr></thead>
          <tbody>
            {team.map(m=>(
              <tr key={m.id} onClick={isInhaber?()=>setSelMember({...m}):null} style={{cursor:isInhaber?"pointer":"default"}} onMouseEnter={e=>{if(isInhaber)e.currentTarget.style.background="var(--mf-bg-alt)"}} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <td style={{padding:"6px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>
                  <div style={{display:"flex",alignItems:"center",gap:7}}>
                    <Av text={m.initials} bg={m.avatarBg} col={m.avatarCol} size={24}/>
                    <span style={{fontWeight:500}}>{m.name}</span>
                    {m.sick&&<Sp color="r">Krank</Sp>}
                  </div>
                </td>
                <td style={{padding:"6px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>{m.role}</td>
                <td style={{padding:"6px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>{m.contract}</td>
                <td style={{padding:"6px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>{m.urlaubGenommen}/{m.urlaub}d</td>
                <td style={{padding:"6px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>{totalH(m)}h</td>
                <td style={{padding:"6px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>{m.ueberstunden>0?<Sp color="g">+{m.ueberstunden}h</Sp>:"—"}</td>
                <td style={{padding:"6px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>{m.minusstunden>0?<Sp color="r">-{m.minusstunden}h</Sp>:"—"}</td>
                <td style={{padding:"6px 6px",borderBottom:"0.5px solid var(--mf-border)"}}>{m.sick?<Sp color="r">Krank</Sp>:<Sp color="g">Aktiv</Sp>}</td>
                <td style={{padding:"6px 6px",borderBottom:"0.5px solid var(--mf-border)"}} onClick={e=>e.stopPropagation()}>
                  <label style={{cursor:"pointer",fontSize:10,color:"var(--mf-primary)"}}>
                    <input type="file" accept=".pdf,.jpg,.png" style={{display:"none"}} onChange={e=>{if(e.target.files[0])setAuFiles(p=>({...p,[m.id]:e.target.files[0].name}));}}/>
                    {auFiles[m.id]?<span style={{fontSize:9,color:"#085041"}}>✓ {auFiles[m.id].slice(0,12)}</span>:<span>Hochladen</span>}
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {isInhaber&&<TeamPulseResults/>}

      {/* Wochenplan card - clickable */}
      <div onClick={()=>setWochenplanOpen(true)} style={{background:"var(--mf-bg)",border:"0.5px solid var(--mf-border)",borderRadius:10,padding:12,cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.boxShadow="0 2px 12px rgba(0,0,0,.07)"} onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <CardHead title="Wochenplan — KW 23" icon="ti-calendar-week"/>
          <span style={{fontSize:10,color:"var(--mf-primary)"}}>Öffnen →</span>
        </div>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed"}}>
            <thead><tr>
              <th style={{width:120,textAlign:"left",color:"var(--mf-muted)",fontWeight:400,fontSize:10,padding:"3px 6px",borderBottom:"0.5px solid var(--mf-border)"}}>Mitarbeiter</th>
              {DAYS.map(d=><th key={d} style={{textAlign:"center",color:"var(--mf-muted)",fontWeight:400,fontSize:10,padding:"3px 6px",borderBottom:"0.5px solid var(--mf-border)"}}>{d}</th>)}
              <th style={{textAlign:"right",color:"var(--mf-muted)",fontWeight:400,fontSize:10,padding:"3px 6px",borderBottom:"0.5px solid var(--mf-border)"}}>∑</th>
            </tr></thead>
            <tbody>
              {team.map(m=>(
                <tr key={m.id}>
                  <td style={{padding:"4px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:10}}>
                    <div style={{display:"flex",alignItems:"center",gap:5}}><Av text={m.initials} bg={m.avatarBg} col={m.avatarCol} size={18}/>{m.name.split(" ").slice(-1)[0]}</div>
                  </td>
                  {DAYS.map(d=>{
                    const h=m.stunden[d]||0;
                    const isKrank=m.sick&&(d==="Do"||d==="Fr");
                    const isUrlaub=(m.urlaubTageJuni||[]).some(day=>day>=2&&day<=8&&DAYS.indexOf(d)<5);
                    return (
                      <td key={d} style={{padding:"3px 2px",borderBottom:"0.5px solid var(--mf-border)",textAlign:"center"}}>
                        {isKrank?<span style={{fontSize:9,padding:"1px 4px",borderRadius:10,background:"#FCEBEB",color:"#791F1F"}}>K</span>
                        :isUrlaub?<span style={{fontSize:9,padding:"1px 4px",borderRadius:10,background:"#E1F5EE",color:"#085041"}}>U</span>
                        :h>0?<span style={{fontSize:9,padding:"1px 5px",borderRadius:10,background:m.avatarBg,color:m.avatarCol}}>{h}h</span>
                        :<span style={{fontSize:9,color:"var(--mf-muted)"}}>—</span>}
                      </td>
                    );
                  })}
                  <td style={{padding:"4px 6px",borderBottom:"0.5px solid var(--mf-border)",textAlign:"right",fontSize:10,fontWeight:500}}>{totalH(m)}h</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Urlaubskalender card */}
      <Card>
        <CardHead title="Urlaubskalender — Juni 2026" icon="ti-beach"/>
        {[23,24,25,26].map(kw=>{
          const vac = vacInKW(kw);
          const overlap = vac.length>=2;
          return (
            <div key={kw} style={{marginBottom:6,padding:"6px 8px",borderRadius:6,background:overlap?"#FFF3E0":"var(--mf-bg-alt)",border:overlap?"0.5px solid #FFA726":"none"}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                <span style={{fontSize:11,fontWeight:500,color:overlap?"#E65100":"var(--mf-text)"}}>KW {kw}</span>
                {overlap&&<span style={{fontSize:9,padding:"1px 6px",borderRadius:10,background:"#FFA726",color:"#fff",fontWeight:500}}>⚠ Überschneidung</span>}
              </div>
              <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                {vac.length?vac.map(m=>(
                  <span key={m.id} style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:m.avatarBg,color:m.avatarCol}}>{m.name.split(" ").slice(-1)[0]}</span>
                )):<span style={{fontSize:10,color:"var(--mf-muted)"}}>Kein Urlaub</span>}
              </div>
            </div>
          );
        })}
        <div style={{marginTop:6}}>
          <div style={{fontSize:10,color:"var(--mf-muted)",marginBottom:5}}>Kalenderübersicht Juni</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:1}}>
            {["Mo","Di","Mi","Do","Fr","Sa","So"].map(d=><div key={d} style={{textAlign:"center",fontSize:8,color:"var(--mf-muted)",padding:"2px 0",fontWeight:500}}>{d}</div>)}
            {calCells(2026,5).map((day,i)=>{
              if(!day) return <div key={"e"+i}/>;
              const members = team.filter(m=>(m.urlaubTageJuni||[]).includes(day));
              const isSick = team.some(m=>m.sick&&day>=19&&day<=23);
              return (
                <div key={day} style={{textAlign:"center",padding:"2px 1px",borderRadius:3,background:members.length>=2?"#FFF3E0":members.length===1?"#E1F5EE":isSick?"#FCEBEB":"transparent",border:"0.5px solid var(--mf-border)",minHeight:22}}>
                  <div style={{fontSize:8,color:"var(--mf-muted)"}}>{day}</div>
                  <div style={{display:"flex",justifyContent:"center",gap:1,flexWrap:"wrap"}}>
                    {members.map(m=><div key={m.id} style={{width:4,height:4,borderRadius:"50%",background:m.avatarCol}}/>)}
                    {isSick&&!members.length&&<div style={{width:4,height:4,borderRadius:"50%",background:"#791F1F"}}/>}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{display:"flex",gap:10,marginTop:6,fontSize:9,color:"var(--mf-muted)"}}>
            <span><span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",background:"#0F6E56",marginRight:3}}/>Urlaub</span>
            <span><span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",background:"#FFA726",marginRight:3}}/>Überschneidung</span>
            <span><span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",background:"#791F1F",marginRight:3}}/>Krank</span>
          </div>
        </div>
      </Card>

      {/* WOCHENPLAN MODAL */}
      {wochenplanOpen&&<>
        <div onClick={()=>setWochenplanOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay}}/>
        <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:"90vw",height:"85vh",maxWidth:1100,background:"var(--mf-bg)",borderRadius:14,zIndex:Z.modal,display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 64px rgba(0,0,0,.3)"}}>
          <div style={{padding:"14px 18px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
            <span style={{fontSize:14,fontWeight:600,flex:1}}>Wochenplan</span>
            <button onClick={()=>wpView==="woche"?setWpKW(k=>Math.max(1,k-1)):prevMonth()} style={{background:"var(--mf-bg-alt)",border:"none",borderRadius:6,padding:"4px 9px",cursor:"pointer",fontSize:13}}>←</button>
            <span style={{fontSize:12,fontWeight:500,minWidth:90,textAlign:"center"}}>
              {wpView==="woche"?`KW ${wpKW}`:`${MONTH_NAMES[wpMonth.month]} ${wpMonth.year}`}
            </span>
            <button onClick={()=>wpView==="woche"?setWpKW(k=>Math.min(53,k+1)):nextMonth()} style={{background:"var(--mf-bg-alt)",border:"none",borderRadius:6,padding:"4px 9px",cursor:"pointer",fontSize:13}}>→</button>
            <div style={{display:"flex",gap:4,marginLeft:8}}>
              {["woche","monat"].map(v=>(
                <button key={v} onClick={()=>setWpView(v)} style={{fontSize:11,padding:"4px 12px",borderRadius:6,border:"none",background:wpView===v?"#0F6E56":"var(--mf-bg-alt)",color:wpView===v?"#E1F5EE":"var(--mf-text)",cursor:"pointer",fontWeight:wpView===v?500:400}}>
                  {v==="woche"?"Woche":"Monat"}
                </button>
              ))}
            </div>
            <button onClick={printWochenplan} style={{fontSize:11,padding:"4px 12px",borderRadius:6,border:"0.5px solid var(--mf-border2)",background:"var(--mf-bg-alt)",cursor:"pointer",marginLeft:4}}>PDF exportieren</button>
            {wpView==="woche"&&hasOverlap(wpKW)&&<span style={{fontSize:11,padding:"4px 10px",borderRadius:6,background:"#FFF3E0",color:"#E65100",fontWeight:500}}>⚠ Urlaubsüberschneidung KW {wpKW}</span>}
            <div onClick={()=>setWochenplanOpen(false)} style={{cursor:"pointer",fontSize:20,color:"var(--mf-muted)",width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",background:"var(--mf-bg-alt)"}}>×</div>
          </div>

          <div style={{flex:1,overflowY:"auto",padding:"16px 18px"}}>
            {wpView==="woche"?(
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr>
                  <th style={{width:160,textAlign:"left",color:"var(--mf-muted)",fontWeight:400,fontSize:11,padding:"6px 8px",borderBottom:"1px solid #e7e5e4"}}>Mitarbeiter</th>
                  {DAYS.map(d=><th key={d} style={{textAlign:"center",color:"var(--mf-muted)",fontWeight:400,fontSize:11,padding:"6px 8px",borderBottom:"1px solid #e7e5e4"}}>{d}</th>)}
                  <th style={{textAlign:"right",color:"var(--mf-muted)",fontWeight:400,fontSize:11,padding:"6px 8px",borderBottom:"1px solid #e7e5e4"}}>∑</th>
                </tr></thead>
                <tbody>
                  {team.map(m=>{
                    const mDays = kwDays(wpKW);
                    return (
                      <tr key={m.id} style={{borderBottom:"0.5px solid var(--mf-border)"}}>
                        <td style={{padding:"8px 8px",fontSize:12}}>
                          <div style={{display:"flex",alignItems:"center",gap:7}}><Av text={m.initials} bg={m.avatarBg} col={m.avatarCol} size={24}/><div><div style={{fontWeight:500}}>{m.name}</div><div style={{fontSize:10,color:"var(--mf-muted)"}}>{m.ueberstunden>0?`+${m.ueberstunden}h Überst.`:m.minusstunden>0?`-${m.minusstunden}h Minusst.`:"Konto ausgeglichen"}</div></div></div>
                        </td>
                        {DAYS.map((d,di)=>{
                          const dayOfMonth = mDays[di];
                          const isUrlaub = dayOfMonth&&(m.urlaubTageJuni||[]).includes(dayOfMonth);
                          const isKrank = m.sick&&(d==="Do"||d==="Fr");
                          const h = m.stunden[d]||0;
                          const cellKey = `${m.id}-${d}`;
                          const isEditing = editingCell===cellKey;
                          const bg = isKrank?"#FCEBEB":isUrlaub?"#E1F5EE":"transparent";
                          return (
                            <td key={d} style={{padding:"6px 4px",textAlign:"center",background:bg}}>
                              {isKrank?<span style={{fontSize:10,padding:"2px 7px",borderRadius:10,background:"#FCEBEB",color:"#791F1F",fontWeight:500}}>Krank</span>
                              :isUrlaub?<span style={{fontSize:10,padding:"2px 7px",borderRadius:10,background:"#E1F5EE",color:"#085041",fontWeight:500}}>Urlaub</span>
                              :isEditing?(
                                <input autoFocus type="number" min={0} max={12} defaultValue={h}
                                  style={{width:44,fontSize:11,padding:"3px 4px",borderRadius:5,border:"1px solid #0F6E56",textAlign:"center",fontFamily:"inherit"}}
                                  onBlur={e=>{const v=+e.target.value;setTeam(prev=>prev.map(t=>t.id===m.id?{...t,stunden:{...t.stunden,[d]:v}}:t));setEditingCell(null);}}
                                  onKeyDown={e=>{if(e.key==="Enter"||e.key==="Escape"){e.target.blur();}}}/>
                              ):(
                                <span onClick={()=>setEditingCell(cellKey)} style={{fontSize:11,padding:"3px 8px",borderRadius:6,cursor:"pointer",background:h>0?m.avatarBg:"var(--mf-bg-alt)",color:h>0?m.avatarCol:"var(--mf-muted)",display:"inline-block",minWidth:32}}>
                                  {h>0?`${h}h`:"—"}
                                </span>
                              )}
                            </td>
                          );
                        })}
                        <td style={{padding:"8px 8px",textAlign:"right",fontSize:12,fontWeight:600}}>{totalH(m)}h</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ):(
              <div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:4}}>
                  {["Mo","Di","Mi","Do","Fr","Sa","So"].map(d=><div key={d} style={{textAlign:"center",fontSize:11,color:"var(--mf-muted)",fontWeight:500,padding:"4px 0"}}>{d}</div>)}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
                  {calCells(wpMonth.year,wpMonth.month).map((day,i)=>{
                    if(!day) return <div key={"e"+i} style={{minHeight:60}}/>;
                    const isJune2026 = wpMonth.year===2026&&wpMonth.month===5;
                    const iso = `${wpMonth.year}-${String(wpMonth.month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
                    const vacM = team.filter(m=>(m.urlaubDaten||[]).includes(iso)||(isJune2026&&(m.urlaubTageJuni||[]).includes(day)));
                    const sickM = isJune2026?team.filter(m=>m.sick&&day>=19&&day<=23):[];
                    return (
                      <div key={day} style={{minHeight:60,border:"0.5px solid var(--mf-border)",borderRadius:4,padding:"3px 4px",background:vacM.length>=2?"#FFF3E0":"var(--mf-bg)"}}>
                        <div style={{fontSize:10,fontWeight:500,color:"var(--mf-muted)",marginBottom:3}}>{day}</div>
                        <div style={{display:"flex",flexDirection:"column",gap:1}}>
                          {vacM.map(m=><div key={m.id} style={{fontSize:8,padding:"1px 4px",borderRadius:8,background:m.avatarBg,color:m.avatarCol}}>{m.initials}</div>)}
                          {sickM.map(m=><div key={m.id} style={{fontSize:8,padding:"1px 4px",borderRadius:8,background:"#FCEBEB",color:"#791F1F"}}>{m.initials}K</div>)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{display:"flex",gap:12,marginTop:10,fontSize:10,color:"var(--mf-muted)"}}>
                  <span><span style={{display:"inline-block",width:10,height:10,borderRadius:2,background:"#E1F5EE",marginRight:4}}/>Urlaub</span>
                  <span><span style={{display:"inline-block",width:10,height:10,borderRadius:2,background:"#FFF3E0",marginRight:4}}/>Überschneidung</span>
                  <span><span style={{display:"inline-block",width:10,height:10,borderRadius:2,background:"#FCEBEB",marginRight:4}}/>Krank</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </>}

      {/* MEMBER DETAIL MODAL */}
      {selMember&&<>
        <div onClick={()=>setSelMember(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay}}/>
        <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:520,height:"85vh",maxWidth:"96vw",background:"var(--mf-bg)",borderRadius:14,zIndex:Z.modal,display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 64px rgba(0,0,0,.3)"}}>
          <div style={{padding:"14px 18px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",gap:9,flexShrink:0}}>
            <Av text={selMember.initials} bg={selMember.avatarBg} col={selMember.avatarCol} size={36}/>
            <div style={{flex:1}}><div style={{fontSize:14,fontWeight:600}}>{selMember.name}</div><div style={{fontSize:11,color:"var(--mf-muted)"}}>{selMember.role} · {selMember.contract}</div></div>
            <div onClick={()=>setSelMember(null)} style={{cursor:"pointer",fontSize:20,color:"var(--mf-muted)",width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",background:"var(--mf-bg-alt)"}}>×</div>
          </div>
          <div style={{padding:"14px 18px",flex:1,overflowY:"auto"}}>
            <SLabel>Wochenarbeitszeit</SLabel>
            {(()=>{
              const sum = DAYS.reduce((s,d)=>s+(selMember.stunden[d]||0),0);
              const soll = parseInt((selMember.contract.match(/\d+/)||[40])[0]);
              const diff = sum - soll;
              const setDay = (d,val)=>{const s={...selMember.stunden,[d]:Math.max(0,Math.min(12,val))};const u={...selMember,stunden:s};setSelMember(u);setTeam(prev=>prev.map(m=>m.id===selMember.id?u:m));};
              return (<>
                <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:5,marginBottom:8}}>
                  {DAYS.map((d,di)=>{
                    const val = selMember.stunden[d]||0;
                    const frei = val===0;
                    return (
                      <div key={d} style={{textAlign:"center",background:frei?"transparent":"var(--mf-bg-alt)",border:frei?"1px dashed var(--mf-border2)":"1px solid transparent",borderRadius:10,padding:"6px 2px"}}>
                        <div style={{fontSize:9,fontWeight:600,color:di>=5?"var(--mf-neg)":"var(--mf-muted)",marginBottom:2}}>{d}</div>
                        <input type="number" min={0} max={12} value={val}
                          onChange={e=>setDay(d,+e.target.value)}
                          style={{width:"100%",fontSize:13,fontWeight:600,padding:"3px 0",borderRadius:6,border:"none",background:"transparent",color:frei?"var(--mf-muted)":"var(--mf-text)",textAlign:"center",fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}/>
                        <div style={{fontSize:8,color:"var(--mf-muted)"}}>{frei?"frei":"Std."}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:"var(--mf-bg-alt)",borderRadius:10,padding:"8px 12px",marginBottom:4}}>
                  <span style={{fontSize:11,color:"var(--mf-muted)"}}>Summe pro Woche</span>
                  <span style={{fontSize:13,fontWeight:600}}>{sum} h <span style={{fontSize:10,fontWeight:400,color:"var(--mf-muted)"}}>/ Vertrag {soll} h</span>
                    {diff!==0&&<Sp color={diff>0?"a":"r"} style={{marginLeft:6}}>{diff>0?`+${diff} h`:`${diff} h`}</Sp>}
                    {diff===0&&<Sp color="g" style={{marginLeft:6}}>passt</Sp>}
                  </span>
                </div>
              </>);
            })()}
            <Divider/>
            <SLabel>Urlaub</SLabel>
            {(()=>{
              const rest = selMember.urlaub - selMember.urlaubGenommen;
              const pct = Math.min(100, Math.round(selMember.urlaubGenommen/selMember.urlaub*100));
              const setUrlaub = (delta)=>{const u={...selMember,urlaubGenommen:Math.max(0,Math.min(selMember.urlaub,selMember.urlaubGenommen+delta))};setSelMember(u);setTeam(prev=>prev.map(m=>m.id===selMember.id?u:m));};
              return (
                <div style={{background:"var(--mf-bg-alt)",borderRadius:12,padding:"10px 12px",marginBottom:8}}>
                  <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:6}}>
                    <span style={{fontSize:20,fontWeight:600,color:"var(--mf-primary)"}}>{rest} <span style={{fontSize:11,fontWeight:400,color:"var(--mf-muted)"}}>Tage übrig</span></span>
                    <span style={{fontSize:11,color:"var(--mf-muted)"}}>{selMember.urlaubGenommen} von {selMember.urlaub} genommen</span>
                  </div>
                  <div style={{height:7,background:"var(--mf-bg)",borderRadius:4,overflow:"hidden",marginBottom:9}}>
                    <div style={{height:7,width:`${pct}%`,background:"var(--mf-primary)",borderRadius:4,transition:"width .2s"}}/>
                  </div>
                  <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:10}}>
                    <Btn small onClick={()=>setUrlaub(-1)}>−</Btn>
                    <span style={{fontSize:11,color:"var(--mf-muted)"}}>Einzeltag korrigieren</span>
                    <Btn small color="g" onClick={()=>setUrlaub(1)}>+</Btn>
                    <div style={{flex:1}}/>
                    <span style={{fontSize:10,color:"var(--mf-muted)"}}>Kranktage: <strong style={{color:"var(--mf-text)"}}>{selMember.kranktage}</strong></span>
                  </div>
                  <div style={{borderTop:"0.5px solid var(--mf-border)",paddingTop:9}}>
                    <div style={{fontSize:10,fontWeight:600,color:"var(--mf-muted)",marginBottom:6}}>URLAUB PLANEN</div>
                    <div style={{display:"flex",gap:7,alignItems:"center",flexWrap:"wrap"}}>
                      <input type="date" value={vacFrom} onChange={e=>setVacFrom(e.target.value)} style={{fontSize:12,padding:"7px 9px",borderRadius:9,border:"1px solid var(--mf-border2)",background:"var(--mf-bg)",color:"var(--mf-text)",fontFamily:"inherit"}}/>
                      <span style={{fontSize:11,color:"var(--mf-muted)"}}>bis</span>
                      <input type="date" value={vacTo} onChange={e=>setVacTo(e.target.value)} style={{fontSize:12,padding:"7px 9px",borderRadius:9,border:"1px solid var(--mf-border2)",background:"var(--mf-bg)",color:"var(--mf-text)",fontFamily:"inherit"}}/>
                      <Btn color="g" onClick={()=>{
                        if(!vacFrom) return;
                        const from = new Date(vacFrom+"T12:00"), to = new Date((vacTo||vacFrom)+"T12:00");
                        if(to<from){ toast("Das Bis-Datum liegt vor dem Von-Datum.","r"); return; }
                        const days=[];
                        for(let d=new Date(from); d<=to; d.setDate(d.getDate()+1)){
                          const wd=d.getDay();
                          if(wd!==0&&wd!==6) days.push(d.toISOString().slice(0,10));
                        }
                        const existing = new Set(selMember.urlaubDaten||[]);
                        const neu = days.filter(x=>!existing.has(x));
                        if(!neu.length){ toast("Zeitraum enthält keine neuen Werktage.","r"); return; }
                        const u={...selMember,urlaubDaten:[...(selMember.urlaubDaten||[]),...neu].sort(),urlaubGenommen:Math.min(selMember.urlaub,selMember.urlaubGenommen+neu.length)};
                        setSelMember(u);setTeam(prev=>prev.map(m=>m.id===selMember.id?u:m));
                        setVacFrom("");setVacTo("");
                        toast(`${neu.length} Urlaubstag${neu.length===1?"":"e"} eingetragen — sichtbar im Monatskalender`);
                      }}>Eintragen</Btn>
                    </div>
                    {(selMember.urlaubDaten||[]).length>0&&(
                      <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:8}}>
                        {(selMember.urlaubDaten||[]).slice(-8).map(d=>(
                          <span key={d} style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:10,padding:"2px 7px",borderRadius:9,background:"var(--mf-bg)",border:"0.5px solid var(--mf-border)"}}>
                            {new Date(d+"T12:00").toLocaleDateString("de-DE",{day:"2-digit",month:"2-digit"})}
                            <span onClick={()=>{const u={...selMember,urlaubDaten:(selMember.urlaubDaten||[]).filter(x=>x!==d),urlaubGenommen:Math.max(0,selMember.urlaubGenommen-1)};setSelMember(u);setTeam(prev=>prev.map(m=>m.id===selMember.id?u:m));}} style={{cursor:"pointer",color:"var(--mf-muted)"}}>×</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              <Btn color={selMember.sick?"a":undefined} onClick={()=>{const updated={...selMember,sick:!selMember.sick,kranktage:selMember.sick?selMember.kranktage:selMember.kranktage+1,sickNote:selMember.sick?"":new Date().toLocaleDateString("de-DE")};setSelMember(updated);setTeam(prev=>prev.map(m=>m.id===selMember.id?updated:m));}}>
                {selMember.sick?"Gesundschreiben":"Krankmeldung"}
              </Btn>
            </div>
            {selMember.sick&&<div style={{marginTop:6,fontSize:11,color:"#791F1F",background:"#FCEBEB",padding:"6px 9px",borderRadius:5}}>Krankmeldung seit: {selMember.sickNote}</div>}
            <div style={{marginTop:8}}>
              <div style={{fontSize:10,color:"var(--mf-muted)",marginBottom:4}}>AU-Bescheinigung hochladen</div>
              <label style={{cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6,fontSize:11,padding:"5px 10px",borderRadius:6,border:"0.5px solid var(--mf-border2)",background:"var(--mf-bg-alt)",color:"var(--mf-text)"}}>
                <i className="ti ti-upload" style={{fontSize:13}}/>
                <input type="file" accept=".pdf,.jpg,.png" style={{display:"none"}} onChange={e=>{if(e.target.files[0]){const fn=e.target.files[0].name;setAuFiles(p=>({...p,[selMember.id]:fn}));const u={...selMember,sickNote:selMember.sickNote||new Date().toLocaleDateString("de-DE")};setSelMember(u);}}}/>
                {auFiles[selMember.id]||"Datei auswählen"}
              </label>
              {auFiles[selMember.id]&&<span style={{fontSize:10,color:"var(--mf-primary)",marginLeft:8}}>✓ Gespeichert</span>}
            </div>
            <Divider/>
            <SLabel>Überstunden &amp; Minusstunden</SLabel>
            <div style={{display:"flex",gap:7,marginBottom:8}}>
              <div style={{flex:1,background:"#E1F5EE",borderRadius:6,padding:"8px 10px",textAlign:"center"}}><div style={{fontSize:16,fontWeight:500,color:"#085041"}}>+{selMember.ueberstunden}h</div><div style={{fontSize:10,color:"#085041"}}>Überstunden</div></div>
              <div style={{flex:1,background:"#FCEBEB",borderRadius:6,padding:"8px 10px",textAlign:"center"}}><div style={{fontSize:16,fontWeight:500,color:"#791F1F"}}>-{selMember.minusstunden}h</div><div style={{fontSize:10,color:"#791F1F"}}>Minusstunden</div></div>
            </div>
            {(()=>{
              const adj = (key,delta)=>{const u={...selMember,[key]:Math.max(0,selMember[key]+delta)};setSelMember(u);setTeam(prev=>prev.map(m=>m.id===selMember.id?u:m));};
              return (
                <div style={{display:"flex",gap:7}}>
                  <div style={{flex:1,display:"flex",gap:5,justifyContent:"center"}}>
                    <Btn small onClick={()=>adj("ueberstunden",-1)}>−</Btn>
                    <Btn small color="g" onClick={()=>adj("ueberstunden",1)}>+</Btn>
                  </div>
                  <div style={{flex:1,display:"flex",gap:5,justifyContent:"center"}}>
                    <Btn small onClick={()=>adj("minusstunden",-1)}>−</Btn>
                    <Btn small color="r" onClick={()=>adj("minusstunden",1)}>+</Btn>
                  </div>
                </div>
              );
            })()}
            <Divider/>
            <Btn onClick={()=>{setSelMember(null);setChatTarget(selMember.initials);setChatOpen(true);}} style={{width:"100%",textAlign:"center"}}>Direktnachricht senden</Btn>
          </div>
        </div>
      </>}

      {/* CHAT */}
      {chatOpen&&(
        <>
          <div onClick={()=>setChatOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.3)",zIndex:Z.chatOverlay}}/>
          <div style={{position:"fixed",bottom:24,right:24,width:320,height:420,background:"var(--mf-bg)",border:"0.5px solid var(--mf-border2)",borderRadius:"10px",zIndex:Z.chat,display:"flex",flexDirection:"column",boxShadow:"0 4px 24px rgba(0,0,0,.12)"}}>
            <div style={{padding:"10px 12px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",gap:8}}>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:500}}>Chat: {chatTarget==="group"?"Gruppen-Chat":team.find(m=>m.initials===chatTarget)?.name||chatTarget}</div>
                <div style={{fontSize:10,color:"var(--mf-muted)"}}>{chatTarget==="group"?"Alle Teammitglieder":"Direktnachricht"}</div>
              </div>
              <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                <button onClick={()=>setChatTarget("group")} style={{fontSize:9,padding:"2px 6px",borderRadius:10,border:"0.5px solid var(--mf-border2)",background:chatTarget==="group"?"#0F6E56":"transparent",color:chatTarget==="group"?"#E1F5EE":"var(--mf-muted)",cursor:"pointer"}}>Gruppe</button>
                {team.filter(m=>m.initials!==ROLES_CFG[role]?.fav).map(m=>(
                  <button key={m.initials} onClick={()=>setChatTarget(m.initials)} style={{fontSize:9,padding:"2px 6px",borderRadius:10,border:"0.5px solid var(--mf-border2)",background:chatTarget===m.initials?"#0F6E56":"transparent",color:chatTarget===m.initials?"#E1F5EE":"var(--mf-muted)",cursor:"pointer"}}>{m.initials}</button>
                ))}
              </div>
              <div onClick={()=>setChatOpen(false)} style={{cursor:"pointer",fontSize:16,color:"var(--mf-muted)"}}>×</div>
            </div>
            <div style={{flex:1,overflowY:"auto",padding:"10px 12px",display:"flex",flexDirection:"column",gap:7}}>
              {(messages[chatTarget]||[]).map((m,i)=>{
                const myFav = ROLES_CFG[role]?.fav;
                const isMe = m.from===myFav;
                return (
                  <div key={`${m.from}-${m.time}-${i}`} style={{display:"flex",flexDirection:isMe?"row-reverse":"row",gap:7,alignItems:"flex-end"}}>
                    <div style={{width:22,height:22,borderRadius:"50%",background:team.find(t=>t.initials===m.from)?.avatarBg||"#E1F5EE",color:team.find(t=>t.initials===m.from)?.avatarCol||"#085041",display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:500,flexShrink:0}}>{m.from}</div>
                    <div style={{maxWidth:"75%",background:isMe?"#0F6E56":"var(--mf-bg-alt)",color:isMe?"#E1F5EE":"var(--mf-text)",borderRadius:10,padding:"6px 9px",fontSize:11,lineHeight:1.5}}>
                      {m.text}<div style={{fontSize:9,opacity:.6,marginTop:2,textAlign:isMe?"right":"left"}}>{m.time}</div>
                    </div>
                  </div>
                );
              })}
              {!(messages[chatTarget]||[]).length&&<div style={{textAlign:"center",fontSize:11,color:"var(--mf-muted)",marginTop:20}}>Noch keine Nachrichten</div>}
              <div ref={chatEndRef}/>
            </div>
            <div style={{padding:"8px 10px",borderTop:"0.5px solid var(--mf-border)",display:"flex",gap:6}}>
              <input value={msgInput} onChange={e=>setMsgInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&sendMsg()} placeholder="Nachricht…" style={{flex:1,fontSize:12,padding:"10px 13px",borderRadius:13,border:"1.5px solid var(--mf-border2)",background:"var(--mf-bg-alt)",color:"var(--mf-text)",fontFamily:"inherit",outline:"none"}}/>
              <Btn color="g" onClick={sendMsg}>↑</Btn>
            </div>
          </div>
        </>
      )}

      {/* EINLADEN MODAL */}
      {einladenOpen&&<>
        <div onClick={()=>setEinladenOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay2}}/>
        <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:400,background:"var(--mf-bg)",borderRadius:14,zIndex:Z.modal2,boxShadow:"0 24px 64px rgba(0,0,0,.3)",overflow:"hidden"}}>
          <div style={{padding:"14px 18px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{fontSize:13,fontWeight:500}}>Neues Teammitglied einladen</div>
            <div onClick={()=>setEinladenOpen(false)} style={{cursor:"pointer",fontSize:18,color:"var(--mf-muted)"}}>×</div>
          </div>
          {einladenDone?(
            <div style={{padding:"28px 18px",textAlign:"center"}}>
              <div style={{marginBottom:10}}><Picto.Mail size={40}/></div>
              <div style={{fontSize:13,fontWeight:500,marginBottom:4}}>Einladung gesendet!</div>
              <div style={{fontSize:11,color:"var(--mf-muted)",marginBottom:16}}>Eine Einladungs-E-Mail wurde an <strong>{einladenForm.email}</strong> gesendet.</div>
              <Btn color="g" onClick={()=>setEinladenOpen(false)}>Schließen</Btn>
            </div>
          ):(
            <div style={{padding:"16px 18px",display:"flex",flexDirection:"column"}}>
              <Input label="Name" value={einladenForm.name} onChange={v=>setEinladenForm(f=>({...f,name:v}))} placeholder="Vor- und Nachname"/>
              <Input label="E-Mail" value={einladenForm.email} onChange={v=>setEinladenForm(f=>({...f,email:v}))} placeholder="name@praxis.de" type="email"/>
              <Select label="Rolle" value={einladenForm.rolle} onChange={v=>setEinladenForm(f=>({...f,rolle:v}))} options={["mfa","arzt","dh","inhaber"].map(r=>({value:r,label:ROLES_CFG[r]?.label||r}))}/>
              <div style={{display:"flex",gap:10,marginTop:4}}>
                <Btn color="g" lg style={{flex:1}} onClick={()=>{if(!einladenForm.email.trim()||!einladenForm.name.trim())return;setEinladenDone(true);}}>Einladung senden</Btn>
                <Btn lg onClick={()=>setEinladenOpen(false)}>Abbrechen</Btn>
              </div>
            </div>
          )}
        </div>
      </>}
    </div>
  );
}

// ─── UMSATZ SCREEN ────────────────────────────────────────────────────────────
function UmsatzScreen({ role="inhaber" }) {
  const [urole, setUrole] = useState(()=>["arzt","arzt2","arzt3","dh"].includes(role)?role:"inhaber");
  const [period, setPeriod] = useState("Juni 2026");
  // Mahnlauf: echte Zahlungserinnerungen per Mailer, WYSIWYG-Text mit Platzhaltern
  const [mahnOpen, setMahnOpen] = useState(false);
  const [mahnBusy, setMahnBusy] = useState(false);
  const [mahnResult, setMahnResult] = useState(null);
  const [mahnSubject, setMahnSubject] = useState("Zahlungserinnerung zu Rechnung {nr}");
  const [mahnBody, setMahnBody] = useState(
    "Liebe/r {name},\n\nunsere Rechnung {nr} über {betrag} ist bislang offen. Vermutlich ist Ihnen dies entgangen — wir bitten Sie freundlich, den Betrag in den nächsten Tagen auszugleichen.\n\nSollten Sie die Zahlung bereits veranlasst haben, betrachten Sie diese Erinnerung bitte als gegenstandslos.\n\nBei Fragen zu Ihrer Rechnung sind wir gerne für Sie da.\n\nMit freundlichen Grüßen\n" + praxisSig()
  );

  // ── Echte Invoice-Daten: Cache sofort, dann frisch (LIVE: Supabase)
  const [ALL_INVOICES, setAllInvoices] = useState(()=>invoiceStore.loadCache()||[]);
  useEffect(()=>{ let on=true; invoiceStore.list().then(r=>{ if(on) setAllInvoices(r); }).catch(()=>{}); return ()=>{ on=false; }; },[]);
  const REVENUE_DATA = useMemo(() => {
    try { const s=localStorage.getItem("dida_revenue"); if(s) return JSON.parse(s); } catch {}
    return null;
  }, []);
  // Demo-Inhalte (Behandler-Tabs, Beteiligungen, "Umsatz je Behandler") nur
  // zeigen, wenn Demo-Daten geladen sind — im Demo-Modus automatisch, im
  // Live-Betrieb über App-Einstellungen → "Demo-Daten laden".
  const demoLoaded = useMemo(() => {
    try { return !!localStorage.getItem("dida_demo_loaded"); } catch { return !isSupabaseConfigured; }
  }, []);
  // Echte Behandler-Umsätze aus dem Import (typ "umsatz_behandler") — wenn
  // vorhanden, ersetzen sie die Demo-Tabelle "Umsatz je Behandler".
  const [behStats, setBehStats] = useState([]);
  useEffect(() => { listStats("umsatz_behandler").then(setBehStats).catch(() => {}); }, []);
  const behReal = useMemo(() => {
    const rows = behStats.map(r => ({
      monat: String(r.payload?.monat || String(r.periode).split("|")[0] || "").trim(),
      name: r.payload?.behandler || String(r.periode).split("|")[1] || "?",
      umsatz: r.payload?.umsatz || 0,
    }));
    const inPeriod = rows.filter(r => r.monat.toLowerCase() === String(period).trim().toLowerCase());
    const use = inPeriod.length ? inPeriod : rows;
    const byName = {};
    use.forEach(r => { byName[r.name] = (byName[r.name] || 0) + r.umsatz; });
    const total = Object.values(byName).reduce((a, b) => a + b, 0);
    return {
      list: Object.entries(byName).map(([n, v]) => ({ n, v, pct: total ? Math.round(v / total * 100) : 0 })).sort((a, b) => b.v - a.v),
      exact: inPeriod.length > 0,
      rows,
    };
  }, [behStats, period]);
  // Tabs: Demo-Behandler nur ohne echte Import-Daten; sonst Praxis-Tab plus
  // ein Tab je konfiguriertem Behandler (persönliche Beteiligungs-Ansicht).
  const hasRealBeh = behReal.list.length > 0;
  const umTabs = (demoLoaded && !hasRealBeh)
    ? [["inhaber","DH","Dr. Hoffmann · Inhaberin","#E1F5EE","#085041"],["arzt","DS","Dr. Schulz · Chirurgie","#E6F1FB","#0C447C"],["arzt2","LBe","Dr. Bergmann · KFO","#EEEDFE","#3C3489"],["arzt3","TF","Dr. Fischer · Assistenz","#FFF0E6","#7A3800"],["dh","MR","Maja Roth · ZMP","#F0FFF4","#166534"]].filter(([k])=>role==="inhaber"||k===role)
    : [["inhaber",ini(getPraxis().name||"Praxis"),`${getPraxis().name||"Praxis"} · Übersicht`,"rgba(53,201,155,0.16)","var(--mf-primary)"],
       ...(role==="inhaber" ? getBehandler().map(b=>[`b:${b.id}`,b.short,b.name,"rgba(53,201,155,0.16)","var(--mf-primary)"]) : [])];
  useEffect(() => { if (!umTabs.some(t => t[0] === urole)) setUrole("inhaber"); }, [demoLoaded, hasRealBeh, urole]); // eslint-disable-line react-hooks/exhaustive-deps
  // Monat-Text ("Juni 2026", "2026-06", "06/2026") → sortierbarer Index
  const monatIdx = (s) => {
    const iso = String(s).match(/(\d{4})[-/](\d{1,2})/); if (iso) return +iso[1]*12 + +iso[2];
    const parts = String(s).split(/[\s/.]+/);
    const mi = MONATE_DE.findIndex(x => x.toLowerCase().startsWith((parts[0]||"").toLowerCase().slice(0,3)));
    const yr = parseInt(parts[1]);
    return (mi >= 0 && yr) ? yr*12 + mi + 1 : 0;
  };
  const MONATE_DE = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
  const parseInvDate = (d) => { try { if(d&&d.includes("-")&&d.length>=8) return new Date(d); const [dd,mm,yy]=d.split("."); return new Date(2000+parseInt(yy),parseInt(mm)-1,parseInt(dd)); } catch { return new Date(0); } };
  const filterInvByPeriod = (invs, p) => {
    if(!p) return invs;
    const parts = p.split(" ");
    if (p.startsWith("Q")) {
      const q=parseInt(p[1]), yr=parseInt(parts[1]);
      return invs.filter(i=>{ const d=parseInvDate(i.datum); return Math.floor(d.getMonth()/3)+1===q&&d.getFullYear()===yr; });
    }
    if (p.endsWith("gesamt")) {
      const yr=parseInt(p);
      return invs.filter(i=>parseInvDate(i.datum).getFullYear()===yr);
    }
    const mi=MONATE_DE.indexOf(parts[0]), yr=parseInt(parts[1]);
    if(mi>=0&&yr) return invs.filter(i=>{ const d=parseInvDate(i.datum); return d.getMonth()===mi&&d.getFullYear()===yr; });
    return invs;
  };
  const invPeriod = filterInvByPeriod(ALL_INVOICES, period);

  // ── Mahnlauf: offene Rechnungen des Zeitraums, echte Erinnerungs-Mails
  const invId = (i) => i.id || parseInt(String(i.nr).replace(/\D/g,"")) || Date.now();
  const mahnKandidaten = invPeriod.filter(i=>i.status==="offen"||i.status==="mahnung");
  const mahnMitMail = mahnKandidaten.map(i=>({inv:i, email:findPatientEmail(i.patient)}));
  const mahnSendbar = mahnMitMail.filter(t=>t.email);
  const runMahnlauf = async () => {
    if (serverActive() && !mahnSendbar.length) { toast("Keine offene Rechnung hat eine hinterlegte Patienten-E-Mail.","r"); return; }
    setMahnBusy(true);
    try {
      const fill = (s,inv) => String(s).replace(/{nr}/g,inv.nr).replace(/{name}/g,inv.patient.split(",")[1]?.trim()||inv.patient).replace(/{betrag}/g,inv.betrag);
      // Live: nur Rechnungen mit E-Mail. Demo: alle (simulierter Versand).
      const targets = serverActive() ? mahnSendbar : mahnMitMail.map(t=>({inv:t.inv, email:t.email||"demo@dida.local"}));
      const res = await sendMails(targets.map(({inv,email})=>({ to: email||"demo@dida.local", toName: inv.patient, subject: fill(mahnSubject,inv), text: fill(mahnBody,inv) })));
      const nrs = new Set(targets.map(t=>t.inv.nr));
      setAllInvoices(prev=>prev.map(i=>nrs.has(i.nr)&&i.status!=="mahnung"?{...i,status:"mahnung"}:i));
      if (serverActive()) targets.forEach(({inv})=>{ if(inv.status!=="mahnung") invoiceStore.upsert({...inv,status:"mahnung",id:invId(inv)}).catch(()=>{}); });
      setMahnResult({ sent: res.sent ?? targets.length, skipped: mahnKandidaten.length - targets.length, simulated: res.simulated });
    } catch(e) { toast(`Mahnlauf fehlgeschlagen: ${e.message}`,"r"); }
    setMahnBusy(false);
  };

  // Look up dida_revenue for the selected period
  const revPeriod = (() => {
    if (!REVENUE_DATA) return null;
    if (period.startsWith("Q")) {
      const q=parseInt(period[1]), yr=parseInt(period.split(" ")[1]);
      const months=Object.entries(REVENUE_DATA).filter(([k])=>{ const[y,m]=k.split("-"); return parseInt(y)===yr&&Math.floor((parseInt(m)-1)/3)+1===q; }).map(([,v])=>v);
      if (!months.length) return null;
      return {gesamt:months.reduce((s,v)=>s+v.gesamt,0),einnahmen:months.reduce((s,v)=>s+v.einnahmen,0),offen:months.reduce((s,v)=>s+v.offen,0),kasse:months.reduce((s,v)=>s+v.kasse,0),privat:months.reduce((s,v)=>s+v.privat,0),igel:months.reduce((s,v)=>s+v.igel,0)};
    }
    const parts=period.split(" "); const mi=MONATE_DE.indexOf(parts[0]), yr=parseInt(parts[1]);
    if (mi>=0&&yr) { const k=`${yr}-${String(mi+1).padStart(2,"0")}`; return REVENUE_DATA[k]||null; }
    return null;
  })();

  // Invoice-computed fallbacks
  const invEinnahmen_c = Math.round(invPeriod.reduce((s,i)=>s+(i.status==="bezahlt"?i.betragNum:0),0));
  const invOffen_c     = Math.round(invPeriod.reduce((s,i)=>s+(i.status==="offen"||i.status==="mahnung"?i.betragNum:0),0));
  const invKasse_c     = Math.round(invPeriod.filter(i=>i.typ==="Kassenabrechnung").reduce((s,i)=>s+i.betragNum,0));
  const invPrivat_c    = Math.round(invPeriod.filter(i=>i.typ==="Privatliquidation").reduce((s,i)=>s+i.betragNum,0));
  const invIgel_c      = Math.round(invPeriod.filter(i=>i.typ==="IGeL").reduce((s,i)=>s+i.betragNum,0));
  const invGesamt_c    = invKasse_c + invPrivat_c + invIgel_c;

  // Final values — dida_revenue takes priority over invoice computation
  const invEinnahmen = revPeriod ? revPeriod.einnahmen : invEinnahmen_c;
  const invOffen     = revPeriod ? revPeriod.offen     : invOffen_c;
  const invKasse     = revPeriod ? revPeriod.kasse     : invKasse_c;
  const invPrivat    = revPeriod ? revPeriod.privat    : invPrivat_c;
  const invIgel      = revPeriod ? revPeriod.igel      : invIgel_c;
  const invGesamt    = revPeriod ? revPeriod.gesamt    : invGesamt_c;
  const invKassePct  = invGesamt ? Math.round(invKasse/invGesamt*100) : 0;
  const invPrivatPct = invGesamt ? Math.round(invPrivat/invGesamt*100) : 0;
  const invIgelPct   = invGesamt ? Math.round(invIgel/invGesamt*100) : 0;

  // 6-Monats-Chart — dida_revenue preferred
  const now6 = new Date();
  const CHART_MONTHS = Array.from({length:6},(_,i)=>{ const d=new Date(now6); d.setDate(1); d.setMonth(d.getMonth()-(5-i)); const key=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`; return {month:d.getMonth(),year:d.getFullYear(),label:MONATE_DE[d.getMonth()].slice(0,3),key}; }).map(m=>({
    ...m,
    rev: REVENUE_DATA&&REVENUE_DATA[m.key] ? REVENUE_DATA[m.key].gesamt :
      Math.round(ALL_INVOICES.filter(i=>{ const d=parseInvDate(i.datum); return d.getMonth()===m.month&&d.getFullYear()===m.year; }).reduce((s,i)=>s+i.betragNum,0))
  }));
  const MONTHLY_REV = CHART_MONTHS.map(m=>m.rev||0);
  const last3Avg = Math.round(MONTHLY_REV.slice(-3).reduce((a,b)=>a+b,0)/3) || 16600;
  const prognoseLow  = Math.round(last3Avg * 0.93);
  const prognoseHigh = Math.round(last3Avg * 1.11);
  const prognoseBase = Math.round(last3Avg * 1.04);
  // Jahresziel: einstellbar (Einstellung "jahresziel"), Demo-Default 840k
  const [jahresZiel, setJahresZielState] = useState(()=>getSetting("jahresziel")||(demoFallbacks()?840000:0));
  useEffect(()=>{ loadSettings().then(()=>{ const v=getSetting("jahresziel"); if(v) setJahresZielState(v); }).catch(()=>{}); },[]);
  const saveJahresZiel=(n)=>{ setJahresZielState(n); saveSetting("jahresziel", n).catch(()=>toast("Jahresziel nicht gespeichert — nur der Inhaber darf das.","r")); };
  const jahresIst  = REVENUE_DATA
    ? Object.entries(REVENUE_DATA).filter(([k])=>k.startsWith(String(now6.getFullYear()))).reduce((s,[,v])=>s+v.gesamt,0)
    : Math.round(ALL_INVOICES.filter(i=>parseInvDate(i.datum).getFullYear()===now6.getFullYear()).reduce((s,i)=>s+i.betragNum,0)) || MONTHLY_REV.reduce((a,b)=>a+b,0);
  const jahresPct  = jahresZiel>0 ? Math.min(99, Math.round((jahresIst/jahresZiel)*100)) : 0;
  const RING_R = 34, RING_CX = 44, RING_CY = 44;
  const RING_CIRC = 2*Math.PI*RING_R;

  const Spark = ({data, color="#0F6E56", w=80, h=28}) => {
    if(!data||data.length<2) return null;
    const mn=Math.min(...data), mx=Math.max(...data), range=mx-mn||1;
    const pts=data.map((v,i)=>`${(i/(data.length-1))*w},${h-(((v-mn)/range)*(h-6)+3)}`).join(" ");
    return <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{display:"block",flexShrink:0}}><polygon points={`${pts} ${w},${h} 0,${h}`} fill={color} fillOpacity={0.15}/><polyline points={pts} fill="none" stroke={color} strokeWidth={1.5}/></svg>;
  };

  // Perioden — dida_revenue keys preferred, fallback to invoice months
  const PERIODS = (() => {
    if (REVENUE_DATA) {
      const keys=Object.keys(REVENUE_DATA).sort().reverse();
      const periods=keys.slice(0,4).map(k=>{ const[yr,mo]=k.split("-"); return `${MONATE_DE[parseInt(mo)-1]} ${yr}`; });
      const[yr,mo]=keys[0].split("-");
      return [...periods, `Q${Math.floor((parseInt(mo)-1)/3)+1} ${yr}`];
    }
    const seen=new Set();
    ALL_INVOICES.forEach(i=>{ const d=parseInvDate(i.datum); seen.add(`${MONATE_DE[d.getMonth()]} ${d.getFullYear()}`); });
    const sorted=[...seen].sort((a,b)=>{ const[ma,ya]=[MONATE_DE.indexOf(a.split(" ")[0]),parseInt(a.split(" ")[1])]; const[mb,yb]=[MONATE_DE.indexOf(b.split(" ")[0]),parseInt(b.split(" ")[1])]; return ya!==yb?yb-ya:mb-ma; });
    if(sorted.length===0) return ["Juni 2026","Mai 2026","April 2026"];
    const yr=parseInt(sorted[0].split(" ")[1]);
    const qStr=`Q${Math.floor(MONATE_DE.indexOf(sorted[0].split(" ")[0])/3)+1} ${yr}`;
    return [...sorted.slice(0,4), qStr];
  })();

  return (
    <div style={{display:"flex",flexDirection:"column",gap:10,position:"relative"}}>
      {/* Role tabs */}
      <div style={{display:"flex",gap:0,borderBottom:"0.5px solid var(--mf-border)",background:"var(--mf-bg-alt)",margin:"-12px -14px 0",padding:"0 14px"}}>
        {umTabs.map(([k,av,label,bg,col])=>(
          <div key={k} onClick={()=>setUrole(k)} style={{fontSize:11,padding:"9px 14px",cursor:"pointer",color:urole===k?"#0F6E56":"var(--mf-muted)",fontWeight:urole===k?500:400,borderBottom:urole===k?"2px solid #0F6E56":"2px solid transparent",marginBottom:-0.5,display:"flex",alignItems:"center",gap:7}}>
            <div style={{width:18,height:18,borderRadius:"50%",background:bg,color:col,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:500}}>{av}</div>
            {label}
          </div>
        ))}
        <div style={{marginLeft:"auto",padding:"8px 0",display:"flex",alignItems:"center",gap:5}}>
          {PERIODS.map(p=>(
            <button key={p} onClick={()=>setPeriod(p)} style={{fontSize:10,padding:"2px 8px",borderRadius:20,border:"0.5px solid var(--mf-border2)",background:period===p?"#0F6E56":"transparent",color:period===p?"#E1F5EE":"var(--mf-muted)",cursor:"pointer",fontFamily:"inherit"}}>{p}</button>
          ))}
        </div>
      </div>

      {urole==="inhaber"&&<>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:7,marginTop:10}}>
          <div style={{background:"var(--mf-bg-alt)",borderRadius:"8px",padding:"10px 12px"}}>
            <div style={{fontSize:10,color:"var(--mf-muted)",marginBottom:4}}>Einnahmen {period}</div>
            <div style={{display:"flex",alignItems:"flex-end",justifyContent:"space-between",gap:6}}>
              <div>
                <div style={{fontSize:18,fontWeight:500,lineHeight:1}}>€{(invGesamt||0).toLocaleString("de-DE")}</div>
                <div style={{fontSize:10,marginTop:3,color:"var(--mf-primary)"}}>{invGesamt>0?"Aus Abrechnung · "+period:"Keine Daten für diesen Zeitraum"}</div>
              </div>
              <Spark data={MONTHLY_REV} color="#0F6E56" w={72} h={28}/>
            </div>
          </div>
          <Met v={`€${Math.round(invGesamt*0.34).toLocaleString("de-DE")}`} l={`Kosten ${period}`} d="≈34% Kostenquote" dc="dn"/>
          <Met v={`€${Math.round(invEinnahmen*0.66).toLocaleString("de-DE")}`} l="Rohertrag" d="66% Marge" dc="up"/>
          <Met v={`€${invOffen.toLocaleString("de-DE")}`} l="Offene Posten" d={`${invPeriod.filter(i=>i.status==="offen"||i.status==="mahnung").length} Rechnungen`} dc="dn"/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9}}>
          <Card>
            <CardHead title="Umsatzverteilung" icon="ti-chart-pie"/>
            <ProgRow label="Kasse (BEMA)" val={invKassePct||0} valStr={`€${invKasse.toLocaleString("de-DE")}`}/>
            <ProgRow label="Privat (GOZ)" val={invPrivatPct||0} valStr={`€${invPrivat.toLocaleString("de-DE")}`} color="#185FA5"/>
            <ProgRow label="IGeL" val={invIgelPct||0} valStr={`€${invIgel.toLocaleString("de-DE")}`} color="#BA7517"/>
            <Divider/>
            <div style={{display:"flex",gap:12,fontSize:10,color:"var(--mf-muted)"}}>
              <span>Bezahlt: <strong style={{color:"var(--mf-text)"}}>€{invEinnahmen.toLocaleString("de-DE")}</strong></span>
              <span>Offen: <strong style={{color:"var(--mf-neg)"}}>€{invOffen.toLocaleString("de-DE")}</strong></span>
              <span>Prognose: <strong style={{color:"var(--mf-primary)"}}>€{prognoseBase.toLocaleString("de-DE")}</strong></span>
            </div>
          </Card>
          <Card>
            <CardHead title="Umsatz letzte 6 Monate" icon="ti-chart-bar"/>
            <div style={{display:"flex",alignItems:"flex-end",gap:4,height:56}}>
              {CHART_MONTHS.map((m,i)=>{
                const mn2=Math.min(...MONTHLY_REV.filter(x=>x>0)), mx2=Math.max(...MONTHLY_REV)||1;
                const px=m.rev>0?Math.round(16+((m.rev-mn2)/(mx2-mn2||1))*30):4;
                const isLast=i===CHART_MONTHS.length-1;
                return (
                  <div key={m.label+m.year} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                    <div style={{width:"100%",height:`${px}px`,background:isLast?"#0F6E56":m.rev>0?"#B5D4F4":"#e7e5e4",borderRadius:"2px 2px 0 0"}}/>
                    <span style={{fontSize:8,color:isLast?"#0F6E56":"var(--mf-muted)",fontWeight:isLast?500:400}}>{m.label}</span>
                  </div>
                );
              })}
            </div>
            <div style={{fontSize:10,color:"var(--mf-muted)",marginTop:6}}>Ø letzte 3 Monate: <strong style={{color:"var(--mf-primary)"}}>€{last3Avg.toLocaleString("de-DE")}</strong></div>
          </Card>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:9}}>
          <Card>
            <CardHead title="Kostenverteilung (Modell)" icon="ti-arrows-right-left"/>
            {invGesamt>0 ? (<>
              <ProgRow label="Personal" val={52} valStr={`€${Math.round(invGesamt*0.52*0.34).toLocaleString("de-DE")}`}/>
              <ProgRow label="Miete" val={20} valStr={`€${Math.round(invGesamt*0.20*0.34).toLocaleString("de-DE")}`} color="#185FA5"/>
              <ProgRow label="Labor" val={18} valStr={`€${Math.round(invGesamt*0.18*0.34).toLocaleString("de-DE")}`} color="#993C1D"/>
              <ProgRow label="Material" val={10} valStr={`€${Math.round(invGesamt*0.10*0.34).toLocaleString("de-DE")}`} color="#BA7517"/>
              <div style={{fontSize:9.5,color:"var(--mf-muted)",marginTop:4}}>Branchen-Modellwerte (≈34 % Kostenquote) — echte Kosten folgen mit dem BWA-Import.</div>
            </>) : (
              <div style={{fontSize:11,color:"var(--mf-muted)",padding:"8px 0"}}>Erscheint, sobald Umsätze vorliegen (Abrechnung oder PVS-Import).</div>
            )}
          </Card>
          <Card>
            <CardHead title="Umsatz je Behandler" icon="ti-users"/>
            {behReal.list.length > 0 ? <>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr>{["Behandler","Umsatz","Anteil"].map(h=><th key={h} style={{textAlign:"left",color:"var(--mf-muted)",fontWeight:400,fontSize:10,padding:"3px 5px",borderBottom:"0.5px solid var(--mf-border)"}}>{h}</th>)}</tr></thead>
                <tbody>
                  {behReal.list.map(({n,v,pct})=>(
                    <tr key={n}>
                      <td style={{padding:"4px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:10}}>{n}</td>
                      <td style={{padding:"4px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:10,fontWeight:500}}>€{Math.round(v).toLocaleString("de-DE")}</td>
                      <td style={{padding:"4px 5px",borderBottom:"0.5px solid var(--mf-border)"}}><Sp color="g">{pct}%</Sp></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{fontSize:10,color:"var(--mf-muted)",marginTop:5}}>{behReal.exact ? `Aus PVS-Import · ${period}` : "Aus PVS-Import · Summe aller importierten Perioden"}</div>
            </> : demoLoaded ? <>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr>{["Behandler","Umsatz","Anteil"].map(h=><th key={h} style={{textAlign:"left",color:"var(--mf-muted)",fontWeight:400,fontSize:10,padding:"3px 5px",borderBottom:"0.5px solid var(--mf-border)"}}>{h}</th>)}</tr></thead>
                <tbody>
                  {[["Dr. Hoffmann",28],["Dr. Schulz",20],["Dr. Bergmann",25],["Dr. Fischer",15],["M. Roth (ZMP)",12]].map(([n,pct])=>{
                    const v=Math.round(invGesamt*pct/100);
                    return (<tr key={n}>
                      <td style={{padding:"4px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:10}}>{n}</td>
                      <td style={{padding:"4px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:10,fontWeight:500}}>€{v.toLocaleString("de-DE")}</td>
                      <td style={{padding:"4px 5px",borderBottom:"0.5px solid var(--mf-border)"}}><Sp color="g">{pct}%</Sp></td>
                    </tr>);
                  })}
                </tbody>
              </table>
              <div style={{fontSize:10,color:"var(--mf-muted)",marginTop:5}}>Beispieldaten — Beteiligung: Schulz/Bergmann 25% ab €8k, Fischer 25% ab €6k, Roth 25% ab €3k</div>
            </> : (
              <div style={{fontSize:11,color:"var(--mf-muted)",padding:"10px 0",lineHeight:1.5}}>
                Noch keine Umsätze je Behandler. Zwei Wege im <strong>Import</strong>-Reiter:<br/>
                · Umsatz-CSV mit <strong>Behandler-Spalte</strong> hochladen (wird automatisch erkannt), oder<br/>
                · pro Behandler eine Datei importieren und beim Import <strong>„Diese Datei gehört zu“</strong> setzen.<br/>
                Beispielzahlen ansehen: App-Einstellungen → <strong>„Demo-Daten laden“</strong>.
              </div>
            )}
          </Card>
          <Card>
            <CardHead title="Offene Posten" icon="ti-alert-triangle"/>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              {(invPeriod.filter(i=>i.status==="offen"||i.status==="mahnung").slice(0,4)).map(i=>(
                <div key={i.nr} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"5px 8px",background:i.status==="mahnung"?"#FCEBEB":"#FFF7ED",borderRadius:5,fontSize:11}}>
                  <span style={{color:"var(--mf-text)",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",paddingRight:8}}>{i.patient} · {i.typ.split("l")[0]}</span>
                  <span style={{fontWeight:500,color:"var(--mf-neg)",flexShrink:0}}>{i.betrag}</span>
                </div>
              ))}
              {invPeriod.filter(i=>i.status==="offen"||i.status==="mahnung").length===0&&<div style={{fontSize:10,color:"var(--mf-muted)",padding:"5px 0"}}>Keine offenen Posten in diesem Zeitraum.</div>}
            </div>
            <Divider/>
            <div style={{fontSize:10,color:"var(--mf-muted)",marginBottom:6}}>Gesamt: <strong style={{color:"var(--mf-neg)"}}>€{invOffen.toLocaleString("de-DE")}</strong> · <Sp color="a">{invPeriod.filter(i=>i.status==="mahnung").length} Mahnungen</Sp></div>
            {mahnResult
              ? <div style={{padding:"7px 9px",background:"#E1F5EE",borderRadius:6,fontSize:10,color:"#085041"}}>✓ {mahnResult.simulated?"Simuliert":"Gesendet"}: {mahnResult.sent} Erinnerung{mahnResult.sent===1?"":"en"}{mahnResult.skipped?` · ${mahnResult.skipped} ohne E-Mail übersprungen`:""}</div>
              : <Btn color="r" disabled={!mahnKandidaten.length} onClick={()=>{setMahnResult(null);setMahnOpen(true);}} style={{fontSize:10,width:"100%"}}>Mahnlauf starten{mahnKandidaten.length?` (${mahnKandidaten.length})`:""}</Btn>}
          </Card>
          <Card>
            <CardHead title="Jahresziel" icon="ti-target"/>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <svg width={88} height={88} viewBox="0 0 88 88" style={{flexShrink:0}}>
                <circle cx={RING_CX} cy={RING_CY} r={RING_R} fill="none" stroke="var(--mf-bg-alt)" strokeWidth={11}/>
                <circle cx={RING_CX} cy={RING_CY} r={RING_R} fill="none" stroke="#0F6E56" strokeWidth={11}
                  strokeDasharray={`${(jahresPct/100)*RING_CIRC} ${RING_CIRC}`}
                  strokeDashoffset={RING_CIRC*0.25} strokeLinecap="round" transform="rotate(-90 44 44)"/>
                <text x={RING_CX} y={RING_CY-4} textAnchor="middle" fontSize={14} fontWeight={600} fill="var(--mf-text)" fontFamily="system-ui">{jahresPct}%</text>
                <text x={RING_CX} y={RING_CY+9} textAnchor="middle" fontSize={8} fill="var(--mf-muted)" fontFamily="system-ui">erreicht</text>
              </svg>
              <div>
                <div style={{fontSize:10,color:"var(--mf-muted)",marginBottom:3}}>Ist</div>
                <div style={{fontSize:12,fontWeight:500}}>€{(jahresIst/1000).toFixed(0)}k</div>
                <div style={{fontSize:10,color:"var(--mf-muted)",marginTop:4,marginBottom:3}}>Ziel (€/Jahr)</div>
                <input type="number" step={10000} min={0} defaultValue={jahresZiel||""} placeholder="z.B. 840000" key={jahresZiel}
                  onBlur={e=>{ const n=parseInt(e.target.value)||0; if(n!==jahresZiel) saveJahresZiel(n); }}
                  onKeyDown={e=>{ if(e.key==="Enter") e.target.blur(); }}
                  style={{width:90,fontSize:12,fontWeight:500,padding:"3px 6px",borderRadius:8,border:"1px solid var(--mf-border2)",background:"var(--mf-bg)",color:"var(--mf-text)",fontFamily:"inherit",boxSizing:"border-box"}}/>
                {jahresZiel>0
                  ? <div style={{fontSize:10,color:"var(--mf-primary)",marginTop:4}}>noch €{(Math.max(0,jahresZiel-jahresIst)/1000).toFixed(0)}k</div>
                  : <div style={{fontSize:10,color:"var(--mf-muted)",marginTop:4}}>Ziel eintragen — Enter speichert</div>}
              </div>
            </div>
          </Card>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9}}>
          <Card>
            <CardHead title={`Umsatzprognose ${MONATE_DE[(now6.getMonth()+1)%12]} ${now6.getMonth()===11?now6.getFullYear()+1:now6.getFullYear()}`} icon="ti-chart-line"/>
            <div style={{fontSize:18,fontWeight:500,marginBottom:2}}>ca. € {prognoseBase.toLocaleString("de-DE")}</div>
            <div style={{fontSize:10,color:"var(--mf-muted)",marginBottom:8}}>Konfidenzbereich: € {prognoseLow.toLocaleString("de-DE")} – € {prognoseHigh.toLocaleString("de-DE")}</div>
            <div style={{display:"flex",flexDirection:"column",gap:4,fontSize:10,color:"var(--mf-muted)"}}>
              <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:"0.5px solid var(--mf-border)"}}><span>Basis (Ø letzte 3 Monate)</span><strong style={{color:"var(--mf-text)"}}>€ {last3Avg.toLocaleString("de-DE")}</strong></div>
              <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:"0.5px solid var(--mf-border)"}}><span>Wachstumstrend</span><Sp color="g">+4%</Sp></div>
              <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0"}}><span>Saisonaler Faktor</span><Sp color="b">neutral</Sp></div>
            </div>
            <div style={{marginTop:8,padding:"7px 9px",background:"#E1F5EE",borderRadius:6,fontSize:10,color:"#085041"}}>Trend: 3 Monate in Folge über Vormonat</div>
          </Card>
        </div>
        <div style={{display:"flex",alignItems:"flex-start",gap:7,padding:"8px 10px",borderRadius:6,background:"#FAEEDA",border:"0.5px solid #FAC775",fontSize:11,color:"#633806"}}>
          <i className="ti ti-bulb" style={{fontSize:14,flexShrink:0,marginTop:1}}/>
          <div><strong>Optimierungshinweis:</strong> IGeL-Anteil bei 12% — Branchendurchschnitt 18–22%. Potenzial: ca. <strong>+€1.100/Monat</strong> durch gezielte Patientenansprache.</div>
        </div>
      </>}

      {urole==="arzt"&&<>
        <div style={{padding:"7px 10px",background:"#E1F5EE",border:"0.5px solid #9FE1CB",borderRadius:6,fontSize:11,color:"#085041",display:"flex",alignItems:"center",gap:7,marginTop:10}}>
          <i className="ti ti-info-circle" style={{fontSize:14}}/>
          <span>Nur Ihre eigenen Leistungen sichtbar. Praxiskosten nicht einsehbar.</span>
        </div>
        {(()=>{const a1=Math.round(invGesamt*0.22);const a1bet=a1>5000?Math.round(a1*0.20):0;const a1ytd=Math.round(a1bet*(now6.getMonth()+1)*0.9);return(
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:7}}>
          <Met v={`€${a1.toLocaleString("de-DE")}`} l={`Mein Umsatz ${MONATE_DE[now6.getMonth()]}`} d="↑ 8% vs. Vormonat" dc="up"/>
          <Met v="20%" l="Beteiligungsquote" d="vertraglich"/>
          <Met v={`€${a1bet.toLocaleString("de-DE")}`} l="Meine Beteiligung" d={`${MONATE_DE[now6.getMonth()]} ${now6.getFullYear()}`} dc="up"/>
          <Met v={`€${a1ytd.toLocaleString("de-DE")}`} l="Beteiligung YTD" d={`Jan–${MONATE_DE[now6.getMonth()].slice(0,3)} ${now6.getFullYear()}`} dc="up"/>
        </div>);})()}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9}}>
          <Card><CardHead title="Meine Leistungen" icon="ti-chart-pie"/>
            <ProgRow label="Kasse (BEMA)" val={71} valStr="€3.990" color="#185FA5"/>
            <ProgRow label="Privat (GOZ)" val={24} valStr="€1.350"/>
            <ProgRow label="IGeL" val={5} valStr="€280" color="#BA7517"/>
          </Card>
          <Card><CardHead title="Beteiligung je Monat" icon="ti-chart-bar"/>
            <div style={{display:"flex",alignItems:"flex-end",gap:4,height:52}}>
              {[["Jan",60],["Feb",68],["Mrz",65],["Apr",74],["Mai",80],["Jun",100]].map(([m,h])=>(
                <div key={m} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                  <div style={{width:"100%",height:`${h}%`,background:m==="Jun"?"#185FA5":"#B5D4F4",borderRadius:"2px 2px 0 0"}}/>
                  <span style={{fontSize:8,color:m==="Jun"?"#185FA5":"var(--mf-muted)",fontWeight:m==="Jun"?500:400}}>{m}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
        <Card><CardHead title="Top-Leistungen nach Umsatz" icon="ti-list"/>
          <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed"}}>
            <thead><tr>{["Leistung","Anzahl","Umsatz","Beteiligung"].map(h=><th key={h} style={{textAlign:"left",color:"var(--mf-muted)",fontWeight:400,fontSize:10,padding:"3px 5px",borderBottom:"0.5px solid var(--mf-border)"}}>{h}</th>)}</tr></thead>
            <tbody>
              {[["Kontrolle & Beratung","42","€1.260","€252"],["Füllung Komposit","28","€1.680","€336"],["Extraktion","12","€840","€168"],["Überkronung","6","€1.200","€240"]].map(([l,a,u,b])=>(
                <tr key={l}>{[l,a,u].map((v,i)=><td key={i} style={{padding:"5px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,fontWeight:i===2?500:400}}>{v}</td>)}
                <td style={{padding:"5px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,fontWeight:500,color:"#185FA5"}}>{b}</td></tr>
              ))}
            </tbody>
          </table>
        </Card>
      </>}

      {urole==="arzt2"&&(()=>{
        const a2=Math.round(invGesamt*0.189);
        const a2bet=a2>8000?Math.round(a2*0.25):0;
        return <>
        <div style={{padding:"7px 10px",background:"#EEEDFE",border:"0.5px solid #C4B5FD",borderRadius:6,fontSize:11,color:"#3C3489",display:"flex",alignItems:"center",gap:7,marginTop:10}}>
          <i className="ti ti-info-circle" style={{fontSize:14}}/>
          <span>Persönliche Umsatzübersicht Dr. Bergmann — Beteiligungsquote: <strong>25% ab €8.000/Mo</strong></span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:7}}>
          <Met v={`€${a2.toLocaleString("de-DE")}`} l={`Mein Umsatz ${MONATE_DE[now6.getMonth()]}`} d="↑ 11% vs. Vormonat" dc="up"/>
          <Met v="25%" l="Beteiligungsquote" d="ab €8.000 vertraglich"/>
          <Met v={`€${a2bet.toLocaleString("de-DE")}`} l="Meine Beteiligung" d={`${MONATE_DE[now6.getMonth()]} ${now6.getFullYear()}`} dc={a2bet>0?"up":"dn"}/>
          <Met v="42" l={`KFO-Sitzungen ${MONATE_DE[now6.getMonth()]}`} d="↑ 4" dc="up"/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9}}>
          <Card><CardHead title="Meine Leistungen" icon="ti-adjustments"/>
            <ProgRow label="Brackets (Kasse)" val={55} valStr={`€${Math.round(a2*0.55).toLocaleString("de-DE")}`} color="#3C3489"/>
            <ProgRow label="Aligner (Privat)" val={32} valStr={`€${Math.round(a2*0.32).toLocaleString("de-DE")}`} color="#7B61E4"/>
            <ProgRow label="Retainer" val={13} valStr={`€${Math.round(a2*0.13).toLocaleString("de-DE")}`} color="#B5AFF0"/>
          </Card>
          <Card><CardHead title="KFO-Übersicht" icon="ti-list"/>
            <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed"}}>
              <thead><tr>{["Patient","Datum","Leistung","Betrag"].map(h=><th key={h} style={{textAlign:"left",color:"var(--mf-muted)",fontWeight:400,fontSize:10,padding:"3px 5px",borderBottom:"0.5px solid var(--mf-border)"}}>{h}</th>)}</tr></thead>
              <tbody>
                {[["Schreier, T.","09.06.","Bracket-Set","€1.240"],["Graf, M.","08.06.","Aligner-Phase","€860"],["Bauer, L.","07.06.","Retainer","€420"],["Wolf, K.","06.06.","KFO-Kontrolle","€68"]].map(([n,d,l,q])=>(
                  <tr key={n}>{[n,d,l].map((v,i)=><td key={i} style={{padding:"5px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>{v}</td>)}
                  <td style={{padding:"5px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,fontWeight:500,color:"#3C3489"}}>{q}</td></tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
        </>;})()}

      {urole==="arzt3"&&(()=>{
        const a3=Math.round(invGesamt*0.130);
        const a3bet=a3>6000?Math.round(a3*0.25):0;
        return <>
        <div style={{padding:"7px 10px",background:"#FFF0E6",border:"0.5px solid #FDBA74",borderRadius:6,fontSize:11,color:"#7A3800",display:"flex",alignItems:"center",gap:7,marginTop:10}}>
          <i className="ti ti-info-circle" style={{fontSize:14}}/>
          <span>Persönliche Umsatzübersicht Dr. Fischer — Beteiligungsquote: <strong>25% ab €6.000/Mo</strong></span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:7}}>
          <Met v={`€${a3.toLocaleString("de-DE")}`} l={`Mein Umsatz ${MONATE_DE[now6.getMonth()]}`} d="↑ 8% vs. Vormonat" dc="up"/>
          <Met v="25%" l="Beteiligungsquote" d="ab €6.000 vertraglich"/>
          <Met v={`€${a3bet.toLocaleString("de-DE")}`} l="Meine Beteiligung" d={`${MONATE_DE[now6.getMonth()]} ${now6.getFullYear()}`} dc={a3bet>0?"up":"dn"}/>
          <Met v="56" l={`Sitzungen ${MONATE_DE[now6.getMonth()]}`} d="↑ 3" dc="up"/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9}}>
          <Card><CardHead title="Meine Leistungen" icon="ti-chart-pie"/>
            <ProgRow label="Kontrolle (Kasse)" val={60} valStr={`€${Math.round(a3*0.60).toLocaleString("de-DE")}`} color="#7A3800"/>
            <ProgRow label="Füllungen" val={28} valStr={`€${Math.round(a3*0.28).toLocaleString("de-DE")}`} color="#BA7517"/>
            <ProgRow label="Extraktion" val={12} valStr={`€${Math.round(a3*0.12).toLocaleString("de-DE")}`} color="#FAC775"/>
          </Card>
          <Card><CardHead title="Sitzungs-Übersicht" icon="ti-list"/>
            <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed"}}>
              <thead><tr>{["Patient","Datum","Leistung","Betrag"].map(h=><th key={h} style={{textAlign:"left",color:"var(--mf-muted)",fontWeight:400,fontSize:10,padding:"3px 5px",borderBottom:"0.5px solid var(--mf-border)"}}>{h}</th>)}</tr></thead>
              <tbody>
                {[["Braun, M.","09.06.","Kontrolle","€42"],["Koch, S.","08.06.","Füllung 36","€88"],["Schmidt, H.","07.06.","Extraktion 48","€124"],["Engel, P.","06.06.","Kontrolle","€42"]].map(([n,d,l,q])=>(
                  <tr key={n}>{[n,d,l].map((v,i)=><td key={i} style={{padding:"5px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>{v}</td>)}
                  <td style={{padding:"5px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,fontWeight:500,color:"#7A3800"}}>{q}</td></tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
        </>;})()}

      {urole==="dh"&&<>
        <div style={{padding:"7px 10px",background:"#F0FFF4",border:"0.5px solid #6EE7B7",borderRadius:6,fontSize:11,color:"#166534",display:"flex",alignItems:"center",gap:7,marginTop:10}}>
          <i className="ti ti-info-circle" style={{fontSize:14}}/>
          <span>Persönliche Umsatzübersicht Maja Roth — Beteiligungsquote: <strong>25% ab €3.000/Mo</strong></span>
        </div>
        {(()=>{const dhUm=Math.round(invGesamt*0.12);const dhBet=dhUm>3000?Math.round(dhUm*0.25):0;return(
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:7}}>
          <Met v={`€${dhUm.toLocaleString("de-DE")}`} l={`Mein Umsatz ${MONATE_DE[now6.getMonth()]}`} d="↑ 15% vs. Vormonat" dc="up"/>
          <Met v="25%" l="Beteiligungsquote" d="ab €3.000 vertraglich"/>
          <Met v={`€${dhBet.toLocaleString("de-DE")}`} l="Meine Beteiligung" d={`${MONATE_DE[now6.getMonth()]} ${now6.getFullYear()}`} dc="up"/>
          <Met v="38" l="PZR/Paro-Sitzungen" d="↑ 6" dc="up"/>
        </div>);})()}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9}}>
          <Card><CardHead title="Meine Leistungen" icon="ti-tooth"/>
            <ProgRow label="PZR" val={68} valStr="€4.920" color="#166534"/>
            <ProgRow label="Paro (UPT)" val={20} valStr="€1.460" color="#0F6E56"/>
            <ProgRow label="Bleaching" val={8} valStr="€580" color="#6EE7B7"/>
            <ProgRow label="Fluoridierung" val={4} valStr="€280" color="#BBF7D0"/>
          </Card>
          <Card><CardHead title="PZR & Paro-Übersicht" icon="ti-list"/>
            <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed"}}>
              <thead><tr>{["Patient","Datum","Leistung","Quote"].map(h=><th key={h} style={{textAlign:"left",color:"var(--mf-muted)",fontWeight:400,fontSize:10,padding:"3px 5px",borderBottom:"0.5px solid var(--mf-border)"}}>{h}</th>)}</tr></thead>
              <tbody>
                {[["Maier, M.","05.06.","PZR","€22"],["Weber, K.","06.06.","UPT III","€68"],["Fischer, A.","06.06.","Bleaching","€80"],["Richter, E.","09.06.","PZR+F","€26"]].map(([n,d,l,q])=>(
                  <tr key={n}>{[n,d,l].map((v,i)=><td key={i} style={{padding:"5px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>{v}</td>)}
                  <td style={{padding:"5px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,fontWeight:500,color:"#166534"}}>{q}</td></tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      </>}

      {/* Persönliche Ansicht je konfiguriertem Behandler — echt gerechnet aus
          importierten Behandler-Umsätzen + Beteiligungskonditionen (Settings) */}
      {urole.startsWith("b:")&&(()=>{
        const b = getBehandler().find(x=>`b:${x.id}`===urole);
        if(!b) return null;
        const mine = behReal.rows.filter(r=>r.name.toLowerCase()===b.name.toLowerCase());
        // je Monat aufsummieren, chronologisch sortieren
        const byMonat = {};
        mine.forEach(r=>{ byMonat[r.monat]=(byMonat[r.monat]||0)+r.umsatz; });
        const monate = Object.entries(byMonat).map(([monat,ums])=>({monat,ums,idx:monatIdx(monat)})).sort((a,x)=>a.idx-x.idx);
        const beteiligung = (ums)=> b.betQuote==null ? null : (b.betAb!=null&&ums<b.betAb) ? 0 : Math.round(ums*b.betQuote/100);
        const periodRow = monate.find(m=>m.monat.toLowerCase()===String(period).trim().toLowerCase());
        const gesamt = monate.reduce((s,m)=>s+m.ums,0);
        if(!mine.length) return (
          <div style={{marginTop:10}}><Insight tone="a"><strong>Für {b.name} sind noch keine Umsätze importiert.</strong> Import-Reiter → Umsatzstatistik mit Behandler-Spalte hochladen oder beim Import „Diese Datei gehört zu: {b.name}“ wählen.</Insight></div>
        );
        return (<>
          {b.betQuote==null&&<div style={{marginTop:10}}><Insight tone="a">Keine Beteiligungskonditionen hinterlegt — Quote und Schwelle im Benutzermenü unter <strong>Praxis &amp; Behandler</strong> eintragen, dann rechnet dida die Beteiligung automatisch.</Insight></div>}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:7,marginTop:10}}>
            <Met v={periodRow?`€${Math.round(periodRow.ums).toLocaleString("de-DE")}`:"—"} l={`Umsatz ${period}`} d={periodRow?"aus PVS-Import":"keine Daten für diesen Zeitraum"}/>
            <Met v={b.betQuote!=null?`${b.betQuote}%`:"—"} l="Beteiligungsquote" d={b.betQuote!=null?(b.betAb!=null?`ab €${b.betAb.toLocaleString("de-DE")}/Monat`:"ab dem ersten Euro"):"nicht hinterlegt"}/>
            <Met v={periodRow&&b.betQuote!=null?`€${beteiligung(periodRow.ums).toLocaleString("de-DE")}`:"—"} l={`Beteiligung ${period}`} d={periodRow&&b.betQuote!=null&&beteiligung(periodRow.ums)===0?"Schwelle nicht erreicht":""} dc={periodRow&&b.betQuote!=null&&beteiligung(periodRow.ums)>0?"up":undefined}/>
            <Met v={`€${Math.round(gesamt).toLocaleString("de-DE")}`} l="Umsatz gesamt" d={`${monate.length} importierte Monate`}/>
          </div>
          <Card>
            <CardHead title={`Monatsübersicht — ${b.name}`} icon="ti-chart-bar"/>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr>{["Monat","Umsatz",b.betQuote!=null?"Beteiligung":""].filter(Boolean).map(h=><th key={h} style={{textAlign:"left",color:"var(--mf-muted)",fontWeight:400,fontSize:10,padding:"3px 5px",borderBottom:"0.5px solid var(--mf-border)"}}>{h}</th>)}</tr></thead>
              <tbody>
                {monate.map(m=>(
                  <tr key={m.monat}>
                    <td style={{padding:"4px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>{m.monat}</td>
                    <td style={{padding:"4px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,fontWeight:500}}>€{Math.round(m.ums).toLocaleString("de-DE")}</td>
                    {b.betQuote!=null&&<td style={{padding:"4px 5px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,fontWeight:500,color:beteiligung(m.ums)>0?"var(--mf-primary)":"var(--mf-muted)"}}>€{beteiligung(m.ums).toLocaleString("de-DE")}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {b.betQuote!=null&&b.betAb!=null&&<div style={{fontSize:10,color:"var(--mf-muted)",marginTop:5}}>Beteiligung: {b.betQuote}% des Monatsumsatzes, sofern mindestens €{b.betAb.toLocaleString("de-DE")} erreicht sind.</div>}
          </Card>
        </>);
      })()}

      {mahnOpen&&<>
        <div onClick={()=>!mahnBusy&&setMahnOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay}}/>
        <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:560,maxHeight:"88vh",maxWidth:"96vw",background:"var(--mf-bg)",borderRadius:14,zIndex:Z.modal,display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 64px rgba(0,0,0,.3)"}}>
          <div style={{padding:"14px 18px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
            <div style={{width:36,height:36,borderRadius:8,background:"#FCEBEB",display:"flex",alignItems:"center",justifyContent:"center"}}><i className="ti ti-mail-forward" style={{fontSize:18,color:"var(--mf-neg)"}}/></div>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:600}}>Mahnlauf — Zahlungserinnerung</div>
              <div style={{fontSize:11,color:"var(--mf-muted)"}}>{mahnKandidaten.length} offene Rechnung{mahnKandidaten.length===1?"":"en"} · {mahnSendbar.length} mit E-Mail</div>
            </div>
            <div onClick={()=>!mahnBusy&&setMahnOpen(false)} style={{cursor:"pointer",fontSize:20,color:"var(--mf-muted)",width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",background:"var(--mf-bg-alt)"}}>×</div>
          </div>
          <div style={{padding:"14px 18px",overflowY:"auto",display:"flex",flexDirection:"column",gap:10}}>
            {mahnResult ? (
              <div style={{textAlign:"center",padding:"18px 0"}}>
                <div style={{fontSize:30,marginBottom:8}}>✓</div>
                <div style={{fontSize:13,fontWeight:600,marginBottom:4}}>{mahnResult.simulated?"Mahnlauf simuliert":"Mahnlauf ausgeführt"}</div>
                <div style={{fontSize:11,color:"var(--mf-muted)",lineHeight:1.5}}>{mahnResult.sent} Erinnerung{mahnResult.sent===1?"":"en"} {mahnResult.simulated?"vorbereitet":"versendet"}{mahnResult.skipped?`, ${mahnResult.skipped} ohne E-Mail übersprungen`:""}. Betroffene Rechnungen stehen jetzt auf „Mahnung".</div>
                <Btn color="g" onClick={()=>setMahnOpen(false)} style={{marginTop:14}}>Schließen</Btn>
              </div>
            ) : (<>
              {!mahnKandidaten.length && <div style={{fontSize:11,color:"var(--mf-muted)"}}>Keine offenen Rechnungen im Zeitraum {period}.</div>}
              {mahnKandidaten.length>0 && <>
                <div>
                  <div style={{fontSize:10.5,fontWeight:600,color:"var(--mf-muted)",marginBottom:6}}>Empfänger ({mahnSendbar.length} von {mahnKandidaten.length})</div>
                  <div style={{display:"flex",flexDirection:"column",gap:4,maxHeight:140,overflowY:"auto"}}>
                    {mahnMitMail.map(({inv,email})=>(
                      <div key={inv.nr} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 9px",background:"var(--mf-bg-alt)",borderRadius:6,fontSize:10.5}}>
                        <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{inv.patient} · {inv.nr}</span>
                        <span style={{fontWeight:500,color:"var(--mf-neg)"}}>{inv.betrag}</span>
                        {email?<Sp color="g">Mail</Sp>:<Sp color="a">keine Mail</Sp>}
                      </div>
                    ))}
                  </div>
                </div>
                <Input label="Betreff" value={mahnSubject} onChange={setMahnSubject}/>
                <Textarea label="Nachricht" value={mahnBody} onChange={setMahnBody} rows={9}/>
                <div style={{fontSize:9.5,color:"var(--mf-muted)",lineHeight:1.5}}>Platzhalter: <code>{"{name}"}</code> Vorname · <code>{"{nr}"}</code> Rechnungsnr. · <code>{"{betrag}"}</code> Betrag. Rechnungen ohne hinterlegte E-Mail werden übersprungen.</div>
                <div style={{display:"flex",gap:8}}>
                  <Btn color="r" lg style={{flex:1}} disabled={mahnBusy} onClick={runMahnlauf}>{mahnBusy?"Sende…":`${serverActive()?mahnSendbar.length:mahnKandidaten.length} Erinnerung${(serverActive()?mahnSendbar.length:mahnKandidaten.length)===1?"":"en"} senden`}</Btn>
                  <Btn lg disabled={mahnBusy} onClick={()=>setMahnOpen(false)}>Abbrechen</Btn>
                </div>
              </>}
            </>)}
          </div>
        </div>
      </>}
    </div>
  );
}

// ─── BEWERTUNGEN SCREEN ───────────────────────────────────────────────────────

function NewsletterScreen({ mailPreset, onPresetConsumed }) {
  const [topic, setTopic] = useState("");
  const [segment, setSegment] = useState("Alle Patienten (1.247)");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [generating, setGenerating] = useState(false);
  const [tab, setTab] = useState("neu");
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [sendDone, setSendDone] = useState(false);
  const [vorschauOpen, setVorschauOpen] = useState(false);
  const [entwurfSaved, setEntwurfSaved] = useState(false);

  // Einzelmail state
  const [mailRecipient, setMailRecipient] = useState("");
  const [mailSubject, setMailSubject] = useState("");
  const [mailBody, setMailBody] = useState("");
  const [mailTopic, setMailTopic] = useState("");
  const [mailGenerating, setMailGenerating] = useState(false);
  const [mailPatQuery, setMailPatQuery] = useState("");
  const [sentMails, setSentMails] = useState(()=>{
    try{const s=localStorage.getItem("dida_sent_mails");if(s)return JSON.parse(s);}catch{}
    return [];
  });
  useEffect(()=>{try{localStorage.setItem("dida_sent_mails",JSON.stringify(sentMails));}catch{}},[sentMails]);

  // Versand-Statistik aus Mailjet (Öffnungen, Klicks, Rückläufer).
  // Fehler werden angezeigt statt verschluckt — eine nicht deployte Function
  // soll auffallen und nicht als "0 %" durchgehen.
  const [mailStats, setMailStats] = useState(null);
  const [mailStatsErr, setMailStatsErr] = useState("");
  const [statsTage, setStatsTage] = useState(30);
  useEffect(()=>{
    let on = true;
    setMailStatsErr(""); setMailStats(null);
    loadMailStats(statsTage)
      .then(d=>{ if(on) setMailStats(d); })
      .catch(e=>{ if(on) setMailStatsErr(e.message); });
    return ()=>{ on = false; };
  },[statsTage]);
  const oeffnungsrate = mailStats?.ok ? mailQuote(mailStats.summe.geoeffnet, mailStats.summe.gesendet) : null;

  useEffect(()=>{
    if(!mailPreset) return;
    setTab("einzelmail");
    setMailRecipient(mailPreset.patient||"");
    setMailPatQuery(mailPreset.patient||"");
    if(onPresetConsumed) onPresetConsumed();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[mailPreset]);

  const patSuggestsMail = useMemo(() => mailPatQuery.length>=1 ? getAllPatientNames().filter(n=>n.toLowerCase().includes(mailPatQuery.toLowerCase())).slice(0,5) : [], [mailPatQuery]);

  const MAIL_TEMPLATES = {
    "terminerinnerung": (name) => ({
      subject:`Terminerinnerung — Ihr Termin steht an`,
      body:`Liebe/r ${name},\n\nwir möchten Sie an Ihren bevorstehenden Termin in unserer Praxis erinnern.\n\nSollten Sie den Termin nicht wahrnehmen können, bitten wir Sie, uns rechtzeitig zu informieren, damit wir den Termin anderweitig vergeben können.\n\nBei Fragen stehen wir Ihnen gerne zur Verfügung.\n\nMit freundlichen Grüßen\n${praxisSig()}`
    }),
    "recall pzr": (name) => ({
      subject:`Erinnerung: Ihre PZR-Behandlung steht an`,
      body:`Liebe/r ${name},\n\nes ist wieder Zeit für Ihre professionelle Zahnreinigung (PZR)!\n\nRegelmäßige Prophylaxe-Termine sind wichtig für Ihre Zahngesundheit. Vereinbaren Sie jetzt bequem online Ihren nächsten Termin.\n\nWir freuen uns auf Ihren Besuch!\n\nMit freundlichen Grüßen\nIhr Praxisteam`
    }),
    "vorsorge": (name) => ({
      subject:`Vorsorge-Erinnerung für ${name}`,
      body:`Liebe/r ${name},\n\nIhre regelmäßige Vorsorgeuntersuchung ist fällig. Frühzeitige Erkennung schützt Ihre Gesundheit.\n\nBitte vereinbaren Sie zeitnah einen Termin. Wir sind für Sie da!\n\nMit freundlichen Grüßen\n${praxisSig()}`
    }),
  };

  const generateMailText = () => {
    if(!mailTopic) return;
    const name = mailRecipient.split(",")[1]?.trim() || mailRecipient.split(",")[0] || "Patient/in";
    const key = Object.keys(MAIL_TEMPLATES).find(k=>mailTopic.toLowerCase().includes(k));
    setMailGenerating(true);
    setTimeout(()=>{
      const tpl = key ? MAIL_TEMPLATES[key](name) : composeMailText(mailTopic, `Liebe/r ${name}`);
      setMailSubject(tpl.subject);
      setMailBody(tpl.body);
      setMailGenerating(false);
    }, 1000);
  };

  const sendMail = async () => {
    if(!mailRecipient||!mailSubject) return;
    const email = findPatientEmail(mailRecipient);
    if (serverActive() && !email) { toast(`Für ${mailRecipient} ist keine E-Mail hinterlegt — in der Patientenliste ergänzen.`, "r"); return; }
    try {
      const res = await sendMails([{ to: email || "demo@dida.local", toName: mailRecipient, subject: mailSubject, text: mailBody }]);
      toast(res.simulated ? "Demo — Versand simuliert" : "E-Mail gesendet ✓");
      setSentMails(prev=>[{id:Date.now(),recipient:mailRecipient,subject:mailSubject,datum:new Date().toLocaleDateString("de-DE",{day:"2-digit",month:"2-digit",year:"2-digit"}),time:new Date().toLocaleTimeString("de-DE",{hour:"2-digit",minute:"2-digit"})},...prev]);
      setMailRecipient(""); setMailPatQuery(""); setMailSubject(""); setMailBody(""); setMailTopic("");
    } catch(e) { toast(`Versand fehlgeschlagen: ${e.message}`, "r"); }
  };

  const nlDemo = (()=>{ try { return !!localStorage.getItem("dida_demo_loaded"); } catch { return false; } })();
  const [realCampaigns, setRealCampaigns] = useState(()=>{ try{const s=localStorage.getItem("dida_campaigns");if(s)return JSON.parse(s);}catch{} return []; });
  const CAMPAIGNS = !nlDemo ? [] : [
    { name:"PZR-Recall Frühjahr 2026", date:"03.06.26", segment:"Prophylaxe aktiv", sent:848, opened:"71%", status:"g" },
    { name:"Sommerpause-Hinweis 2026", date:"15.05.26", segment:"Alle Patienten", sent:1247, opened:"58%", status:"g" },
    { name:"KFO-Elternabend Einladung", date:"01.04.26", segment:"KFO-Patienten Ü12", sent:312, opened:"64%", status:"g" },
    { name:"Neue Online-Terminbuchung", date:"10.03.26", segment:"Alle Patienten", sent:1247, opened:"62%", status:"g" },
  ];

  const TOPIC_TEMPLATES = {
    "Prophylaxe":{ subject:"Ihr nächster Prophylaxe-Termin — Jetzt Recall buchen", body:`Liebe Patientinnen und Patienten,\n\neine regelmäßige professionelle Zahnreinigung (PZR) ist der beste Schutz gegen Karies und Parodontitis.\n\nEs ist wieder Zeit für Ihren Recall-Termin! Vereinbaren Sie jetzt einfach online Ihre nächste PZR.\n\nAls Teil unseres Prophylaxeprogramms profitieren Sie von:\n– Frühzeitiger Karies- und Parodontitis-Erkennung\n– Professioneller Reinigung aller Zahnflächen\n– Persönlicher Mundhygieneberatung durch unser Prophylaxe-Team\n\nWir freuen uns auf Ihren Besuch!\n\nMit freundlichen Grüßen\n${praxisSig()}` },
    "PZR-Recall":{ subject:"Ihr nächster Prophylaxe-Termin wartet!", body:`Liebe Patientinnen und Patienten,\n\nfür eine gesunde Mundflora empfehlen wir eine regelmäßige professionelle Zahnreinigung (PZR).\n\nEs ist Zeit für Ihren nächsten Prophylaxe-Termin! Vereinbaren Sie jetzt einfach online einen Termin.\n\nAls Teilnehmer unseres Prophylaxeprogramms profitieren Sie von:\n– Frühzeitiger Erkennung von Karies und Parodontitis\n– Professioneller Reinigung für strahlendes Lächeln\n– Persönlicher Beratung durch unsere ZMP/Dentalhygienikerin\n\nMit freundlichen Grüßen\n${praxisSig()}` },
    "Sommerpause":{ subject:"Unsere Sommerferien — so erreichen Sie uns", body:`Liebe Patientinnen und Patienten,\n\nwir informieren Sie über unsere Urlaubszeiten:\n\nVom 28. Juli bis 15. August 2025 ist unsere Praxis geschlossen.\n\nIn dringenden Fällen wenden Sie sich bitte an die ärztliche Bereitschaftsnummer 116 117.\n\nAb 18. August stehen wir Ihnen wieder gewohnt zur Verfügung — Termine können Sie bereits jetzt online buchen.\n\nSchöne Sommerferien wünscht Ihnen\n${praxisSig()}` },
    "Vorsorge":{ subject:"Zahnvorsorge — Ihr Kontrolltermin steht an", body:`Liebe Patientinnen und Patienten,\n\nIhre Zahngesundheit liegt uns am Herzen! Es ist Zeit für Ihren jährlichen Kontroll-Termin.\n\nRegelmäßige Kontrollen helfen dabei, Karies, Parodontitis und andere Zahnerkrankungen frühzeitig zu erkennen. Vereinbaren Sie jetzt Ihren persönlichen Kontrolltermin.\n\nWas unser Kontroll-Check umfasst:\n– Karies- und Parodontitis-Screening\n– Röntgen-Kontrolle nach Befund\n– Persönliche Mundhygieneberatung\n– Terminplanung für weitere Behandlungen\n\nMit freundlichen Grüßen\n${praxisSig()}` },
  };

  const generate = () => {
    if(!topic) return;
    const tpl = Object.entries(TOPIC_TEMPLATES).find(([k])=>topic.toLowerCase().includes(k.toLowerCase()));
    if(tpl) {
      setGenerating(true);
      setTimeout(()=>{ setSubject(tpl[1].subject); setBody(tpl[1].body); setGenerating(false); }, 1200);
    } else {
      setGenerating(true);
      setTimeout(()=>{
        const g = composeMailText(topic, "Liebe Patientinnen und Patienten");
        setSubject(g.subject); setBody(g.body);
        setGenerating(false);
      }, 1200);
    }
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:7}}>
        {nlDemo ? (<>
          <Met v="1.247" l="Empfänger gesamt"/>
          <Met v="68%" l="Ø Öffnungsrate" d="↑ 3%" dc="up"/>
          <Met v="4" l="Kampagnen 2025"/>
          <Met v="312" l="Segmentierte Listen"/>
        </>) : (<>
          <Met v={String(getAllPatientNames().length)} l="Empfänger" d="Patientenstamm"/>
          <Met v={String(sentMails.length)} l="Gesendete Einzelmails"/>
          <Met v={mailStats?.ok?String(mailStats.summe.gesendet):"—"} l={`Versendet (${statsTage} Tage)`} d={mailStats?.ok?"laut Mailjet":"Statistik lädt …"}/>
          <Met v={oeffnungsrate!=null?`${oeffnungsrate} %`:"—"} l="Öffnungsrate"
            d={oeffnungsrate!=null?`${mailStats.summe.geoeffnet} von ${mailStats.summe.gesendet} geöffnet`:mailStatsErr?"Statistik nicht abrufbar":"noch kein Versand im Zeitraum"}
            dc={oeffnungsrate!=null&&oeffnungsrate>=25?"up":undefined}/>
        </>)}
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:0,borderBottom:"0.5px solid var(--mf-border)"}}>
        {[["neu","Newsletter"],["einzelmail","Einzelmail"],["verlauf","Verlauf"]].map(([k,l])=>(
          <div key={k} onClick={()=>setTab(k)} style={{fontSize:12,padding:"8px 14px",cursor:"pointer",color:tab===k?"#0F6E56":"var(--mf-muted)",fontWeight:tab===k?500:400,borderBottom:tab===k?"2px solid #0F6E56":"2px solid transparent",marginBottom:-0.5}}>{l}</div>
        ))}
      </div>

      {tab==="neu"&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginTop:4}}>
        {/* Left: composer */}
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <div style={{background:"#E1F5EE",border:"0.5px solid #9FE1CB",borderRadius:6,padding:"8px 10px",fontSize:11,color:"#085041",display:"flex",alignItems:"flex-start",gap:7}}>
            <i className="ti ti-robot" style={{fontSize:14,flexShrink:0,marginTop:1}}/>
            <div><strong>KI-Texterstellung:</strong> Gib ein Thema ein und die KI verfasst einen professionellen Newsletter-Text, den du anpassen kannst.</div>
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:3}}>
            <label style={{fontSize:10,fontWeight:500,color:"var(--mf-muted)"}}>Thema eingeben (KI generiert Text)</label>
            <div style={{display:"flex",gap:6}}>
              <Input value={topic} onChange={setTopic} placeholder="z.B. PZR-Recall, Bleaching-Aktion, Sommerpause…" style={{flex:1,marginBottom:0}}/>
              <Btn color="g" onClick={generate} disabled={generating||!topic} style={{fontSize:11,padding:"6px 12px",display:"flex",alignItems:"center",gap:5,opacity:generating||!topic?0.5:1,cursor:generating||!topic?"default":"pointer"}}>
                {generating?<><span style={{animation:"spin 1s linear infinite",display:"inline-block"}}>⟳</span> Generiert…</>:<><i className="ti ti-sparkles" style={{fontSize:12}}/>Generieren</>}
              </Btn>
            </div>
            <div style={{display:"flex",gap:5,flexWrap:"wrap",marginTop:3}}>
              {["Grippeschutzimpfung","PZR-Recall","Sommerpause","Vorsorge"].map(t=>(
                <button key={t} onClick={()=>{setTopic(t);}} style={{fontSize:10,padding:"2px 8px",borderRadius:20,border:"0.5px solid var(--mf-border2)",background:"var(--mf-bg-alt)",color:"var(--mf-muted)",cursor:"pointer",fontFamily:"inherit"}}>{t}</button>
              ))}
            </div>
          </div>

          <Select label="Zielgruppe" value={segment} onChange={setSegment} options={["Alle Patienten (1.247)","Ü60 · Impfstatus offen (312)","Prophylaxe aktiv (848)","Diabetiker Typ 2 (89)","Keine Vorsorge seit 2 Jahren (204)"]}/>

          <Input label="Betreff" value={subject} onChange={setSubject} placeholder="Betreffzeile…" style={{marginBottom:0}}/>

          <div style={{flex:1,display:"flex",flexDirection:"column"}}>
            <Textarea label="Text (bearbeitbar)" value={body} onChange={setBody} rows={9} style={{marginBottom:0,flex:1}}/>
          </div>

          <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
            {sendDone?(
              <div style={{fontSize:11,color:"#085041",background:"#E1F5EE",padding:"5px 11px",borderRadius:5}}>✓ Kampagne gesendet!</div>
            ):sendConfirmOpen?(
              <div style={{display:"flex",gap:5,alignItems:"center",background:"#FAEEDA",padding:"5px 10px",borderRadius:5}}>
                <span style={{fontSize:11,color:"#633806"}}>An {segment} senden?</span>
                <Btn color="g" onClick={async()=>{
                  setSendConfirmOpen(false);
                  const pats = loadPatientsCache().filter(p=>(p.email||"").includes("@")&&p.status!=="Inaktiv");
                  if(serverActive()&&!pats.length){ toast("Keine Patienten mit E-Mail-Adresse — per CSV-Import oder Patientenliste ergänzen.","r"); return; }
                  try{
                    let sent=0;
                    if(serverActive()){
                      for(let i=0;i<pats.length;i+=50){ const r=await sendMails(pats.slice(i,i+50).map(p=>({to:p.email,toName:`${p.vorname} ${p.nachname}`,subject,text:body}))); sent+=r.sent||0; }
                    } else { sent = pats.length || 1247; }
                    const camp={name:subject||topic||"Newsletter",date:new Date().toLocaleDateString("de-DE",{day:"2-digit",month:"2-digit",year:"2-digit"}),segment:serverActive()?`${sent} mit E-Mail`:segment,sent,opened:"—",status:"g"};
                    setRealCampaigns(prev=>{const n=[camp,...prev]; try{localStorage.setItem("dida_campaigns",JSON.stringify(n));}catch{} return n;});
                    setSendDone(true);
                    if(serverActive()) toast(`Kampagne gesendet: ${sent} Mails ✓`);
                  }catch(e){ toast(`Kampagne abgebrochen: ${e.message} — Hinweis: Mailjet-Gratis-Tarif erlaubt 200 Mails/Tag.`,"r"); }
                }}>Ja, senden</Btn>
                <Btn onClick={()=>setSendConfirmOpen(false)}>Nein</Btn>
              </div>
            ):(
              <Btn color="g" onClick={()=>setSendConfirmOpen(true)}>Kampagne senden</Btn>
            )}
            <Btn onClick={()=>setVorschauOpen(true)}>Vorschau</Btn>
            <div style={{display:"flex",alignItems:"center",gap:5}}>
              <Btn onClick={()=>{try{localStorage.setItem("dida_newsletter_draft",JSON.stringify({topic,segment,subject,body}));}catch{}setEntwurfSaved(true);setTimeout(()=>setEntwurfSaved(false),2500);}}>Als Entwurf speichern</Btn>
              {entwurfSaved&&<span style={{fontSize:10,color:"var(--mf-primary)"}}>✓ Gespeichert</span>}
            </div>
          </div>
        </div>

        {/* Right: preview */}
        <div>
          <div style={{fontSize:10,fontWeight:500,color:"var(--mf-muted)",letterSpacing:".4px",textTransform:"uppercase",marginBottom:7}}>E-Mail-Vorschau</div>
          <div style={{border:"0.5px solid var(--mf-border2)",borderRadius:8,overflow:"hidden"}}>
            <div style={{background:"#0F6E56",padding:"12px 14px",display:"flex",alignItems:"center",gap:8}}>
              <div style={{width:22,height:22,background:"rgba(255,255,255,.2)",borderRadius:5,display:"flex",alignItems:"center",justifyContent:"center"}}><i className="ti ti-heartbeat" style={{fontSize:12,color:"#fff"}}/></div>
              <span style={{fontSize:11,fontWeight:500,color:"#E1F5EE"}}>dida · {praxisName()}</span>
            </div>
            <div style={{padding:"12px 14px",background:"var(--mf-bg)"}}>
              {subject&&<div style={{fontSize:12,fontWeight:500,marginBottom:8,color:"var(--mf-text)"}}>{subject}</div>}
              {body?<div style={{fontSize:11,color:"var(--mf-muted)",lineHeight:1.7,whiteSpace:"pre-line"}}>{body}</div>:(
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  {[100,80,95,60,88].map((w,i)=><div key={i} style={{height:8,background:"var(--mf-bg-alt)",borderRadius:4,width:`${w}%`}}/>)}
                </div>
              )}
              {(subject||body)&&<div style={{marginTop:12,padding:"7px 14px",background:"#0F6E56",borderRadius:5,fontSize:11,color:"#E1F5EE",textAlign:"center",cursor:"pointer"}}>Termin buchen →</div>}
            </div>
            <div style={{background:"var(--mf-bg-alt)",padding:"8px 14px",fontSize:9,color:"var(--mf-muted)",textAlign:"center"}}>
              {praxisFooter()} · Abmelden
            </div>
          </div>
          <div style={{marginTop:8,padding:"6px 9px",background:"var(--mf-bg-alt)",borderRadius:6,fontSize:10,color:"var(--mf-muted)"}}>
            Empfänger: <strong style={{color:"var(--mf-text)"}}>{segment}</strong>
          </div>
        </div>
      </div>}

      {tab==="einzelmail"&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginTop:4}}>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <div style={{background:"#E1F5EE",border:"0.5px solid #9FE1CB",borderRadius:6,padding:"8px 10px",fontSize:11,color:"#085041",display:"flex",alignItems:"flex-start",gap:7}}>
            <i className="ti ti-robot" style={{fontSize:14,flexShrink:0,marginTop:1}}/>
            <div><strong>KI-Texterstellung:</strong> Empfänger und Thema eingeben — KI schreibt einen personalisierten Text.</div>
          </div>
          <div style={{position:"relative"}}>
            <label style={{fontSize:10,fontWeight:500,color:"var(--mf-muted)",display:"block",marginBottom:3}}>Empfänger</label>
            <input value={mailPatQuery} onChange={e=>{setMailPatQuery(e.target.value);setMailRecipient(e.target.value);}} placeholder="Name eingeben…" autoComplete="off"
              style={{width:"100%",fontSize:11,padding:"6px 8px",borderRadius:5,border:"0.5px solid var(--mf-border2)",background:"var(--mf-bg-alt)",color:"var(--mf-text)",fontFamily:"inherit",boxSizing:"border-box"}}/>
            {patSuggestsMail.length>0&&(
              <div style={{position:"absolute",top:"100%",left:0,right:0,background:"var(--mf-bg)",border:"0.5px solid var(--mf-border2)",borderRadius:6,zIndex:Z.dropdown,boxShadow:"0 4px 16px rgba(0,0,0,.1)"}}>
                {patSuggestsMail.map(n=>(
                  <div key={n} onClick={()=>{setMailRecipient(n);setMailPatQuery(n);}} style={{padding:"6px 10px",fontSize:11,cursor:"pointer",borderBottom:"0.5px solid var(--mf-border)"}} onMouseEnter={e=>e.currentTarget.style.background="var(--mf-bg-alt)"} onMouseLeave={e=>e.currentTarget.style.background="var(--mf-bg)"}>{n}</div>
                ))}
              </div>
            )}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:3}}>
            <label style={{fontSize:10,fontWeight:500,color:"var(--mf-muted)"}}>Thema (KI generiert Text)</label>
            <div style={{display:"flex",gap:6}}>
              <input value={mailTopic} onChange={e=>setMailTopic(e.target.value)} placeholder="z.B. Terminerinnerung, Recall PZR, Vorsorge…"
                style={{flex:1,fontSize:11,padding:"6px 8px",borderRadius:5,border:"0.5px solid var(--mf-border2)",background:"var(--mf-bg-alt)",color:"var(--mf-text)",fontFamily:"inherit"}}/>
              <Btn color="g" onClick={generateMailText} disabled={mailGenerating||!mailTopic} style={{fontSize:11,padding:"6px 12px",display:"flex",alignItems:"center",gap:5,opacity:mailGenerating||!mailTopic?0.5:1,cursor:mailGenerating||!mailTopic?"default":"pointer"}}>
                {mailGenerating?<><span style={{display:"inline-block"}}>⟳</span> …</>:<><i className="ti ti-sparkles" style={{fontSize:12}}/>Generieren</>}
              </Btn>
            </div>
            <div style={{display:"flex",gap:5,flexWrap:"wrap",marginTop:3}}>
              {["Terminerinnerung","Recall PZR","Vorsorge"].map(t=>(
                <button key={t} onClick={()=>setMailTopic(t)} style={{fontSize:10,padding:"2px 8px",borderRadius:20,border:"0.5px solid var(--mf-border2)",background:"var(--mf-bg-alt)",color:"var(--mf-muted)",cursor:"pointer",fontFamily:"inherit"}}>{t}</button>
              ))}
            </div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:3}}>
            <label style={{fontSize:10,fontWeight:500,color:"var(--mf-muted)"}}>Betreff</label>
            <Input value={mailSubject} onChange={setMailSubject} label="Betreff" placeholder="Betreffzeile…" style={{marginBottom:0}}/>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:3}}>
            <label style={{fontSize:10,fontWeight:500,color:"var(--mf-muted)"}}>Text</label>
            <Textarea value={mailBody} onChange={setMailBody} rows={7} style={{marginBottom:0}}/>
          </div>
          <Btn color="g" onClick={sendMail}>Mail senden</Btn>
          {sentMails.length>0&&<>
            <div style={{fontSize:10,fontWeight:500,color:"var(--mf-muted)",marginTop:4}}>Gesendete Einzelmails</div>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              {sentMails.slice(0,6).map(m=>(
                <div key={m.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 9px",background:"var(--mf-bg-alt)",borderRadius:6,fontSize:10}}>
                  <i className="ti ti-mail-check" style={{fontSize:13,color:"var(--mf-primary)",flexShrink:0}}/>
                  <div style={{flex:1}}><div style={{fontWeight:500}}>{m.recipient}</div><div style={{color:"var(--mf-muted)"}}>{m.subject}</div></div>
                  <div style={{color:"var(--mf-muted)",whiteSpace:"nowrap"}}>{m.datum} {m.time}</div>
                </div>
              ))}
            </div>
          </>}
        </div>
        <div>
          <div style={{fontSize:10,fontWeight:500,color:"var(--mf-muted)",letterSpacing:".4px",textTransform:"uppercase",marginBottom:7}}>Vorschau</div>
          <div style={{border:"0.5px solid var(--mf-border2)",borderRadius:8,overflow:"hidden"}}>
            <div style={{background:"#0F6E56",padding:"10px 14px",display:"flex",alignItems:"center",gap:8}}>
              <div style={{width:20,height:20,background:"rgba(255,255,255,.2)",borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center"}}><i className="ti ti-heartbeat" style={{fontSize:11,color:"#fff"}}/></div>
              <span style={{fontSize:10,fontWeight:500,color:"#E1F5EE"}}>dida · {praxisName()}</span>
            </div>
            <div style={{padding:"12px 14px",background:"var(--mf-bg)"}}>
              {mailRecipient&&<div style={{fontSize:10,color:"var(--mf-muted)",marginBottom:6}}>An: {mailRecipient}</div>}
              {mailSubject&&<div style={{fontSize:12,fontWeight:500,marginBottom:8,color:"var(--mf-text)"}}>{mailSubject}</div>}
              {mailBody?<div style={{fontSize:11,color:"var(--mf-muted)",lineHeight:1.7,whiteSpace:"pre-line"}}>{mailBody}</div>
              :<div style={{display:"flex",flexDirection:"column",gap:5}}>{[100,80,90].map((w,i)=><div key={i} style={{height:7,background:"var(--mf-bg-alt)",borderRadius:4,width:`${w}%`}}/>)}</div>}
            </div>
            <div style={{background:"var(--mf-bg-alt)",padding:"7px 14px",fontSize:9,color:"var(--mf-muted)",textAlign:"center"}}>{praxisFooter()}</div>
          </div>
        </div>
      </div>}

      {tab==="verlauf"&&<Card style={{marginBottom:10}}>
        <CardHead title="Zustellung & Öffnungen" icon="ti-chart-dots"
          right={
            <div style={{display:"flex",gap:4}}>
              {[7,30,90].map(t=>(
                <button key={t} onClick={()=>setStatsTage(t)}
                  style={{fontSize:10,padding:"3px 9px",borderRadius:20,border:"0.5px solid var(--mf-border2)",
                    background:statsTage===t?"#0F6E56":"var(--mf-bg-alt)",color:statsTage===t?"#E1F5EE":"var(--mf-muted)",
                    cursor:"pointer",fontFamily:"inherit"}}>{t} Tage</button>
              ))}
            </div>
          }/>
        {mailStatsErr ? (
          <Insight tone="a"><strong>Statistik nicht abrufbar:</strong> {mailStatsErr}</Insight>
        ) : mailStats?.demo ? (
          <Insight>Versand-Statistik gibt es nur im Live-Modus — in der Demo würden hier erfundene Öffnungsraten stehen.</Insight>
        ) : !mailStats ? (
          <div style={{fontSize:11,color:"var(--mf-muted)",padding:"8px 0"}}>Lädt Mailjet-Statistik …</div>
        ) : mailStats.summe.gesendet===0 ? (
          <Insight>In den letzten {statsTage} Tagen wurde nichts versendet — sobald Mails rausgehen, erscheinen hier Zustellung und Öffnungen.</Insight>
        ) : (<>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:7,marginBottom:12}}>
            <MiniStat v={String(mailStats.summe.gesendet)} l="Versendet"/>
            <MiniStat v={`${mailQuote(mailStats.summe.geoeffnet,mailStats.summe.gesendet)} %`} l="Geöffnet" tone="g"/>
            <MiniStat v={`${mailQuote(mailStats.summe.geklickt,mailStats.summe.gesendet)} %`} l="Link geklickt"/>
            <MiniStat v={String(mailStats.summe.bounces)} l="Unzustellbar" tone={mailStats.summe.bounces>0?"r":undefined}/>
            <MiniStat v={String(mailStats.summe.spam)} l="Als Spam gemeldet" tone={mailStats.summe.spam>0?"r":undefined}/>
          </div>
          {mailStats.summe.bounces>0&&(
            <div style={{marginBottom:10}}>
              <Insight tone="a"><strong>{mailStats.summe.bounces}</strong> {mailStats.summe.bounces===1?"Mail kam":"Mails kamen"} nicht an — meist veraltete Adressen. In der Patientenliste korrigieren, sonst leidet die Zustellbarkeit aller Mails.</Insight>
            </div>
          )}
          <SH t="Je Tag"/>
          {mailStats.reihe.filter(d=>d.gesendet>0).slice(-14).map(d=>(
            <ProgRow key={d.tag} label={d.tag.split("-").reverse().join(".")}
              val={mailQuote(d.geoeffnet,d.gesendet)||0}
              valStr={`${d.geoeffnet}/${d.gesendet}`}/>
          ))}
          <div style={{fontSize:10,color:"var(--mf-muted)",marginTop:8,lineHeight:1.5}}>
            Zahlen kommen direkt aus Mailjet und umfassen alle über dida versendeten Mails (Recall, Mahnung, Umfrage, Newsletter). Öffnungen misst Mailjet über einen Pixel im HTML-Teil — Mails, die vor der Umstellung auf HTML rausgingen, zählen dort mit 0.
          </div>
        </>)}
      </Card>}

      {tab==="verlauf"&&<Card>
        <CardHead title="Gesendete Kampagnen" icon="ti-send"/>
        <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed"}}>
          <thead><tr>{["Kampagne","Datum","Segment","Gesendet","Öffnungsrate","Status"].map(h=><th key={h} style={{textAlign:"left",color:"var(--mf-muted)",fontWeight:400,fontSize:10,padding:"3px 6px",borderBottom:"0.5px solid var(--mf-border)"}}>{h}</th>)}</tr></thead>
          <tbody>
            {realCampaigns.map((cp,i)=>(
              <tr key={"r"+i}>{[cp.name,cp.date,cp.segment,String(cp.sent),cp.opened].map((v,j)=><td key={j} style={{padding:"5px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,fontWeight:j===0?500:400,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v}</td>)}
              <td style={{padding:"5px 6px",borderBottom:"0.5px solid var(--mf-border)"}}><Sp color="g">Gesendet</Sp></td></tr>
            ))}
            {CAMPAIGNS.length===0&&realCampaigns.length===0&&<tr><td colSpan={99} style={{padding:"12px 5px",fontSize:11,color:"var(--mf-muted)",textAlign:"center"}}>Noch keine Kampagnen versendet — die erste startest du oben unter „Newsletter“.</td></tr>}
            {CAMPAIGNS.map(c=>(
              <tr key={c.name} style={{cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.background="var(--mf-bg-alt)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                onClick={()=>{setSubject(c.name);setSegment(c.segment);setSendDone(false);setSendConfirmOpen(false);setTab("neu");}}>
                <td style={{padding:"6px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,fontWeight:500}}>{c.name}</td>
                <td style={{padding:"6px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>{c.date}</td>
                <td style={{padding:"6px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>{c.segment}</td>
                <td style={{padding:"6px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>{c.sent}</td>
                <td style={{padding:"6px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,color:"var(--mf-primary)",fontWeight:500}}>{c.opened}</td>
                <td style={{padding:"6px 6px",borderBottom:"0.5px solid var(--mf-border)"}}><Sp color={c.status}>Gesendet</Sp></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>}

      {/* VORSCHAU MODAL */}
      {vorschauOpen&&<>
        <div onClick={()=>setVorschauOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay2}}/>
        <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:520,background:"var(--mf-bg)",borderRadius:14,zIndex:Z.modal2,boxShadow:"0 24px 64px rgba(0,0,0,.3)",overflow:"hidden"}}>
          <div style={{padding:"14px 18px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{fontSize:13,fontWeight:500}}>E-Mail-Vorschau</div>
            <div onClick={()=>setVorschauOpen(false)} style={{cursor:"pointer",fontSize:18,color:"var(--mf-muted)"}}>×</div>
          </div>
          <div style={{padding:"16px 18px"}}>
            <div style={{border:"0.5px solid var(--mf-border2)",borderRadius:8,overflow:"hidden",marginBottom:12}}>
              <div style={{background:"#0F6E56",padding:"12px 14px",display:"flex",alignItems:"center",gap:8}}>
                <div style={{width:22,height:22,background:"rgba(255,255,255,.2)",borderRadius:5,display:"flex",alignItems:"center",justifyContent:"center"}}><i className="ti ti-heartbeat" style={{fontSize:12,color:"#fff"}}/></div>
                <span style={{fontSize:11,fontWeight:500,color:"#E1F5EE"}}>dida · {praxisName()}</span>
              </div>
              <div style={{padding:"12px 14px",background:"var(--mf-bg)"}}>
                {subject?<div style={{fontSize:12,fontWeight:500,marginBottom:8}}>{subject}</div>:<div style={{fontSize:11,color:"var(--mf-muted)",fontStyle:"italic",marginBottom:8}}>Kein Betreff eingegeben</div>}
                {body?<div style={{fontSize:11,color:"var(--mf-muted)",lineHeight:1.7,whiteSpace:"pre-line"}}>{body}</div>:<div style={{fontSize:11,color:"var(--mf-muted)",fontStyle:"italic"}}>Kein Text eingegeben</div>}
              </div>
              <div style={{background:"var(--mf-bg-alt)",padding:"8px 14px",fontSize:9,color:"var(--mf-muted)",textAlign:"center"}}>{praxisFooter()} · Abmelden</div>
            </div>
            <div style={{fontSize:10,color:"var(--mf-muted)"}}>Empfänger: <strong style={{color:"var(--mf-text)"}}>{segment}</strong></div>
          </div>
        </div>
      </>}
    </div>
  );
}

// ─── ABRECHNUNG SCREEN ────────────────────────────────────────────────────────
const INIT_INVOICES = [
  { nr:"2025-0089", patient:"Maier, Maria", datum:"10.06.25", kv:"AOK", betrag:"€89.40", betragNum:89.40, typ:"Kassenabrechnung", status:"offen", notiz:"", positionen:[{bez:"Anamnese und Beratung",bet:"€35.00"},{bez:"Untersuchung allgemein",bet:"€54.40"}] },
  { nr:"2026-0088", patient:"Fischer, Anna", datum:"09.06.26", kv:"Privat", betrag:"€380.00", betragNum:380.00, typ:"Privatliquidation", status:"offen", notiz:"GOZ §5 Abs.2", positionen:[{bez:"Bleaching In-Office",bet:"€280.00"},{bez:"Schiene Oberkiefer",bet:"€100.00"}] },
  { nr:"2026-0087", patient:"Weber, Klaus", datum:"08.06.26", kv:"TK", betrag:"€142.60", betragNum:142.60, typ:"Kassenabrechnung", status:"bezahlt", notiz:"", positionen:[{bez:"Krone 46 eingliedern",bet:"€98.40"},{bez:"Kontrolle & Befund",bet:"€44.20"}] },
  { nr:"2026-0086", patient:"Schmidt, Hans", datum:"07.06.26", kv:"BKK", betrag:"€224.80", betragNum:224.80, typ:"Kassenabrechnung", status:"bezahlt", notiz:"", positionen:[{bez:"Wurzelbehandlung 36",bet:"€156.40"},{bez:"Röntgenaufnahme",bet:"€68.40"}] },
  { nr:"2026-0085", patient:"Braun, Max", datum:"06.06.26", kv:"BKK", betrag:"€89.50", betragNum:89.50, typ:"IGeL", status:"mahnung", notiz:"2. Mahnung", positionen:[{bez:"Bleaching-Beratung + Aufhellung",bet:"€89.50"}] },
];

function AbrechnungScreen() {
  const [invoices, setInvoices] = useState(()=>{
    const cached=invoiceStore.loadCache(); return cached!==null?cached:(demoFallbacks()?INIT_INVOICES:[]);
  });
  useEffect(()=>{ let on=true; invoiceStore.list().then(r=>{ if(on&&(r.length||!demoFallbacks())) setInvoices(r); }).catch(()=>{}); return ()=>{ on=false; }; },[]);
  useEffect(()=>{ if(serverActive()) return; try{localStorage.setItem("dida_invoices",JSON.stringify(invoices));}catch{} },[invoices]); // LIVE spiegelt der Store
  // Rechnungen sind über nr identifiziert — für die DB eine stabile Zahl daraus
  const invId=(i)=>i.id||parseInt(String(i.nr).replace(/\D/g,""))||Date.now();
  const persistInv=(i)=>{ if(!serverActive()) return; invoiceStore.upsert({...i,id:invId(i)}).catch(()=>toast("Rechnung nicht gespeichert — Verbindung prüfen.","r")); };

  const [quarter, setQuarter] = useState(()=>`Q${Math.floor(new Date().getMonth()/3)+1} ${new Date().getFullYear()}`);
  const [statusFilter, setStatusFilter] = useState("alle");
  const [selInv, setSelInv] = useState(null);
  const [newInvOpen, setNewInvOpen] = useState(false);
  const [newInv, setNewInv] = useState({patient:"",datum:"",betrag:"",typ:"Kassenabrechnung",notiz:""});
  const [patQuery, setPatQuery] = useState("");

  const patSuggestions = useMemo(() => patQuery.length>=1 ? getAllPatientNames().filter(n=>n.toLowerCase().includes(patQuery.toLowerCase())).slice(0,5) : [], [patQuery]);

  const parseDate = (d) => { try { if(d&&d.includes("-")&&d.length>=8) return new Date(d); const [dd,mm,yy]=d.split("."); return new Date(2000+parseInt(yy),parseInt(mm)-1,parseInt(dd)); } catch { return new Date(0); } };
  const quarterInvoices = (() => {
    if(quarter==="Alle") return invoices;
    const m=quarter.match(/^Q(\d) (\d{4})$/);
    if(!m) return invoices;
    const q=parseInt(m[1]),yr=parseInt(m[2]);
    return invoices.filter(i=>{ const d=parseDate(i.datum); return Math.floor(d.getMonth()/3)+1===q&&d.getFullYear()===yr; });
  })();
  const filteredInvoices = statusFilter==="alle" ? quarterInvoices : quarterInvoices.filter(i=>i.status===statusFilter);

  const exportCSV = () => {
    const header = ["Nr.","Patient","Datum","KV","Betrag","Typ","Status","Notiz"];
    const rows = filteredInvoices.map(i=>[i.nr,i.patient,i.datum,i.kv,i.betrag,i.typ,i.status,i.notiz||""].map(v=>`"${v}"`).join(","));
    const csv = [header.join(","),...rows].join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8,﻿"+encodeURIComponent(csv);
    a.download = `rechnungen_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  const saveNewInv = () => {
    if(!newInv.patient||!newInv.datum||!newInv.betrag) return;
    const num = parseFloat(newInv.betrag.replace(",",".").replace("€",""))||0;
    const maxNr = invoices.reduce((mx,i)=>{const n=parseInt(i.nr.split("-")[1]||0);return n>mx?n:mx;},0);
    const inv = {
      nr:`${new Date().getFullYear()}-${String(maxNr+1).padStart(4,"0")}`,
      patient:newInv.patient, datum:newInv.datum, kv:"—", betrag:`€${num.toFixed(2)}`,
      betragNum:num, typ:newInv.typ, status:"offen", notiz:newInv.notiz,
      positionen:[{bez:newInv.typ,bet:`€${num.toFixed(2)}`}]
    };
    setInvoices(prev=>[inv,...prev]); // optimistisch
    persistInv(inv);
    setNewInv({patient:"",datum:"",betrag:"",typ:"Kassenabrechnung",notiz:""});
    setPatQuery("");
    setNewInvOpen(false);
  };

  const STATUS_LABELS = {offen:"Offen",bezahlt:"Bezahlt",mahnung:"Mahnung"};
  const STATUS_COLORS = {offen:"a",bezahlt:"g",mahnung:"r"};

  return (
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <div style={{display:"flex",gap:0,alignItems:"center",padding:"7px 0",borderBottom:"0.5px solid var(--mf-border)"}}>
        <div style={{display:"flex",gap:5}}>
          {(()=>{ const yr=new Date().getFullYear(); return [`Q1 ${yr}`,`Q2 ${yr}`,`Q3 ${yr}`,`Q4 ${yr}`,"Alle"]; })().map(q=>(
            <button key={q} onClick={()=>setQuarter(q)} style={{fontSize:10,padding:"3px 10px",borderRadius:20,border:"0.5px solid var(--mf-border2)",background:quarter===q?"#0F6E56":"var(--mf-bg-alt)",color:quarter===q?"#E1F5EE":"var(--mf-muted)",cursor:"pointer",fontFamily:"inherit"}}>{q}</button>
          ))}
        </div>
        <div style={{marginLeft:"auto",display:"flex",gap:6,alignItems:"center"}}>
          {(() => {
            let last = null;
            try { const log = JSON.parse(localStorage.getItem("dida_import_log")||"[]"); last = log[0]||null; } catch {}
            return last ? (
              <div style={{display:"flex",alignItems:"center",gap:5,fontSize:10,padding:"3px 8px",borderRadius:5,background:"#E1F5EE",color:"#085041"}}>
                <i className="ti ti-file-import" style={{fontSize:11}}/> PVS-Daten: zuletzt {last.ts}
              </div>
            ) : (
              <div style={{display:"flex",alignItems:"center",gap:5,fontSize:10,padding:"3px 8px",borderRadius:5,background:"var(--mf-bg-alt)",color:"var(--mf-muted)"}}>
                <i className="ti ti-file-import" style={{fontSize:11}}/> Noch kein PVS-Import
              </div>
            );
          })()}
          <Btn onClick={exportCSV}>Export CSV</Btn>
        </div>
      </div>

      {(()=>{ const sumTyp=t=>Math.round(quarterInvoices.filter(i=>i.typ===t).reduce((s,i)=>s+i.betragNum,0)); return (
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:7}}>
        <Met v={`€${sumTyp("Kassenabrechnung").toLocaleString("de-DE")}`} l={`Kassenabrechnung ${quarter}`} d="BEMA-Ziffern abgerechnet"/>
        <Met v={`€${sumTyp("Privatliquidation").toLocaleString("de-DE")}`} l={`Privatliquidation ${quarter}`} d="GOZ-Positionen"/>
        <Met v={`€${sumTyp("IGeL").toLocaleString("de-DE")}`} l={`IGeL-Leistungen ${quarter}`}/>
        <Met v={`€${invoices.filter(i=>i.status==="offen").reduce((s,i)=>s+i.betragNum,0).toFixed(2).replace(".",",")}`} l="Offene Forderungen" d={`${invoices.filter(i=>i.status==="offen").length} Rechnungen`} dc="dn"/>
      </div> ); })()}

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9}}>
        <Card>
          <CardHead title="Schnittstellen-Status" icon="ti-plug"/>
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            {(() => {
              const feeds = listFeeds();
              let lastImport = null;
              try { const log = JSON.parse(localStorage.getItem("dida_import_log")||"[]"); lastImport = log[0]||null; } catch {}
              const rows = [
                isSupabaseConfigured
                  ? ["ti-database","var(--mf-primary)","Datenbank (Supabase)","g","Verbunden"]
                  : ["ti-database-off","#BA7517","Datenbank","a","Demo-Modus"],
                feeds.length
                  ? ["ti-calendar-repeat","var(--mf-primary)",`Kalender-Abo (${feeds.length} aktiv)`,"g",feeds[0].lastSync?`Sync ${feeds[0].lastSync}`:"Aktiv"]
                  : ["ti-calendar-off","#888","Kalender-Abo (Outlook/Google)","n","Nicht eingerichtet"],
                lastImport
                  ? ["ti-file-import","var(--mf-primary)","PVS-CSV-Import","g",`Zuletzt ${lastImport.ts}`]
                  : ["ti-file-import","#BA7517","PVS-CSV-Import","a","Noch kein Import"],
                ["ti-message","#888","SMS/E-Mail-Versand (Recall)","n","Geplant"],
              ];
              return rows.map(([icon,col,label,s,st])=>(
                <div key={label} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",background:"var(--mf-bg-alt)",borderRadius:5,fontSize:11}}>
                  <i className={`ti ${icon}`} style={{fontSize:13,color:col,flexShrink:0}}/>
                  <span style={{flex:1}}>{label}</span>
                  <Sp color={s}>{st}</Sp>
                </div>
              ));
            })()}
          </div>
        </Card>
        <Card>
          <CardHead title={`Abrechnungsübersicht ${quarter}`} icon="ti-chart-pie"/>
          {(()=>{
            const gesamt=quarterInvoices.reduce((s,i)=>s+i.betragNum,0);
            if(!gesamt) return <div style={{fontSize:11,color:"var(--mf-muted)",padding:"8px 0"}}>Noch keine Rechnungen in diesem Quartal — per PVS-Import einspielen oder manuell anlegen.</div>;
            const p=(t)=>quarterInvoices.filter(i=>i.typ===t).reduce((s,i)=>s+i.betragNum,0);
            const offen=quarterInvoices.filter(i=>i.status!=="bezahlt").reduce((s,i)=>s+i.betragNum,0);
            const f=(n)=>`€${Math.round(n).toLocaleString("de-DE")}`;
            return (<>
              <ProgRow label="Kasse (BEMA)" val={Math.round(p("Kassenabrechnung")/gesamt*100)} valStr={f(p("Kassenabrechnung"))}/>
              <ProgRow label="Privat (GOZ)" val={Math.round(p("Privatliquidation")/gesamt*100)} valStr={f(p("Privatliquidation"))} color="#185FA5"/>
              <ProgRow label="IGeL" val={Math.round(p("IGeL")/gesamt*100)} valStr={f(p("IGeL"))} color="#BA7517"/>
              <ProgRow label="Offen" val={Math.round(offen/gesamt*100)} valStr={f(offen)} color="#993C1D"/>
            </>);
          })()}
        </Card>
      </div>

      <Card>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:12,fontWeight:500}}>Rechnungsübersicht</span>
            <div style={{display:"flex",gap:3}}>
              {["alle","offen","bezahlt","mahnung"].map(f=>(
                <button key={f} onClick={()=>setStatusFilter(f)} style={{fontSize:10,padding:"2px 9px",borderRadius:20,border:"0.5px solid var(--mf-border2)",background:statusFilter===f?"#0F6E56":"var(--mf-bg-alt)",color:statusFilter===f?"#E1F5EE":"var(--mf-muted)",cursor:"pointer",fontFamily:"inherit",fontWeight:statusFilter===f?500:400}}>
                  {f==="alle"?"Alle":STATUS_LABELS[f]}
                </button>
              ))}
            </div>
          </div>
          <Btn color="g" onClick={()=>setNewInvOpen(true)}>+ Neue Rechnung</Btn>
        </div>
        <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed"}}>
          <thead><tr>{["Nr.","Patient","Datum","KV","Betrag","Typ","Status"].map(h=><th key={h} style={{textAlign:"left",color:"var(--mf-muted)",fontWeight:400,fontSize:10,padding:"3px 6px",borderBottom:"0.5px solid var(--mf-border)"}}>{h}</th>)}</tr></thead>
          <tbody>
            {filteredInvoices.map(inv=>(
              <tr key={inv.nr} onClick={()=>setSelInv(inv)} style={{cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.background="var(--mf-bg-alt)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <td style={{padding:"5px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,color:"var(--mf-muted)"}}>{inv.nr}</td>
                <td style={{padding:"5px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,fontWeight:500}}>{inv.patient}</td>
                <td style={{padding:"5px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>{inv.datum}</td>
                <td style={{padding:"5px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>{inv.kv}</td>
                <td style={{padding:"5px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,fontWeight:500}}>{inv.betrag}</td>
                <td style={{padding:"5px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>{inv.typ}</td>
                <td style={{padding:"5px 6px",borderBottom:"0.5px solid var(--mf-border)"}}><Sp color={STATUS_COLORS[inv.status]}>{STATUS_LABELS[inv.status]}</Sp></td>
              </tr>
            ))}
            {!filteredInvoices.length&&<tr><td colSpan={7} style={{padding:"16px 6px",textAlign:"center",fontSize:11,color:"var(--mf-muted)"}}>Keine Rechnungen gefunden</td></tr>}
          </tbody>
        </table>
      </Card>

      {/* INVOICE DETAIL MODAL */}
      {selInv&&<>
        <div onClick={()=>setSelInv(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay}}/>
        <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:520,maxHeight:"85vh",maxWidth:"96vw",background:"var(--mf-bg)",borderRadius:14,zIndex:Z.modal,display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 64px rgba(0,0,0,.3)"}}>
          <div style={{padding:"14px 18px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
            <i className="ti ti-file-invoice" style={{fontSize:20,color:"var(--mf-primary)"}}/>
            <div style={{flex:1}}><div style={{fontSize:14,fontWeight:600}}>Rechnung {selInv.nr}</div><div style={{fontSize:11,color:"var(--mf-muted)"}}>{selInv.patient}</div></div>
            <div onClick={()=>setSelInv(null)} style={{cursor:"pointer",fontSize:20,color:"var(--mf-muted)",width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",background:"var(--mf-bg-alt)"}}>×</div>
          </div>
          <div style={{padding:"16px 18px",overflowY:"auto"}}>
            <SLabel>Rechnungsdetails</SLabel>
            <DRow label="Patient">{selInv.patient}</DRow>
            <DRow label="Datum">{selInv.datum}</DRow>
            <DRow label="Krankenkasse / KV">{selInv.kv}</DRow>
            <DRow label="Typ">{selInv.typ}</DRow>
            <DRow label="Gesamtbetrag"><span style={{fontWeight:600,color:"var(--mf-primary)"}}>{selInv.betrag}</span></DRow>
            <DRow label="Status"><Sp color={STATUS_COLORS[selInv.status]}>{STATUS_LABELS[selInv.status]}</Sp></DRow>
            {selInv.notiz&&<DRow label="Notiz">{selInv.notiz}</DRow>}
            <Divider/>
            <SLabel>Positionen</SLabel>
            <table style={{width:"100%",borderCollapse:"collapse",marginBottom:6}}>
              <thead><tr>
                <th style={{textAlign:"left",fontSize:10,color:"var(--mf-muted)",fontWeight:400,padding:"3px 6px",borderBottom:"0.5px solid var(--mf-border)"}}>Bezeichnung</th>
                <th style={{textAlign:"right",fontSize:10,color:"var(--mf-muted)",fontWeight:400,padding:"3px 6px",borderBottom:"0.5px solid var(--mf-border)"}}>Betrag</th>
              </tr></thead>
              <tbody>
                {(selInv.positionen||[]).map((p,i)=>(
                  <tr key={`${p.bez}-${i}`}>
                    <td style={{padding:"5px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11}}>{p.bez}</td>
                    <td style={{padding:"5px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,textAlign:"right",fontWeight:500}}>{p.bet}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{padding:"6px 6px",fontSize:12,fontWeight:600}}>Gesamt</td>
                  <td style={{padding:"6px 6px",fontSize:12,fontWeight:600,textAlign:"right",color:"var(--mf-primary)"}}>{selInv.betrag}</td>
                </tr>
              </tbody>
            </table>
            <Divider/>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {selInv.status!=="bezahlt"&&<Btn color="g" onClick={()=>{const u={...selInv,status:"bezahlt"};setInvoices(prev=>prev.map(i=>i.nr===selInv.nr?u:i));setSelInv(u);persistInv(u);}}>Als bezahlt markieren</Btn>}
              {selInv.status==="offen"&&<Btn color="r" onClick={()=>{const u={...selInv,status:"mahnung"};setInvoices(prev=>prev.map(i=>i.nr===selInv.nr?u:i));setSelInv(u);persistInv(u);}}>Mahnung setzen</Btn>}
              <Btn onClick={()=>setSelInv(null)}>Schließen</Btn>
            </div>
          </div>
        </div>
      </>}

      {/* NEW INVOICE MODAL */}
      {newInvOpen&&<>
        <div onClick={()=>setNewInvOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay}}/>
        <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:480,maxHeight:"85vh",maxWidth:"96vw",background:"var(--mf-bg)",borderRadius:14,zIndex:Z.modal,display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 64px rgba(0,0,0,.3)"}}>
          <div style={{padding:"14px 18px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
            <span style={{fontSize:14,fontWeight:600,flex:1}}>Neue Rechnung</span>
            <div onClick={()=>setNewInvOpen(false)} style={{cursor:"pointer",fontSize:20,color:"var(--mf-muted)",width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",background:"var(--mf-bg-alt)"}}>×</div>
          </div>
          <div style={{padding:"16px 18px",overflowY:"auto"}}>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div style={{position:"relative"}}>
                <label style={{fontSize:10,color:"var(--mf-muted)",display:"block",marginBottom:3}}>Patient *</label>
                <input value={patQuery} onChange={e=>{setPatQuery(e.target.value);setNewInv(p=>({...p,patient:e.target.value}));}}
                  placeholder="Name eingeben…" autoComplete="off"
                  style={{width:"100%",fontSize:12,padding:"7px 10px",borderRadius:6,border:"0.5px solid var(--mf-border2)",background:"var(--mf-bg-alt)",color:"var(--mf-text)",fontFamily:"inherit",boxSizing:"border-box"}}/>
                {patSuggestions.length>0&&(
                  <div style={{position:"absolute",top:"100%",left:0,right:0,background:"var(--mf-bg)",border:"0.5px solid var(--mf-border2)",borderRadius:6,zIndex:Z.dropdown,boxShadow:"0 4px 16px rgba(0,0,0,.1)"}}>
                    {patSuggestions.map(n=>(
                      <div key={n} onClick={()=>{setPatQuery(n);setNewInv(p=>({...p,patient:n}));}} style={{padding:"7px 10px",fontSize:11,cursor:"pointer",borderBottom:"0.5px solid var(--mf-border)"}} onMouseEnter={e=>e.currentTarget.style.background="var(--mf-bg-alt)"} onMouseLeave={e=>e.currentTarget.style.background="var(--mf-bg)"}>{n}</div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label style={{fontSize:10,color:"var(--mf-muted)",display:"block",marginBottom:3}}>Datum *</label>
                <input type="date" value={newInv.datum} onChange={e=>setNewInv(p=>({...p,datum:e.target.value}))}
                  style={{width:"100%",fontSize:12,padding:"7px 10px",borderRadius:6,border:"0.5px solid var(--mf-border2)",background:"var(--mf-bg-alt)",color:"var(--mf-text)",fontFamily:"inherit",boxSizing:"border-box"}}/>
              </div>
              <div>
                <label style={{fontSize:10,color:"var(--mf-muted)",display:"block",marginBottom:3}}>Betrag (€) *</label>
                <input type="number" min="0" step="0.01" value={newInv.betrag} onChange={e=>setNewInv(p=>({...p,betrag:e.target.value}))}
                  placeholder="0.00"
                  style={{width:"100%",fontSize:12,padding:"7px 10px",borderRadius:6,border:"0.5px solid var(--mf-border2)",background:"var(--mf-bg-alt)",color:"var(--mf-text)",fontFamily:"inherit",boxSizing:"border-box"}}/>
              </div>
              <div>
                <label style={{fontSize:10,color:"var(--mf-muted)",display:"block",marginBottom:3}}>Typ</label>
                <select value={newInv.typ} onChange={e=>setNewInv(p=>({...p,typ:e.target.value}))}
                  style={{width:"100%",fontSize:12,padding:"7px 10px",borderRadius:6,border:"0.5px solid var(--mf-border2)",background:"var(--mf-bg-alt)",color:"var(--mf-text)",fontFamily:"inherit",boxSizing:"border-box"}}>
                  <option>Kassenabrechnung</option>
                  <option>Privatliquidation</option>
                  <option>IGeL</option>
                </select>
              </div>
              <div>
                <label style={{fontSize:10,color:"var(--mf-muted)",display:"block",marginBottom:3}}>Notiz (optional)</label>
                <input value={newInv.notiz} onChange={e=>setNewInv(p=>({...p,notiz:e.target.value}))}
                  placeholder="Interne Notiz…"
                  style={{width:"100%",fontSize:12,padding:"7px 10px",borderRadius:6,border:"0.5px solid var(--mf-border2)",background:"var(--mf-bg-alt)",color:"var(--mf-text)",fontFamily:"inherit",boxSizing:"border-box"}}/>
              </div>
              <div style={{display:"flex",gap:6,paddingTop:4}}>
                <Btn color="g" onClick={saveNewInv}>Rechnung speichern</Btn>
                <Btn onClick={()=>setNewInvOpen(false)}>Abbrechen</Btn>
              </div>
            </div>
          </div>
        </div>
      </>}
    </div>
  );
}

// ─── MAIN APP ──────────────────────────────────────────────────────────────────
const ROLES_CFG = {
  inhaber:{ label:"Inhaberin",  pill:"g",  fav:"DH",  favBg:"#E1F5EE", favCol:"#085041", fn:"Dr. Hoffmann",  fr:"Inhaberin · Admin" },
  arzt:   { label:"Zahnarzt",     pill:"b",  fav:"DS",  favBg:"#E6F1FB", favCol:"#0C447C", fn:"Dr. Schulz",    fr:"Zahnarzt · Chirurgie" },
  arzt2:  { label:"Zahnärztin KFO", pill:"pu", fav:"LBe", favBg:"#EEEDFE", favCol:"#3C3489", fn:"Dr. Bergmann",  fr:"Zahnärztin · KFO" },
  arzt3:  { label:"Zahnarzt i.A.", pill:"b",  fav:"TF",  favBg:"#FFF0E6", favCol:"#7A3800", fn:"Dr. Fischer",   fr:"Zahnarzt · Assistenz" },
  dh:     { label:"ZMP/DH",     pill:"g",  fav:"MR",  favBg:"#F0FFF4", favCol:"#166534", fn:"Maja Roth",     fr:"ZMP · Dentalhygienikerin" },
  mfa:    { label:"Empfang",    pill:"a",  fav:"LB",  favBg:"#FAEEDA", favCol:"#633806", fn:"Lisa Beck",     fr:"MFA · Empfang" },
};

function LoginScreen({ onLogin, onBack }) {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfaFactor, setMfaFactor] = useState(null); // 2FA: verifizierter TOTP-Faktor
  const [mfaCode, setMfaCode] = useState("");

  const markIntro = () => { try { sessionStorage.setItem("dida_login_intro", "1"); } catch {} };
  const submit = async () => {
    if (!isSupabaseConfigured) { markIntro(); onLogin(); return; }   // Demo-Modus
    if (!email || !pw) { setErr("Bitte E-Mail und Passwort eingeben."); return; }
    setBusy(true); setErr("");
    const { error } = await supabase.auth.signInWithPassword({ email, password: pw });
    if (error) { setBusy(false); setErr("Anmeldung fehlgeschlagen — E-Mail oder Passwort prüfen."); return; }
    markIntro(); // Schreibschrift-Übergang beim Wechsel ins Dashboard
    // 2FA aktiv? Dann Code-Schritt zeigen (die App bleibt bis AAL2 gesperrt)
    try {
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal?.nextLevel === "aal2" && aal.currentLevel !== "aal2") {
        const { data: f } = await supabase.auth.mfa.listFactors();
        const totp = (f?.totp || []).find(x => x.status === "verified");
        if (totp) setMfaFactor(totp.id);
      }
    } catch {}
    setBusy(false);
    // Erfolg ohne 2FA: der Auth-Listener in dida übernimmt
  };

  const submitMfa = async () => {
    if (mfaCode.trim().length < 6) { setErr("Bitte den 6-stelligen Code aus deiner Authenticator-App eingeben."); return; }
    setBusy(true); setErr("");
    const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({ factorId: mfaFactor });
    if (chErr) { setBusy(false); setErr("Verbindung fehlgeschlagen — bitte erneut versuchen."); return; }
    const { error } = await supabase.auth.mfa.verify({ factorId: mfaFactor, challengeId: ch.id, code: mfaCode.trim() });
    setBusy(false);
    if (error) { setErr("Der Code stimmt nicht — bitte neu eingeben."); setMfaCode(""); return; }
    // Erfolg: Session ist jetzt AAL2, der Auth-Listener übernimmt
  };

  return (
    <div style={{position:"relative",width:"100%",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"100vh",fontFamily:"-apple-system,system-ui,sans-serif",overflow:"hidden",padding:"24px 16px",boxSizing:"border-box"}}>
      {/* Weiche Markenflächen im Hintergrund */}
      <div style={{position:"absolute",top:"-140px",right:"-100px",width:420,height:420,borderRadius:"50%",background:"radial-gradient(circle,rgba(53,201,155,0.5),transparent 65%)",filter:"blur(50px)",animation:"dida-blob 9s ease-in-out infinite alternate",pointerEvents:"none"}}/>
      <div style={{position:"absolute",bottom:"-160px",left:"-120px",width:480,height:480,borderRadius:"50%",background:"radial-gradient(circle,rgba(53,201,155,0.32),transparent 65%)",filter:"blur(60px)",animation:"dida-blob 11s ease-in-out infinite alternate-reverse",pointerEvents:"none"}}/>

      <div style={{position:"relative",display:"flex",flexDirection:"column",alignItems:"center",animation:"dida-fade-up .5s ease-out both"}}>
        <DidaLogo size={76}/>
        <div style={{marginTop:14}}><DidaWordmark size={30}/></div>
        <div style={{fontSize:13,color:"var(--mf-muted)",marginTop:6,marginBottom:26}}>deine digitale Praxisassistenz</div>
      </div>

      <div style={{position:"relative",background:"var(--mf-bg)",borderRadius:20,padding:"28px 30px",width:"100%",maxWidth:380,boxSizing:"border-box",boxShadow:"0 20px 60px rgba(0,0,0,0.22), 0 0 0 0.5px rgba(60,60,67,0.10)",animation:"dida-fade-up .5s ease-out .08s both"}}>
        {!mfaFactor ? <>
          <div style={{fontSize:16,fontWeight:600,marginBottom:3,color:"var(--mf-text)"}}>Willkommen zurück</div>
          <div style={{fontSize:12,color:"var(--mf-muted)",marginBottom:18}}>
            {isSupabaseConfigured ? "Melde dich mit deinem Praxis-Konto an." : "Demo-Modus — Anmeldung ohne Passwortprüfung."}
          </div>
          <Input label="E-Mail" value={email} onChange={setEmail} placeholder="name@praxis.de"/>
          <div onKeyDown={e=>e.key==="Enter"&&submit()}>
            <Input label="Passwort" type="password" value={pw} onChange={setPw}/>
          </div>
          {err&&<div style={{fontSize:11,color:"var(--mf-neg)",marginBottom:10}}>{err}</div>}
          <Btn color="g" lg style={{width:"100%",marginTop:4}} onClick={submit}>{busy?"Anmelden…":"Anmelden"}</Btn>
        </> : <>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
            <Picto.Shield size={20}/>
            <div style={{fontSize:16,fontWeight:600,color:"var(--mf-text)"}}>Bestätigungscode</div>
          </div>
          <div style={{fontSize:12,color:"var(--mf-muted)",marginBottom:18}}>Gib den 6-stelligen Code aus deiner Authenticator-App ein.</div>
          <div onKeyDown={e=>e.key==="Enter"&&submitMfa()}>
            <Input label="Code" value={mfaCode} onChange={v=>setMfaCode(v.replace(/\D/g,"").slice(0,6))} placeholder="123456"/>
          </div>
          {err&&<div style={{fontSize:11,color:"var(--mf-neg)",marginBottom:10}}>{err}</div>}
          <Btn color="g" lg style={{width:"100%",marginTop:4}} onClick={submitMfa}>{busy?"Prüfe…":"Bestätigen"}</Btn>
          <Btn lg style={{width:"100%",marginTop:8}} onClick={()=>{ supabase.auth.signOut(); setMfaFactor(null); setMfaCode(""); setErr(""); }}>Zurück zur Anmeldung</Btn>
        </>}
      </div>

      <div style={{position:"relative",display:"flex",alignItems:"center",gap:6,marginTop:22,fontSize:11,color:"var(--mf-muted)",animation:"dida-fade-up .5s ease-out .16s both"}}>
        <Picto.Lock size={14} stroke="var(--mf-muted)"/>
        Verschlüsselte Anmeldung · Server in Frankfurt · DSGVO-konform
      </div>
      {onBack&&<div onClick={onBack} style={{position:"relative",marginTop:10,fontSize:11,color:"var(--mf-muted)",cursor:"pointer",animation:"dida-fade-up .5s ease-out .2s both"}}>← Zurück zur Startseite</div>}
    </div>
  );
}

function PraxisSettings({ onDone }) {
  const [praxis, setPraxis] = useState(getPraxis);
  const [behandler, setBehandler] = useState(getBehandler);
  const [dayCfg, setDayCfg] = useState(()=>{ try{const s=localStorage.getItem("dida_daycfg");if(s)return JSON.parse(s);}catch{} return DEFAULT_DAY_CONFIG; });
  const [neuName, setNeuName] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(()=>{ loadSettings().then(s=>{ setPraxis(s.praxis); if(s.behandler.length) setBehandler(s.behandler); }).catch(()=>{}); },[]);

  const addBeh = () => {
    const name = neuName.trim();
    if(!name) return;
    const id = name.toLowerCase().replace(/[^a-zäöü]/g,"").slice(0,6) + "_" + (behandler.length+1);
    setBehandler(b=>[...b,{ id, name, short: makeShort(name), color: BEHANDLER_PALETTE[b.length % BEHANDLER_PALETTE.length] }]);
    setNeuName("");
  };
  const save = async () => {
    if(!behandler.length){ toast("Mindestens ein Behandler wird benötigt.","r"); return; }
    setBusy(true);
    try {
      await saveSetting("praxis", praxis);
      await saveSetting("behandler", behandler);
      try{ localStorage.setItem("dida_daycfg", JSON.stringify(dayCfg)); }catch{}
      toast("Praxis-Einstellungen gespeichert");
      onDone();
    } catch(e) {
      toast("Speichern fehlgeschlagen — nur der Inhaber darf Praxis-Einstellungen ändern.","r");
    }
    setBusy(false);
  };

  return (<>
    <SLabel>Praxis</SLabel>
    <Input label="Praxisname" value={praxis.name} onChange={v=>setPraxis(p=>({...p,name:v}))} placeholder={DEFAULT_PRAXIS.name} style={{marginBottom:0}}/>
    <Input label="Straße & Nr." value={praxis.strasse} onChange={v=>setPraxis(p=>({...p,strasse:v}))} style={{marginBottom:0}}/>
    <Input label="Praxis-E-Mail (Antwortadresse für Patienten-Mails)" value={praxis.email||""} onChange={v=>setPraxis(p=>({...p,email:v}))} placeholder="info@praxis.de" style={{marginBottom:0}}/>
    <div style={{display:"grid",gridTemplateColumns:"1fr 2fr",gap:"0 10px"}}>
      <Input label="PLZ" value={praxis.plz} onChange={v=>setPraxis(p=>({...p,plz:v}))} style={{marginBottom:0}}/>
      <Input label="Ort" value={praxis.ort} onChange={v=>setPraxis(p=>({...p,ort:v}))} style={{marginBottom:0}}/>
    </div>
    <SLabel>Behandler ({behandler.length})</SLabel>
    {behandler.length===0&&<div style={{fontSize:11,color:"var(--mf-neg)",marginBottom:4}}>Noch keine Behandler — Terminplan, Recall und Auslastung brauchen mindestens einen.</div>}
    {behandler.length>0&&<div style={{fontSize:10,color:"var(--mf-muted)",marginBottom:2}}>Beteiligung (optional): Quote in % und Umsatz-Schwelle je Monat — für „Meine Beteiligung“ im Umsatz-Reiter.</div>}
    {behandler.map((b,i)=>{
      const updBeh = (patch)=>setBehandler(list=>list.map((x,j)=>j===i?{...x,...patch}:x));
      const numInput = {width:70,fontSize:11,padding:"4px 6px",borderRadius:8,border:"1px solid var(--mf-border2)",background:"var(--mf-bg)",color:"var(--mf-text)",fontFamily:"inherit",boxSizing:"border-box"};
      return (
      <div key={b.id} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",borderBottom:"0.5px solid var(--mf-border)",flexWrap:"wrap"}}>
        <span onClick={()=>updBeh({color:BEHANDLER_PALETTE[(BEHANDLER_PALETTE.indexOf(b.color)+1)%BEHANDLER_PALETTE.length]})}
          title="Farbe wechseln" style={{width:16,height:16,borderRadius:"50%",background:b.color,cursor:"pointer",flexShrink:0}}/>
        <span style={{fontSize:12,color:"var(--mf-text)",flex:1,minWidth:110}}>{b.name}</span>
        <span style={{fontSize:10,color:"var(--mf-muted)"}}>{b.short}</span>
        <input type="number" min={0} max={100} value={b.betQuote??""} placeholder="Quote %" title="Beteiligungsquote in %"
          onChange={e=>updBeh({betQuote:e.target.value===""?undefined:Math.min(100,Math.max(0,parseInt(e.target.value)||0))})} style={numInput}/>
        <input type="number" min={0} step={500} value={b.betAb??""} placeholder="ab €/Mon." title="Beteiligung erst ab diesem Monatsumsatz (leer = ab dem ersten Euro)"
          onChange={e=>updBeh({betAb:e.target.value===""?undefined:Math.max(0,parseInt(e.target.value)||0)})} style={{...numInput,width:84}}/>
        <Btn small color="r" onClick={()=>setBehandler(list=>list.filter((_,j)=>j!==i))}>Entfernen</Btn>
      </div>
      );
    })}
    <div style={{display:"flex",gap:8,alignItems:"center",marginTop:6}}>
      <Input value={neuName} onChange={setNeuName} placeholder="z.B. Dr. Anna Weber" style={{flex:1,marginBottom:0}}/>
      <Btn lg onClick={addBeh}>+ Behandler</Btn>
    </div>
    <SLabel>Öffnungszeiten</SLabel>
    {(()=>{
      const dayNames = ["So","Mo","Di","Mi","Do","Fr","Sa"];
      const order = [1,2,3,4,5,6,0];
      const upd = (wd,patch)=>setDayCfg(p=>({ ...p, [wd]: { ...(p[wd]||{start:8,end:18,closed:false}), ...patch } }));
      const hourOpts = Array.from({length:15},(_,i)=>i+6); // 6–20 Uhr
      return (
        <div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:4}}>
          {order.map(wd=>{
            const cfg = dayCfg[wd]||{start:8,end:18,closed:wd===0||wd===6};
            return (
              <div key={wd} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0",borderBottom:"0.5px solid var(--mf-border)"}}>
                <span style={{width:26,fontSize:12,fontWeight:600,color:cfg.closed?"var(--mf-muted)":"var(--mf-text)"}}>{dayNames[wd]}</span>
                {cfg.closed ? (
                  <span style={{flex:1,fontSize:11,color:"var(--mf-muted)"}}>geschlossen</span>
                ) : (
                  <span style={{flex:1,display:"flex",alignItems:"center",gap:5,fontSize:11}}>
                    <select value={cfg.start} onChange={e=>upd(wd,{start:+e.target.value})} style={{fontSize:11,padding:"3px 5px",borderRadius:8,border:"1px solid var(--mf-border2)",background:"var(--mf-bg)",color:"var(--mf-text)",fontFamily:"inherit"}}>
                      {hourOpts.map(h=><option key={h} value={h}>{h}:00</option>)}
                    </select>
                    bis
                    <select value={cfg.end} onChange={e=>upd(wd,{end:+e.target.value})} style={{fontSize:11,padding:"3px 5px",borderRadius:8,border:"1px solid var(--mf-border2)",background:"var(--mf-bg)",color:"var(--mf-text)",fontFamily:"inherit"}}>
                      {hourOpts.map(h=><option key={h} value={h}>{h}:00</option>)}
                    </select>
                  </span>
                )}
                <Toggle small val={!cfg.closed} onChange={v=>upd(wd,{closed:!v})}/>
              </div>
            );
          })}
        </div>
      );
    })()}
    <div style={{display:"flex",gap:10,marginTop:10}}>
      <Btn color="g" lg style={{flex:1}} onClick={save}>{busy?"Speichere…":"Speichern"}</Btn>
      <Btn lg onClick={onDone}>Abbrechen</Btn>
    </div>
    <div style={{fontSize:10,color:"var(--mf-muted)"}}>Behandler und Öffnungszeiten wirken in Terminplan, Import, Recall und Auslastung. Termintypen verwaltest du weiterhin im Terminplan über das Zahnrad.</div>
  </>);
}

// Zwei-Faktor-Authentifizierung (TOTP): einrichten per QR-Code, deaktivieren
// per Klick. Nur im Live-Betrieb — Demo hat keine echten Konten.
function TwoFactorPanel() {
  const [factors, setFactors] = useState(null);
  const [enroll, setEnroll] = useState(null); // { id, qr, secret }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const refresh = () => supabase.auth.mfa.listFactors().then(({ data }) => setFactors(data?.totp || [])).catch(() => setFactors([]));
  useEffect(() => { if (isSupabaseConfigured) refresh(); }, []);

  if (!isSupabaseConfigured) return <Insight tone="a">Zwei-Faktor-Authentifizierung ist nur im Live-Betrieb mit echten Konten verfügbar (aktuell: Demo-Modus).</Insight>;

  const verified = (factors || []).filter(f => f.status === "verified");

  const startEnroll = async () => {
    setBusy(true); setErr("");
    // abgebrochene, nie bestätigte Einrichtungen aufräumen
    for (const f of (factors || []).filter(f => f.status !== "verified")) {
      await supabase.auth.mfa.unenroll({ factorId: f.id }).catch(() => {});
    }
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp", friendlyName: "dida" });
    setBusy(false);
    if (error) { setErr("Einrichtung fehlgeschlagen — bitte erneut versuchen."); return; }
    setEnroll({ id: data.id, qr: data.totp.qr_code, secret: data.totp.secret });
  };

  const confirmEnroll = async () => {
    if (code.trim().length < 6) { setErr("Bitte den 6-stelligen Code aus der App eingeben."); return; }
    setBusy(true); setErr("");
    const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({ factorId: enroll.id });
    if (chErr) { setBusy(false); setErr("Verbindung fehlgeschlagen — bitte erneut versuchen."); return; }
    const { error } = await supabase.auth.mfa.verify({ factorId: enroll.id, challengeId: ch.id, code: code.trim() });
    setBusy(false);
    if (error) { setErr("Der Code stimmt nicht — bitte neu eingeben."); setCode(""); return; }
    toast("Zwei-Faktor-Authentifizierung ist jetzt aktiv");
    setEnroll(null); setCode(""); refresh();
  };

  const disable = async (id) => {
    if (!window.confirm("2FA wirklich deaktivieren? Die Anmeldung ist dann wieder nur mit Passwort möglich.")) return;
    const { error } = await supabase.auth.mfa.unenroll({ factorId: id });
    if (error) toast("Deaktivieren fehlgeschlagen — Verbindung prüfen.", "r");
    else { toast("2FA wurde deaktiviert"); refresh(); }
  };

  if (factors === null) return <div style={{fontSize:11,color:"var(--mf-muted)"}}>Lade Sicherheitseinstellungen…</div>;

  if (verified.length > 0 && !enroll) return <>
    <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",background:"var(--mf-bg-alt)",borderRadius:12}}>
      <Picto.Shield size={26}/>
      <div style={{flex:1}}>
        <div style={{fontSize:12.5,fontWeight:600}}>2FA ist aktiv</div>
        <div style={{fontSize:10.5,color:"var(--mf-muted)"}}>Anmeldung braucht Passwort + Code aus deiner Authenticator-App.</div>
      </div>
    </div>
    <Btn color="r" lg onClick={()=>disable(verified[0].id)}>2FA deaktivieren</Btn>
  </>;

  if (enroll) return <>
    <div style={{fontSize:11.5,color:"var(--mf-muted)",lineHeight:1.5}}>Scanne den QR-Code mit einer Authenticator-App (z.B. Google Authenticator, Apple Passwörter, Authy) und gib danach den angezeigten Code ein.</div>
    <div style={{display:"flex",justifyContent:"center",padding:"6px 0"}}>
      <img src={enroll.qr} alt="2FA QR-Code" style={{width:170,height:170,borderRadius:12,background:"#fff",padding:8,boxSizing:"border-box"}}/>
    </div>
    <div style={{fontSize:10,color:"var(--mf-muted)",textAlign:"center",wordBreak:"break-all"}}>Manuell: <code style={{fontSize:10}}>{enroll.secret}</code></div>
    <div onKeyDown={e=>e.key==="Enter"&&confirmEnroll()}>
      <Input label="6-stelliger Code" value={code} onChange={v=>setCode(v.replace(/\D/g,"").slice(0,6))} placeholder="123456" style={{marginBottom:0}}/>
    </div>
    {err&&<div style={{fontSize:11,color:"var(--mf-neg)"}}>{err}</div>}
    <div style={{display:"flex",gap:10}}>
      <Btn color="g" lg style={{flex:1}} onClick={confirmEnroll}>{busy?"Prüfe…":"Aktivieren"}</Btn>
      <Btn lg onClick={()=>{ supabase.auth.mfa.unenroll({ factorId: enroll.id }).catch(()=>{}); setEnroll(null); setCode(""); setErr(""); }}>Abbrechen</Btn>
    </div>
  </>;

  return <>
    <div style={{fontSize:11.5,color:"var(--mf-muted)",lineHeight:1.5}}>Mit Zwei-Faktor-Authentifizierung braucht die Anmeldung zusätzlich zum Passwort einen 6-stelligen Code aus einer Authenticator-App auf deinem Handy — Schutz auch bei gestohlenem Passwort.</div>
    {err&&<div style={{fontSize:11,color:"var(--mf-neg)"}}>{err}</div>}
    <Btn color="g" lg onClick={startEnroll}>{busy?"Richte ein…":"2FA aktivieren"}</Btn>
  </>;
}

function UserMenuPanel({ role, cfg, userProfile, isAdmin, onLogout, onThemeChange, currentTheme, onSwitchRole, onProfileSaved, narrow }) {
  const displayName = userProfile?.name || cfg.fn;
  const displaySub  = isSupabaseConfigured ? (ROLES_CFG[role]?.fr || role) : cfg.fr;
  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuRect, setMenuRect] = useState(null);
  const [modal, setModal] = useState(null);
  const EMAIL_BY_ROLE = {inhaber:"dr.hoffmann@praxis.de",arzt:"dr.schulz@praxis.de",arzt2:"dr.bergmann@praxis.de",arzt3:"dr.fischer@praxis.de",dh:"maja.roth@praxis.de",mfa:"lisa.beck@praxis.de"};
  const [profile, setProfile] = useState({name:"", email:"", phone:""});
  const [profileBusy, setProfileBusy] = useState(false);
  const [notifs, setNotifs] = useState({email:true, app:true, termine:true, news:false});
  const [appCfg, setAppCfg] = useState({sprache:"Deutsch", datumformat:"DD.MM.YYYY", timezone:"Europe/Berlin"});
  const [newUser, setNewUser] = useState({name:"", rolle:"arzt", email:"", pw:""});
  const [passwd, setPasswd] = useState({alt:"", neu:"", wieder:""});
  const [pwSaved, setPwSaved] = useState(false);
  const [pwErr, setPwErr] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [userSaved, setUserSaved] = useState(false);
  const [userErr, setUserErr] = useState("");
  const [userBusy, setUserBusy] = useState(false);
  const [profiles, setProfiles] = useState(null);

  // Nutzerliste laden, wenn die Nutzerverwaltung geöffnet wird (Inhaber sieht alle per RLS)
  useEffect(() => {
    if (modal !== "neuernutzer" || !isSupabaseConfigured) return;
    supabase.from("profiles").select("id,name,role").order("name")
      .then(({ data }) => setProfiles(data || []));
  }, [modal]);

  // Profil-Formular mit den echten Kontodaten füllen, wenn es geöffnet wird
  // (früher standen hier die Demo-Rollennamen wie "Lisa Beck")
  useEffect(() => {
    if (modal !== "profil") return;
    let saved = {};
    try { const s = localStorage.getItem("dida_profile"); if (s) saved = JSON.parse(s); } catch {}
    setProfile({
      name: userProfile?.name || saved.name || cfg.fn,
      email: userProfile?.email || saved.email || EMAIL_BY_ROLE[role] || "info@praxis.de",
      phone: saved.phone || "",
    });
  }, [modal]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveProfile = async () => {
    const name = profile.name.trim();
    if (!name) { toast("Der Name darf nicht leer sein.", "r"); return; }
    setProfileBusy(true);
    try {
      if (isSupabaseConfigured) {
        const { data: { user } } = await supabase.auth.getUser();
        const { error } = await supabase.from("profiles").update({ name }).eq("id", user.id);
        if (error) throw error;
      }
      try { localStorage.setItem("dida_profile", JSON.stringify({ name, email: profile.email, phone: profile.phone })); } catch {}
      onProfileSaved?.(name);
      toast("Profil gespeichert");
      setModal(null);
    } catch {
      toast("Profil konnte nicht gespeichert werden — Verbindung prüfen.", "r");
    }
    setProfileBusy(false);
  };

  const changeName = async (id, name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return;
    const before = profiles;
    setProfiles(p => p.map(x => x.id === id ? { ...x, name: trimmed } : x)); // optimistisch
    const { error } = await supabase.from("profiles").update({ name: trimmed }).eq("id", id);
    if (error) { setProfiles(before); toast("Name konnte nicht geändert werden — nur der Inhaber darf das.", "r"); }
    else toast("Name aktualisiert");
  };

  const changeRole = async (id, role) => {
    const before = profiles;
    setProfiles(p => p.map(x => x.id === id ? { ...x, role } : x)); // optimistisch
    const { error } = await supabase.from("profiles").update({ role }).eq("id", id);
    if (error) { setProfiles(before); toast("Rolle konnte nicht geändert werden — nur der Inhaber darf das.", "r"); }
    else toast("Rolle aktualisiert");
  };

  const createUser = async () => {
    setUserErr("");
    if (!newUser.email.includes("@")) { setUserErr("Bitte eine gültige E-Mail angeben."); return; }
    if ((newUser.pw || "").length < 8) { setUserErr("Das Passwort braucht mindestens 8 Zeichen."); return; }
    if (!isSupabaseConfigured) { // Demo: nur Show
      setUserSaved(true);
      setTimeout(() => { setUserSaved(false); setModal(null); setNewUser({ name: "", rolle: "arzt", email: "", pw: "" }); }, 2000);
      return;
    }
    setUserBusy(true);
    const { data, error } = await supabase.functions.invoke("create-user", {
      body: { email: newUser.email.trim(), password: newUser.pw, name: newUser.name.trim(), rolle: newUser.rolle },
    });
    setUserBusy(false);
    if (error) {
      // Bei 4xx/5xx versteckt supabase-js die Antwort in error.context — echten Grund auslesen
      let msg = "";
      try { const body = await error.context?.json?.(); msg = body?.error || ""; } catch {}
      setUserErr(msg || `Anlegen fehlgeschlagen (${error.message || "Function nicht erreichbar"}) — ist create-user deployt?`);
      return;
    }
    if (data?.error) { setUserErr(data.error); return; }
    setUserSaved(true);
    setNewUser({ name: "", rolle: "arzt", email: "", pw: "" });
    supabase.from("profiles").select("id,name,role").order("name").then(({ data: d }) => setProfiles(d || []));
    setTimeout(() => setUserSaved(false), 3000);
  };

  const changePassword = async () => {
    setPwErr("");
    if (passwd.neu !== passwd.wieder) { setPwErr("Die neuen Passwörter stimmen nicht überein."); return; }
    if ((passwd.neu || "").length < 8) { setPwErr("Das neue Passwort braucht mindestens 8 Zeichen."); return; }
    if (!isSupabaseConfigured) { // Demo: nur Show
      setPasswd({ alt: "", neu: "", wieder: "" }); setPwSaved(true);
      setTimeout(() => { setPwSaved(false); setModal(null); }, 1500);
      return;
    }
    setPwBusy(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      // aktuelles Passwort verifizieren, bevor geändert wird
      const { error: verifyErr } = await supabase.auth.signInWithPassword({ email: user.email, password: passwd.alt });
      if (verifyErr) { setPwErr("Das aktuelle Passwort ist falsch."); setPwBusy(false); return; }
      const { error } = await supabase.auth.updateUser({ password: passwd.neu });
      if (error) { setPwErr(error.message); setPwBusy(false); return; }
      setPasswd({ alt: "", neu: "", wieder: "" }); setPwSaved(true);
      setTimeout(() => { setPwSaved(false); setModal(null); }, 1500);
    } catch (e) {
      setPwErr("Änderung fehlgeschlagen — Verbindung prüfen.");
    }
    setPwBusy(false);
  };

  const openModal = m => { setModal(m); setMenuOpen(false); };

  const MENU_ITEMS = [
    {id:"profil",     icon:"ti-user",        label:"Profil bearbeiten"},
    {id:"passwort",   icon:"ti-lock",        label:"Passwort ändern"},
    {id:"zweifaktor", icon:"ti-shield-lock", label:"Zwei-Faktor (2FA)"},
    ...(isAdmin?[{id:"praxis",icon:"ti-building",label:"Praxis & Behandler"},{id:"neuernutzer",icon:"ti-user-plus",label:"Nutzerverwaltung"}]:[]),
    {id:"notifs",     icon:"ti-bell",        label:"Benachrichtigungen"},
    {id:"settings",   icon:"ti-settings-2",  label:"App-Einstellungen"},
  ];


  const t = THEMES[useTheme()];
  return (
    <div style={{position:"relative"}}>
      <div ref={menuRef} onClick={()=>{ if(!menuOpen&&menuRef.current) setMenuRect(menuRef.current.getBoundingClientRect()); setMenuOpen(p=>!p); }} style={{display:"flex",alignItems:"center",gap:7,padding:narrow?"5px 0":"5px 4px",justifyContent:narrow?"center":"flex-start",cursor:"pointer",borderRadius:5,background:t.navActiveBg,border:`0.5px solid ${t.divider}`}} title={narrow?`${cfg.fn} — Menü`:undefined}>
        <div style={{width:26,height:26,borderRadius:"50%",background:cfg.favBg,color:cfg.favCol,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:500,flexShrink:0}}>{isSupabaseConfigured?ini(displayName):cfg.fav}</div>
        {!narrow&&<>
        <div>
          <div style={{fontSize:11,fontWeight:500,color:t.textPrimary}}>{displayName}</div>
          <div style={{fontSize:9,color:t.textSecondary}}>{displaySub}</div>
        </div>
        <i className={`ti ti-chevron-${menuOpen?"up":"down"}`} style={{marginLeft:"auto",fontSize:12,color:t.textSecondary}}/>
        </>}
      </div>

      {menuOpen&&menuRect&&<Portal>
        <div onClick={()=>setMenuOpen(false)} style={{position:"fixed",inset:0,zIndex:Z.menuOverlay}}/>
        <div style={{position:"fixed",left:menuRect.left,width:Math.max(210,menuRect.width),bottom:`calc(100vh - ${menuRect.top}px + 6px)`,background:t.cardBg,backdropFilter:t.blur,WebkitBackdropFilter:t.blur,border:`0.5px solid ${t.divider}`,borderRadius:8,zIndex:Z.menu,boxShadow:"0 4px 20px rgba(0,0,0,.2)",overflow:"hidden"}}>
          {!onSwitchRole&&<div style={{padding:"9px 12px",borderBottom:`0.5px solid ${t.divider}`}}>
            <div style={{fontSize:11,fontWeight:600,color:t.textPrimary}}>{displayName}</div>
            <div style={{fontSize:9.5,color:t.textSecondary,marginTop:1}}>{userProfile?.email||""}</div>
            <div style={{fontSize:9.5,color:t.textSecondary}}>Rolle: {ROLES_CFG[role]?.fr||role}</div>
          </div>}
          {onSwitchRole&&<><div style={{padding:"7px 12px 3px",fontSize:8.5,fontWeight:600,letterSpacing:.5,color:t.navLabel,textTransform:"uppercase"}}>Benutzer wechseln (Demo)</div>
          <div style={{maxHeight:168,overflowY:"auto"}}>
            {Object.entries(ROLES_CFG).map(([k,c])=>(
              <div key={k} onClick={()=>{ if(k!==role&&onSwitchRole) onSwitchRole(k); setMenuOpen(false); }}
                style={{display:"flex",alignItems:"center",gap:7,padding:"5px 12px",cursor:"pointer",background:role===k?t.inputBg:"transparent"}}
                onMouseEnter={e=>e.currentTarget.style.background=t.inputBg} onMouseLeave={e=>e.currentTarget.style.background=role===k?t.inputBg:"transparent"}>
                <div style={{width:18,height:18,borderRadius:"50%",background:c.favBg,color:c.favCol,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:500,flexShrink:0}}>{c.fav}</div>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{fontSize:10.5,fontWeight:role===k?600:400,color:t.textPrimary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.fn}</div>
                  <div style={{fontSize:8.5,color:t.textSecondary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.fr}</div>
                </div>
                {role===k&&<i className="ti ti-check" style={{fontSize:11,color:"var(--mf-primary)",flexShrink:0}}/>}
              </div>
            ))}
          </div>
          <div style={{borderTop:`0.5px solid ${t.divider}`,margin:"2px 0"}}/></>}
          {MENU_ITEMS.map(item=>(
            <div key={item.id} onClick={()=>openModal(item.id)} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",fontSize:11,cursor:"pointer",color:t.textPrimary}} onMouseEnter={e=>e.currentTarget.style.background=t.inputBg} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <i className={`ti ${item.icon}`} style={{fontSize:13,color:t.textSecondary}}/>{item.label}
            </div>
          ))}
          <div style={{borderTop:`0.5px solid ${t.divider}`,margin:"2px 0"}}/>
          <div onClick={()=>openModal("abmelden")} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",fontSize:11,cursor:"pointer",color:"#791F1F"}} onMouseEnter={e=>e.currentTarget.style.background="#FFF5F5"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <i className="ti ti-logout" style={{fontSize:13}}/>Abmelden
          </div>
        </div>
      </Portal>}

      {/* MODALS */}
      {modal&&modal!=="abmelden"&&<Portal>
        <div onClick={()=>setModal(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay3}}/>
        <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:420,maxHeight:"80vh",maxWidth:"96vw",background:"var(--mf-bg)",borderRadius:14,zIndex:Z.modal3,display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 64px rgba(0,0,0,.3)"}}>
          <div style={{padding:"14px 18px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",flexShrink:0}}>
            <span style={{fontSize:14,fontWeight:600,flex:1}}>
              {{profil:"Profil bearbeiten",passwort:"Passwort ändern",zweifaktor:"Zwei-Faktor-Authentifizierung",praxis:"Praxis & Behandler",neuernutzer:"Nutzerverwaltung",notifs:"Benachrichtigungen",settings:"App-Einstellungen"}[modal]}
            </span>
            <div onClick={()=>setModal(null)} style={{cursor:"pointer",fontSize:20,color:"var(--mf-muted)",width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",background:"var(--mf-bg-alt)"}}>×</div>
          </div>
          <div style={{padding:"16px 18px",overflowY:"auto",display:"flex",flexDirection:"column",gap:12}}>

            {modal==="praxis"&&<PraxisSettings onDone={()=>setModal(null)}/>}

            {modal==="profil"&&<>
              <Input label="Name" value={profile.name} onChange={v=>setProfile(p=>({...p,name:v}))}/>
              {isSupabaseConfigured
                ? <Input label="E-Mail" value={profile.email} disabled hint="Deine Anmelde-E-Mail — Änderung über die Nutzerverwaltung."/>
                : <Input label="E-Mail" value={profile.email} onChange={v=>setProfile(p=>({...p,email:v}))}/>}
              <Input label="Telefon" value={profile.phone} onChange={v=>setProfile(p=>({...p,phone:v}))}/>
              <Input label="Rolle" value={isSupabaseConfigured?(ROLES_CFG[role]?.fr||role):cfg.fr} disabled/>
              <div style={{display:"flex",gap:10}}><Btn color="g" lg style={{flex:1}} onClick={saveProfile}>{profileBusy?"Speichere…":"Speichern"}</Btn><Btn lg onClick={()=>setModal(null)}>Abbrechen</Btn></div>
            </>}

            {modal==="zweifaktor"&&<TwoFactorPanel/>}

            {modal==="passwort"&&<>
              {[["alt","Aktuelles Passwort"],["neu","Neues Passwort"],["wieder","Neues Passwort (wiederholen)"]].map(([k,l])=>(
                <Input key={k} type="password" label={l} value={passwd[k]} onChange={v=>setPasswd(p=>({...p,[k]:v}))} style={{marginBottom:0}}/>
              ))}
              {pwErr&&<div style={{fontSize:11,color:"var(--mf-neg)"}}>{pwErr}</div>}
              {pwSaved&&<div style={{fontSize:11,color:"var(--mf-primary)",background:"#E1F5EE",padding:"7px 10px",borderRadius:6}}>✓ Passwort wurde erfolgreich geändert</div>}
              <div style={{display:"flex",gap:10}}><Btn color="g" lg style={{flex:1}} onClick={changePassword}>{pwBusy?"Ändere…":"Speichern"}</Btn><Btn lg onClick={()=>setModal(null)}>Abbrechen</Btn></div>
            </>}

            {modal==="neuernutzer"&&<>
              {isSupabaseConfigured&&<>
                <SLabel>Vorhandene Nutzer</SLabel>
                {profiles===null&&<div style={{fontSize:11,color:"var(--mf-muted)"}}>Lade Nutzer…</div>}
                {profiles&&profiles.length===0&&<div style={{fontSize:11,color:"var(--mf-muted)"}}>Keine Nutzer sichtbar.</div>}
                {(profiles||[]).map(pr=>(
                  <div key={pr.id} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",borderBottom:"0.5px solid var(--mf-border)"}}>
                    <Av text={ini(pr.name||"?")} bg="rgba(53,201,155,0.16)" col="var(--mf-primary)" size={24}/>
                    <input defaultValue={pr.name||""} placeholder="(ohne Name)" title="Name ändern — Enter oder Klick daneben speichert"
                      onBlur={e=>{ if(e.target.value.trim()&&e.target.value.trim()!==(pr.name||"")) changeName(pr.id,e.target.value); }}
                      onKeyDown={e=>{ if(e.key==="Enter") e.target.blur(); }}
                      style={{fontSize:12,flex:1,minWidth:0,border:"none",outline:"none",background:"transparent",color:"var(--mf-text)",fontFamily:"inherit",padding:"4px 2px",borderBottom:"1px dashed transparent"}}
                      onFocus={e=>e.target.style.borderBottom="1px dashed var(--mf-border2)"}
                      onBlurCapture={e=>e.target.style.borderBottom="1px dashed transparent"}/>
                    <Select value={pr.role} onChange={v=>changeRole(pr.id,v)}
                      options={[{value:"inhaber",label:"Inhaber:in"},{value:"arzt",label:"Zahnarzt/Zahnärztin"},{value:"dh",label:"ZMP/DH"},{value:"mfa",label:"MFA / ZFA"}]}
                      style={{marginBottom:0,width:150}}/>
                  </div>
                ))}
                <div style={{fontSize:10,color:"var(--mf-muted)"}}>Tipp: Namen direkt in der Liste anklicken und ändern — Enter speichert.</div>
                <SLabel>Neuen Nutzer anlegen</SLabel>
              </>}
              <Input label="Name" value={newUser.name} onChange={v=>setNewUser(p=>({...p,name:v}))} placeholder="Vor- und Nachname" style={{marginBottom:0}}/>
              <Select label="Rolle" value={newUser.rolle} onChange={v=>setNewUser(p=>({...p,rolle:v}))} options={[{value:"arzt",label:"Zahnarzt/Zahnärztin"},{value:"dh",label:"ZMP/Dentalhygienikerin"},{value:"mfa",label:"MFA / ZFA"},{value:"inhaber",label:"Inhaber:in"}]} style={{marginBottom:0}}/>
              <Input label="E-Mail" value={newUser.email} onChange={v=>setNewUser(p=>({...p,email:v}))} placeholder="name@praxis.de" style={{marginBottom:0}}/>
              <Input label="Start-Passwort" value={newUser.pw} onChange={v=>setNewUser(p=>({...p,pw:v}))} placeholder="Mindestens 8 Zeichen" hint="Bitte persönlich übergeben — die Person kann es unter „Passwort ändern“ selbst neu setzen." style={{marginBottom:0}}/>
              {userErr&&<div style={{fontSize:11,color:"var(--mf-neg)"}}>{userErr}</div>}
              {userSaved&&<div style={{fontSize:11,color:"var(--mf-primary)",background:"#E1F5EE",padding:"7px 10px",borderRadius:6}}>✓ Nutzer wurde angelegt — Anmeldung ab sofort möglich</div>}
              <div style={{display:"flex",gap:10}}><Btn color="g" lg style={{flex:1}} onClick={createUser}>{userBusy?"Lege an…":"Nutzer anlegen"}</Btn><Btn lg onClick={()=>setModal(null)}>Abbrechen</Btn></div>
            </>}

            {modal==="notifs"&&<>
              {[["email","E-Mail-Benachrichtigungen","Bei neuen Ereignissen per E-Mail"],["app","App-Benachrichtigungen","Push-Nachrichten im Browser"],["termine","Terminerinnerungen","24h vor jedem Termin"],["news","Newsletter-Bestätigung","Versandbestätigung nach Kampagne"]].map(([k,l,d])=>(
                <div key={k} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",background:"var(--mf-bg-alt)",borderRadius:6}}>
                  <div><div style={{fontSize:12,fontWeight:500}}>{l}</div><div style={{fontSize:10,color:"var(--mf-muted)"}}>{d}</div></div>
                  <Toggle small val={notifs[k]} onChange={v=>setNotifs(p=>({...p,[k]:v}))}/>
                </div>
              ))}
              <Btn color="g" lg onClick={()=>setModal(null)}>Speichern</Btn>
            </>}

            {modal==="settings"&&<>
              <Select label="Sprache" value={appCfg.sprache} onChange={v=>setAppCfg(p=>({...p,sprache:v}))} options={["Deutsch","English"]} style={{marginBottom:0}}/>
              <Select label="Datumsformat" value={appCfg.datumformat} onChange={v=>setAppCfg(p=>({...p,datumformat:v}))} options={["DD.MM.YYYY","MM/DD/YYYY","YYYY-MM-DD"]} style={{marginBottom:0}}/>
              <Select label="Zeitzone" value={appCfg.timezone} onChange={v=>setAppCfg(p=>({...p,timezone:v}))} options={["Europe/Berlin","Europe/Vienna","Europe/Zurich","UTC"]} style={{marginBottom:0}}/>
              <div>
                <label style={{fontSize:10,color:"var(--mf-muted)",display:"block",marginBottom:5}}>Design</label>
                <SegControl value={currentTheme==="dark"?1:0} onChange={i=>{ if(onThemeChange) onThemeChange(i===1?"dark":"glass"); }} options={["Hell","Dunkel"]}/>
              </div>
              <div>
                <label style={{fontSize:10,color:"var(--mf-muted)",display:"block",marginBottom:5}}>Demo-Daten</label>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <Btn onClick={()=>{ if(window.confirm("Demo-Daten laden? Bestehende lokale Einträge (Termine, HKPs, Recalls …) werden mit Beispieldaten überschrieben.")){ loadDemoData(true); window.location.reload(); } }}>Demo-Daten laden</Btn>
                  {isSupabaseConfigured&&<Btn color="r" onClick={async()=>{
                    if(!window.confirm("Wirklich ALLE Fachdaten aus der Datenbank löschen (Patienten, Termine, HKPs, Recalls, Rezepte, Bewertungen, Rechnungen, Team, Chat, Statistiken)? Nutzerkonten und Praxis-Einstellungen bleiben erhalten.")) return;
                    if(!window.confirm("Letzte Sicherheitsfrage: Das kann nicht rückgängig gemacht werden. Fortfahren?")) return;
                    try {
                      for (const [table,col] of [["patients","id"],["hkps","id"],["recalls","id"],["apts","id"],["reqs","id"],["reviews","id"],["invoices","id"],["team_members","id"],["chat","id"]]) {
                        const { error } = await supabase.from(table).delete().gte(col, 0);
                        if (error) throw error;
                      }
                      const { error: e2 } = await supabase.from("stats").delete().neq("typ","");
                      if (e2) throw e2;
                      try{ Object.keys(localStorage).filter(k=>k.startsWith("dida_")&&k!=="dida_theme").forEach(k=>localStorage.removeItem(k)); localStorage.setItem("dida_clean","1"); }catch{}
                      toast("Datenbank geleert — dida startet neu");
                      setTimeout(()=>window.location.reload(), 900);
                    } catch { toast("Löschen fehlgeschlagen — nur der Inhaber darf das (RLS).","r"); }
                  }}>Datenbank-Testdaten löschen</Btn>}
                  <Btn color="r" onClick={()=>{ if(window.confirm("Alle lokalen dida-Daten löschen und ohne Demo-Daten neu starten (Pilotmodus)? Daten in der Datenbank bleiben erhalten — Theme-Einstellung bleibt.")){
                    try{ Object.keys(localStorage).filter(k=>k.startsWith("dida_")&&k!=="dida_theme").forEach(k=>localStorage.removeItem(k)); }catch{}
                    try{ localStorage.setItem("dida_clean","1"); }catch{}
                    window.location.reload();
                  } }}>Daten löschen (Pilotmodus)</Btn>
                </div>
                <div style={{fontSize:10,color:"var(--mf-muted)",marginTop:5}}>Zum Arbeiten mit Beispielzahlen — alle Kennzahlen werden aus den geladenen Daten berechnet.</div>
              </div>
              <Btn color="g" lg onClick={()=>setModal(null)}>Speichern</Btn>
            </>}
          </div>
        </div>
      </Portal>}

      {modal==="abmelden"&&<Portal>
        <div onClick={()=>setModal(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay3}}/>
        <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:340,background:"var(--mf-bg)",borderRadius:14,zIndex:Z.modal3,overflow:"hidden",boxShadow:"0 24px 64px rgba(0,0,0,.3)"}}>
          <div style={{padding:"20px 20px 16px"}}>
            <div style={{fontSize:14,fontWeight:600,marginBottom:6}}>Abmelden?</div>
            <div style={{fontSize:12,color:"var(--mf-muted)",marginBottom:18}}>Ihre Sitzung wird beendet. Alle lokal gespeicherten Daten (Termine, Patienten, Notizen) werden gelöscht.</div>
            <div style={{display:"flex",gap:8}}>
              <Btn color="r" onClick={onLogout}>Ja, abmelden</Btn>
              <Btn onClick={()=>setModal(null)}>Abbrechen</Btn>
            </div>
          </div>
        </div>
      </Portal>}
    </div>
  );
}

export default function Dida() {
  // Demo-Daten einmalig vor dem ersten Render seeden (statt im Module-Scope)
  // Demo-Seeding nur im DEMO-Modus — im Live-Betrieb startet dida mit leeren
  // Modulen (Clean Slate); Demo-Daten sind über die App-Einstellungen ladbar.
  useState(() => { try { migrateLegacyStorage(); if (demoFallbacks()) loadDemoData(); } catch(e) { console.warn("Demo-Daten:", e); } return true; });
  // Settings + Kalender-Abos im Hintergrund laden — Fehler stören den Start nicht
  useEffect(() => { loadSettings().catch(() => {}); syncAllFeeds().catch(() => {}); }, []);


  // Responsive: <1000px Icon-Sidebar (iPad), <640px Bottom-Tab-Bar (Handy)
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.innerWidth < 1000);
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => {
    const onResize = () => { setNarrow(window.innerWidth < 1000); setMobile(window.innerWidth < 640); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // LIVE-Modus: ausgeloggt starten, Session via Supabase; DEMO: direkt drin
  const [loggedIn, setLoggedIn] = useState(!isSupabaseConfigured);
  // LIVE: mit der niedrigsten Rolle starten, bis das Profil geladen ist (Least Privilege)
  const [role, setRole] = useState(isSupabaseConfigured ? "mfa" : "inhaber");
  // LIVE: {name,email} aus dem Profil · DEMO: gespeicherte Profil-Änderungen
  const [userProfile, setUserProfile] = useState(() => {
    try { const s = localStorage.getItem("dida_profile"); if (s) return JSON.parse(s); } catch {}
    return null;
  });
  const [praxisSetupOpen, setPraxisSetupOpen] = useState(false); // Schnellzugriff aus "Erste Schritte"
  const [showLogin, setShowLogin] = useState(false); // ausgeloggt: Landingpage vs. Login
  const [introActive, setIntroActive] = useState(false); // Schreibschrift-Übergang nach Login
  useEffect(() => {
    if (!loggedIn) return;
    try {
      if (sessionStorage.getItem("dida_login_intro") === "1") {
        sessionStorage.removeItem("dida_login_intro");
        setIntroActive(true);
      }
    } catch {}
  }, [loggedIn]);
// Datenbank-Vollständigkeit prüfen: fehlende Schema-Skripte sichtbar machen
  useEffect(() => {
    if (!loggedIn || !isSupabaseConfigured) return;
    checkSchema().then(missing => {
      if (missing.length) toast(`Datenbank unvollständig — bitte im Supabase SQL Editor ausführen: ${missing.join(", ")}`, "r");
    }).catch(() => {});
  }, [loggedIn]);


  useEffect(() => {
    if (!isSupabaseConfigured) return;
    const loadRole = async (session) => {
      const { data } = await supabase.from("profiles").select("role,name").eq("id", session.user.id).single();
      if (data?.role && ROLES_CFG[data.role]) setRole(data.role);
      setUserProfile({ name: data?.name || session.user.email.split("@")[0], email: session.user.email });
    };
    // Bei aktivierter 2FA zählt die Anmeldung erst nach dem Code (AAL2) —
    // sonst wäre die App schon nach Passwort-Eingabe offen.
    const applySession = async (session) => {
      if (!session) { setLoggedIn(false); return; }
      try {
        const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (aal && aal.nextLevel === "aal2" && aal.currentLevel !== "aal2") { setLoggedIn(false); return; }
      } catch {}
      setLoggedIn(true);
      loadRole(session);
    };
    supabase.auth.getSession().then(({ data: { session } }) => applySession(session));
    // WICHTIG: nicht direkt im Callback arbeiten — supabase-js hält während
    // onAuthStateChange einen internen Auth-Lock; der MFA-Check in
    // applySession würde deadlocken (Symptom: Login erst nach Reload).
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setTimeout(() => applySession(session), 0);
    });
    return () => subscription.unsubscribe();
  }, []);
  const [screen, setScreen] = useState("dashboard");
  const [navDrill, setNavDrill] = useState(null);              // Sidebar-Drilldown (z.B. "tracking")
  const [trackingTab, setTrackingTab] = useState("uebersicht"); // aktiver Tracking-Unterreiter
  // Betritt man Tracking (auch per Dashboard-Kachel), klappt die Sidebar in die
  // Tracking-Unterreiter auf; verlässt man es, zurück zur Haupt-Navigation.
  useEffect(()=>{ setNavDrill(screen==="tracking"?"tracking":null); },[screen]);
  const [terminePreset, setTerminePreset] = useState(null);
  const [rezeptePreset, setRezeptePreset] = useState(null);
  const [mailPreset, setMailPreset] = useState(null);
  const [themeMode, setThemeMode] = useState(() => {
    try { return localStorage.getItem("dida_theme") || "classic"; } catch { return "classic"; }
  });
  const setTheme = (m) => {
    setThemeMode(m);
    try { localStorage.setItem("dida_theme", m); } catch {}
  };
  const t = THEMES[themeMode];
  const cfg = ROLES_CFG[role];

  const NAV = [
    { key:"dashboard",   icon:"ti-layout-dashboard", label:"Dashboard",        roles:["inhaber","arzt","arzt2","arzt3","dh","mfa"] },
    { key:"termine",     icon:"ti-calendar",          label:"Terminplan",        roles:["inhaber","arzt","arzt2","arzt3","dh","mfa"] },
    { key:"patienten",   icon:"ti-users",             label:"Patienten",         roles:["inhaber","arzt","arzt2","arzt3","dh","mfa"] },
    { key:"rezepte",     icon:"ti-prescription",      label:"Rezepte",           roles:["inhaber","arzt","arzt2","arzt3","mfa"] },
    { key:"umsatz",      icon:"ti-chart-bar",         label:"Umsatz & Kosten",   roles:["inhaber","arzt","arzt2","arzt3","dh"] },
    { key:"abrechnung",  icon:"ti-building-bank",     label:"Abrechnung",        roles:["inhaber","mfa"] },
    { key:"mitarbeiter", icon:"ti-users-group",       label:"Mitarbeiter",       roles:["inhaber"] },
    { key:"import",      icon:"ti-file-import",       label:"Import",            roles:["inhaber","mfa"] },
    { key:"newsletter",  icon:"ti-mail",              label:"Mail & Newsletter", roles:["inhaber","mfa"] },
    { key:"tracking",    icon:"ti-chart-dots",        label:"Tracking",          roles:["inhaber","arzt","arzt2","arzt3","dh"] },
  ];

  const sections = [
    { label:"Praxis", keys:["dashboard","termine","patienten","rezepte"] },
    { label:"Verwaltung", keys:["umsatz","abrechnung","mitarbeiter","import"] },
    { label:"Kommunikation", keys:["newsletter"] },
    { label:"Analyse", keys:["tracking"] },
  ];

  // Tracking-Unterreiter — erscheinen beim Drilldown in der Seitenleiste
  const TRACKING_TABS = [
    { k:"uebersicht", l:"Übersicht",   icon:"ti-layout-grid" },
    { k:"hkp",        l:"HKP-Manager", icon:"ti-file-check" },
    { k:"recall",     l:"Recall",      icon:"ti-bell-ringing" },
    { k:"scorecard",  l:"Scorecard",   icon:"ti-target" },
    { k:"feedback",   l:"Feedback",    icon:"ti-star" },
  ];

  const navStyle = (key) => ({
    display:"flex", alignItems:"center", gap:7, padding:"6px 8px", borderRadius:5,
    margin:"1px 5px", cursor:"pointer", fontSize:12,
    color: screen===key ? t.textPrimary : t.textSecondary,
    background: screen===key ? t.navActiveBg : "transparent",
    fontWeight: screen===key ? 500 : 400,
    backdropFilter: screen===key ? t.blur : "none",
    WebkitBackdropFilter: screen===key ? t.blur : "none",
  });

  const handleLogout = () => {
    if (isSupabaseConfigured) { supabase.auth.signOut(); }  // Listener setzt loggedIn
    else setLoggedIn(false);
  };

  if(!loggedIn) return (
    <ThemeCtx.Provider value={themeMode}>
      <div data-theme={themeMode} style={{background:t.appBg,minHeight:"100vh",transition:"background 0.4s"}}>
        {showLogin
          ? <LoginScreen onLogin={()=>setLoggedIn(true)} onBack={()=>setShowLogin(false)}/>
          : <LandingPage onLogin={()=>setShowLogin(true)}/>}
      </div>
    </ThemeCtx.Provider>
  );

  return (
    <ThemeCtx.Provider value={themeMode}>
    {introActive&&<IntroSplash onDone={()=>setIntroActive(false)}/>}
    <ToastHost/>
    <div data-theme={themeMode} style={{background:t.appBg,padding:t.appPad,minHeight:"100vh",boxSizing:"border-box",transition:"background 0.4s"}}>
    <div style={{display:"grid",gridTemplateColumns:mobile?"1fr":narrow?"56px 1fr":"186px 1fr",border:t.outerBorder,borderRadius:t.outerRadius,overflow:"hidden",background:t.outerBg,backdropFilter:t.blur,WebkitBackdropFilter:t.blur,position:"relative",minHeight:720,fontFamily:"system-ui, sans-serif"}}>

      {/* ── SIDEBAR (ab Tablet) ── */}
      {!mobile&&<div style={{background:t.sidebarBg,backdropFilter:t.blur,WebkitBackdropFilter:t.blur,borderRight:t.sidebarBorder,display:"flex",flexDirection:"column"}}>

        {/* Logo */}
        <div style={{padding:"12px",borderBottom:`0.5px solid ${t.divider}`,display:"flex",alignItems:"center",gap:7,justifyContent:narrow?"center":"flex-start"}}>
          <DidaLogo size={26}/>
          {!narrow&&<DidaWordmark size={14} color={t.textPrimary}/>}
        </div>

        {/* Nav — normal, oder Drilldown in die Tracking-Unterreiter */}
        <div style={{flex:1,padding:"3px 0",overflowY:"auto"}}>
          {(navDrill==="tracking"&&screen==="tracking") ? (
            <>
              <div onClick={()=>setNavDrill(null)} title="Zurück" style={{...navStyle("__back__"),...(narrow?{justifyContent:"center",padding:"8px 0"}:{})}}>
                <i className="ti ti-arrow-left" style={{fontSize:narrow?16:14,flexShrink:0}}/>
                {!narrow&&"Zurück"}
              </div>
              {!narrow&&<div style={{padding:"8px 10px 2px",fontSize:9,fontWeight:500,letterSpacing:.5,color:t.navLabel,textTransform:"uppercase"}}>Tracking</div>}
              {TRACKING_TABS.map(tb=>{
                const active=trackingTab===tb.k;
                return (
                  <div key={tb.k} onClick={()=>setTrackingTab(tb.k)} title={tb.l} style={{
                    display:"flex",alignItems:"center",gap:7,padding:narrow?"8px 0":"6px 8px",borderRadius:5,margin:"1px 5px",cursor:"pointer",fontSize:12,
                    justifyContent:narrow?"center":"flex-start",
                    color:active?t.textPrimary:t.textSecondary,background:active?t.navActiveBg:"transparent",fontWeight:active?500:400,
                    backdropFilter:active?t.blur:"none",WebkitBackdropFilter:active?t.blur:"none"}}>
                    <i className={`ti ${tb.icon}`} style={{fontSize:narrow?16:14,flexShrink:0,color:active?"var(--mf-primary)":"inherit"}}/>
                    {!narrow&&tb.l}
                  </div>
                );
              })}
            </>
          ) : (
            sections.map(sec=>{
            const visible = NAV.filter(n=>sec.keys.includes(n.key)&&n.roles.includes(role));
            if(!visible.length) return null;
            return (
              <div key={sec.label}>
                {!narrow&&<div style={{padding:"8px 10px 2px",fontSize:9,fontWeight:500,letterSpacing:.5,color:t.navLabel,textTransform:"uppercase"}}>{sec.label}</div>}
                {visible.map(n=>(
                  <div key={n.key} onClick={()=>{setScreen(n.key);setNavDrill(n.key==="tracking"?"tracking":null);}} title={n.label} style={{...navStyle(n.key),...(narrow?{justifyContent:"center",padding:"8px 0"}:{})}}>
                    <i className={`ti ${n.icon}`} style={{fontSize:narrow?16:14,flexShrink:0,color:screen===n.key?"var(--mf-primary)":"inherit"}}/>
                    {!narrow&&n.label}
                    {!narrow&&n.badge&&(
                      <span style={{marginLeft:"auto",fontSize:9,padding:"1px 4px",borderRadius:8,fontWeight:500,
                        background:{g:"#E1F5EE",a:"#FAEEDA"}[n.badgeColor],
                        color:{g:"#085041",a:"#633806"}[n.badgeColor]}}>
                        {n.badge}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            );
          })
          )}
        </div>

        {/* Footer */}
        <div style={{padding:8,borderTop:`0.5px solid ${t.divider}`}}>
          <UserMenuPanel narrow={narrow} role={role} cfg={cfg} userProfile={userProfile} isAdmin={role==="inhaber"} onLogout={handleLogout} onThemeChange={setTheme} currentTheme={themeMode} onProfileSaved={n=>setUserProfile(pr=>({...(pr||{}),name:n}))} onSwitchRole={isSupabaseConfigured?null:k=>{setRole(k);setScreen("dashboard");}}/>
        </div>
      </div>}

      {/* ── MAIN ── */}
      <div style={{display:"flex",flexDirection:"column",overflow:"hidden",minWidth:0,paddingBottom:0}}>

        {/* Topbar */}
        <div style={{padding:"9px 14px",borderBottom:t.topbarBorder,
          display:"flex",alignItems:"center",gap:8,flexShrink:0,
          background:t.topbarBg,backdropFilter:t.blur,WebkitBackdropFilter:t.blur}}>
          {mobile&&<div onClick={()=>setMoreOpen(true)} aria-label="Menü öffnen"
            style={{width:34,height:34,borderRadius:9,background:t.inputBg,border:`0.5px solid ${t.divider}`,
              display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0,color:t.textPrimary}}>
            <svg width="18" height="14" viewBox="0 0 18 14" aria-hidden="true">
              <path d="M1 1h16M1 7h16M1 13h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>}
          <div>
            <span style={{fontSize:14,fontWeight:500,color:t.textPrimary}}>
              {{dashboard:"Dashboard",termine:"Terminplan",patienten:"Patienten",
                rezepte:"Rezepte & Anfragen",umsatz:"Umsatz & Kosten",abrechnung:"Abrechnung",
                mitarbeiter:"Mitarbeiter",newsletter:"Mail & Newsletter",
                tracking:"Tracking",import:"Import — Daten aus deinem PVS"}[screen]}
            </span>
            <span style={{fontSize:11,color:t.textSecondary}}> — {new Date().toLocaleDateString("de-DE",{weekday:"short",day:"numeric",month:"short",year:"numeric"})}</span>
          </div>
          <div style={{marginLeft:"auto"}}/>
        </div>

        {/* Content */}
        {(screen==="patienten"||screen==="rezepte")
          ? <div style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column",padding:"12px 14px",background:t.contentBg}}>
              {screen==="patienten" && <PatientsScreen setScreen={setScreen} setTerminePreset={setTerminePreset} setRezeptePreset={setRezeptePreset} setMailPreset={setMailPreset}/>}
              {screen==="rezepte"   && <RezepteScreen role={role} rezeptePreset={rezeptePreset} onPresetConsumed={()=>setRezeptePreset(null)}/>}
            </div>
          : <div style={{padding:"12px 14px",flex:1,overflowY:"auto",maxHeight:670,background:t.contentBg}}>
              {screen==="dashboard"   && <DashboardScreen role={role} setScreen={setScreen} profileName={userProfile?.name} onOpenPraxis={()=>setPraxisSetupOpen(true)}/>}
              {screen==="termine"     && <TermineScreen presetPatient={terminePreset} onPresetConsumed={()=>setTerminePreset(null)}/>}
              {screen==="mitarbeiter" && <MitarbeiterScreen role={role}/>}
              {screen==="tracking"    && <TrackingScreen onNavigate={setScreen} tab={trackingTab} onTabChange={setTrackingTab} sidebarNav={!mobile}/>}
              {screen==="import"      && (role==="inhaber"||role==="mfa") && <ImportScreen/>}
              {screen==="umsatz"      && <UmsatzScreen role={role}/>}
              {screen==="abrechnung"  && (role==="inhaber"||role==="mfa") && <AbrechnungScreen/>}
              {screen==="abrechnung"  && role!=="inhaber" && role!=="mfa" && <div style={{padding:20,color:"var(--mf-muted)",fontSize:12}}>Kein Zugriff.</div>}
              {screen==="newsletter"  && <NewsletterScreen mailPreset={mailPreset} onPresetConsumed={()=>setMailPreset(null)}/>}
              
            </div>
        }
      </div>
    </div>

    {/* Praxis-Einrichtung als Schnellzugriff (Erste Schritte) */}
    {praxisSetupOpen&&(
      <Portal>
        <div data-theme={themeMode}>
          <div onClick={()=>setPraxisSetupOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay3}}/>
          <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:440,maxHeight:"85vh",maxWidth:"96vw",background:"var(--mf-bg)",borderRadius:16,zIndex:Z.modal3,display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 64px rgba(0,0,0,.35)"}}>
            <div style={{padding:"14px 18px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
              <span style={{fontSize:15,fontWeight:600}}>Praxis & Behandler</span>
              <div onClick={()=>setPraxisSetupOpen(false)} style={{cursor:"pointer",fontSize:20,color:"var(--mf-muted)",width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",background:"var(--mf-bg-alt)"}}>×</div>
            </div>
            <div style={{padding:"16px 18px",overflowY:"auto",display:"flex",flexDirection:"column",gap:10}}>
              <PraxisSettings onDone={()=>setPraxisSetupOpen(false)}/>
            </div>
          </div>
        </div>
      </Portal>
    )}

    {/* ── Handy: Bottom-Tab-Bar (iOS-Navigation) ── */}
    {/* Mobil: ein-/ausklappbare Seitenleiste (Hamburger oben links) */}
    {mobile&&moreOpen&&(
      <Portal>
        <div onClick={()=>setMoreOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.4)",zIndex:Z.menuOverlay}}/>
        <div style={{position:"fixed",top:0,left:0,bottom:0,width:274,zIndex:Z.menu,
          background:t.cardBg,backdropFilter:t.blur,WebkitBackdropFilter:t.blur,
          boxShadow:"8px 0 40px rgba(0,0,0,.25)",display:"flex",flexDirection:"column",
          padding:"calc(12px + env(safe-area-inset-top)) 10px calc(10px + env(safe-area-inset-bottom))",
          overflowY:"auto",animation:"dida-drawer .22s ease-out"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,padding:"0 6px 12px"}}>
            <DidaLogo size={26}/><DidaWordmark size={18}/>
            <div style={{flex:1}}/>
            <div onClick={()=>setMoreOpen(false)} style={{cursor:"pointer",fontSize:18,color:t.textSecondary,width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",background:t.inputBg}}>×</div>
          </div>
          {NAV.filter(n=>n.roles.includes(role)&&["dashboard","termine","patienten","tracking","mitarbeiter"].includes(n.key)).map(n=>(
            <div key={n.key} onClick={()=>{setScreen(n.key);setMoreOpen(false);}}
              style={{display:"flex",alignItems:"center",gap:11,padding:"12px 12px",borderRadius:11,fontSize:14,
                color:screen===n.key?"var(--mf-primary)":t.textPrimary,fontWeight:screen===n.key?600:400,
                cursor:"pointer",background:screen===n.key?t.navActiveBg:"transparent"}}>
              <i className={`ti ${n.icon}`} style={{fontSize:18}}/>{n.label}
            </div>
          ))}
          <div style={{flex:1}}/>
          <div style={{borderTop:`0.5px solid ${t.divider}`,margin:"8px 4px"}}/>
          <div style={{padding:"0 4px 4px"}}>
            <UserMenuPanel narrow={false} role={role} cfg={cfg} userProfile={userProfile} isAdmin={role==="inhaber"} onLogout={handleLogout} onThemeChange={setTheme} currentTheme={themeMode} onProfileSaved={n=>setUserProfile(pr=>({...(pr||{}),name:n}))} onSwitchRole={isSupabaseConfigured?null:k=>{setRole(k);setScreen("dashboard");setMoreOpen(false);}}/>
          </div>
        </div>
      </Portal>
    )}
    </div>
    </ThemeCtx.Provider>
  );
}
